import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import * as THREE from 'three';
import { FikoEksen } from '../FikoEksen';
import { FikoGrup } from '../FikoGrup';
import { FikoVektor3D } from '../FikoVektor3d';
import { FikoOperasyon } from '../FikoThreeCam/Fikoperasyon';
import { insancaSaniye } from './pipe';
import { FikoCycleTimeRaporComponent } from '../fiko-cycle-time-rapor/fiko-cycle-time-rapor.component';
// CSG (Constructive Solid Geometry) - Material Removal için
import { Evaluator, Brush, SUBTRACTION } from 'three-bvh-csg';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
// Triple-Dexel Stock Removal Engine
import { TripleDexelEngine, DexelToolGeometry, ToolSample, DexelMeshLayout } from './TripleDexelEngine';
// WebGPU Accelerated Dexel Engine
import { DexelGPUEngine, GPUToolSample, createDexelGPUEngine } from './DexelGPUEngine';
// WebWorker Dexel Bridge
import { DexelWorkerBridge, createDexelWorkerBridge, ProcessResult } from './DexelWorkerBridge';
// Voxel CSG Stock Removal Engine (NEW!)
import { VoxelCSGEngine, VoxelToolGeometry, createVoxelCSGEngine } from './VoxelCSGEngine';
import { VoxelGPUCompute, createVoxelGPUCompute } from './VoxelGPUCompute';
import { VoxelWorkerBridge, createVoxelWorkerBridge } from './VoxelWorkerBridge';
// GPU Marching Cubes (zero-readback mesh generation)
import { MarchingCubesGPU, createMarchingCubesGPU } from './MarchingCubesGPU';
// SDF (Signed Distance Field) Engine
import { SDFEngine, createSDFEngine } from './SDFEngine';
import { SDFGPUCompute, createSDFGPUCompute } from './SDFGPUCompute';
// Z-Buffer / Depth Map Engine
import { ZBufferEngine, createZBufferEngine } from './ZBufferEngine';
import { ZBufferGPUEngine, createZBufferGPUEngine } from './ZBufferGPUEngine';

/** Standart cizgi materyali (FikoThreeJsComponent bagimliligini kaldirmak icin inline) */
const STANDART_CIZGI_MATERIAL = new THREE.LineBasicMaterial({
  linewidth: 2,
  polygonOffset: true,
  polygonOffsetFactor: 1,
  polygonOffsetUnits: 1,
  color: 0xffffff,
  vertexColors: true,
  transparent: true,
  opacity: 1,
});

@Component({
  selector: 'fikoyt-fiko-similasyon-v1',
  templateUrl: './fiko-similasyon-v1.component.html',
  styleUrls: ['./fiko-similasyon-v1.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, insancaSaniye, FikoCycleTimeRaporComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FikoSimilasyonV1Component implements OnDestroy {

  /** FikoDialogCore pencere referansi */
  private _pencere: any;
  @Input() set pencere(v: any) {
    this._pencere = v?.instance || v;
  }
  get pencere(): any { return this._pencere; }

  /** Ana nesne - dialog acilinca set edilir, simulasyonu baslatir */
  private _anaNesne: any;
  @Input() set anaNesne(val: any) {
    if (!val) return;
    this._anaNesne = val;
    // Defer baslat to avoid NG0100 (state change during change detection)
    setTimeout(() => this.baslat(), 0);
  }
  get anaNesne(): any { return this._anaNesne; }

  /** Resolved sahne reference (anaNesne veya parent zincirinden) */
  private _sahne: any;
  /** Sahne getter (template erişimi için) */
  get sahne(): any { return this._sahne; }
  /** */
  private subsList: Subscription[] = [];
  private renderListeners: Map<string, any> = new Map();
  /** */
  similasyonIntervalId: any;
  aktifTakimTasima = true;
  /** */
  oncekiNokta!: any;
  aktifNokta: any;
  noktalar: any[];
  /** */
  simGeoBuffer = new THREE.BufferGeometry();
  simGeoBufferNoktalar = new THREE.BufferGeometry();
  similasyonNoktalar: THREE.Points;
  similasyonCizgisi = new THREE.LineSegments(this.simGeoBuffer, STANDART_CIZGI_MATERIAL);
  _simCizgiOnde: boolean = false;
  public get simCizgiOnde(): boolean {
    return this._simCizgiOnde;
  }
  public set simCizgiOnde(v: boolean) {
    this._simCizgiOnde = v;
    if (!v) {
      this.similasyonCizgisi.material.depthTest = true;
      this.similasyonCizgisi.material.depthWrite = true;
      (this.similasyonNoktalar.material as THREE.PointsMaterial).depthTest = true;
      (this.similasyonNoktalar.material as THREE.PointsMaterial).depthWrite = true;
      //@ts-ignore
      this.similasyonCizgisi.parent?.anaGosterim?.ciz();
    } else {
      this.similasyonCizgisi.material.depthTest = false;
      this.similasyonCizgisi.material.depthWrite = false;
      (this.similasyonNoktalar.material as THREE.PointsMaterial).depthTest = false;
      (this.similasyonNoktalar.material as THREE.PointsMaterial).depthWrite = false;
      //@ts-ignore
      this.similasyonCizgisi.parent?.anaGosterim?.ciz();
    }
  }
  /** */

  pozMin: number = 0;
  pozMax: number = 0;
  poz: number = 0;

  simHizi = 1;

  /** Çizgi üzerinde yumuşak ilerleme modu */
  cizgiUzerindeIlerle: boolean = false;
  private interpolasyonT: number = 0; // 0-1 arası segment ilerlemesi
  private interpolasyonPoz = new THREE.Vector3(); // Anlık interpolasyon pozisyonu
  private interpolasyonBasladi: boolean = false; // İnterpolasyon modu aktifleşti mi
  private interpolasyonCizgisi: THREE.Line; // Takıma kadar olan dinamik çizgi

  reelIlerleme: number = 100;
  /** */
  renkIsleme = new THREE.Color("chartreuse");
  renkYaklasma = new THREE.Color("yellow");
  renkUzaklasma = new THREE.Color("blue");
  renkBosta = new THREE.Color("red");
  renkBilinmiyor = new THREE.Color("grey");

  cokluRenkDepo: any[] = [];
  tekRenkDepo: any[] = [];
  private _tekRenk: boolean = false;
  public get tekRenk(): boolean {
    return this._tekRenk;
  }
  public set tekRenk(v: boolean) {
    this._tekRenk = v;
    if (v) {
      this.similasyonCizgisi.geometry.setAttribute("color", new THREE.Float32BufferAttribute(this.tekRenkDepo, 3));
    } else {
      this.similasyonCizgisi.geometry.setAttribute("color", new THREE.Float32BufferAttribute(this.cokluRenkDepo, 3));
    }
    //@ts-ignore
    this._sahne?.anaGosterim?.ciz();
  }
  /** */
  islemeEkseni: FikoEksen;
  gostergeNokta: FikoVektor3D;
  gostergeYon: FikoVektor3D;
  gostergeAci: any;
  gostergeKacmaDegerleri: any;

  /** Kamera modu: 0=serbest, 1=takip kilidi, 2=sinematik drone */
  kameraModu: number = 0;
  kameraYumusaklik: number = 50; // 0=aninda snap, 100=cok yavas takip

  /** Sinematik drone kamera */
  droneHiz: number = 50;
  droneMesafe: number = 50;
  droneYukseklik: number = 40;
  private droneAci: number = 0;

  /** Timeline hareket turu segmentleri */
  operasyonIsaretleri: { isim: string, hareketTuru: string, baslangic: number, bitis: number, renk: string }[] = [];
  /** Operasyon sinirlari (dikey cizgiler) */
  operasyonSinirlari: { pozisyon: number, isim: string }[] = [];

  /** Gidis yonu 3D ok gostergesi */
  yonOku: THREE.ArrowHelper;
  yonOkuGorunur: boolean = false;

  /** Kesim sesi simulasyonu (Web Audio API) */
  sesAktif: boolean = false;
  sesMasterVolume: number = 70;       // 0-100 master ses seviyesi
  sesKesimMix: number = 60;           // 0-100 kesim osilatör oranı
  sesHarmonikMix: number = 40;        // 0-100 harmonik osilatör oranı
  sesGurultuMix: number = 50;         // 0-100 beyaz gürültü (grinding) oranı
  sesServoMix: number = 30;           // 0-100 servo/boşta ses oranı
  sesPitch: number = 50;              // 0-100 frekans kaydırma (50=normal)
  private audioCtx: AudioContext;
  private oscMain: OscillatorNode;
  private oscHarmonic: OscillatorNode;
  private gainMain: GainNode;
  private gainHarmonic: GainNode;
  private noiseGain: GainNode;
  private noiseSource: AudioBufferSourceNode;
  private masterGain: GainNode;

  /** Talaş partikül efekti (THREE.Points particle pool) */
  talasAktif: boolean = false;
  talasSiddetCarpan: number = 50; // 0-100 arasi, kullanici ayarlar
  talasGercekFizik: boolean = true; // true=gercekci fizik (takim yonune gore), false=rastgele
  private talasNoktalar: THREE.Points;
  private talasPozisyonlar: Float32Array;
  private talasHizlar: Float32Array;
  private talasOmurleri: Float32Array;
  private talasRenkler: Float32Array;
  private readonly TALAS_SAYISI = 200;

  /** Titreşim efekti (kamera sarsintisi) */
  titresimAktif: boolean = false;
  titresimSiddetCarpan: number = 50; // 0-100 arasi, kullanici ayarlar
  private titresimOffset = new THREE.Vector3();
  private titresimZaman: number = 0; // sinusoidal chatter icin sayac

  /** Çarpışma kontrolü */
  carpismaKontrolAktif: boolean = false;
  private carpismaRaycaster = new THREE.Raycaster();
  carpismaSayisi: number = 0;
  carpismaFlash: number = 0;
  private carpismaMeshCache: THREE.Mesh[] = null;
  private carpismaCacheZaman: number = 0;

  /** Çarpışma noktaları listesi ve 3D markerlar */
  carpismaNoktalar: { x: number, y: number, z: number, pozIdx: number, zaman: string }[] = [];
  carpismaListeAcik: boolean = false;
  private carpismaMarkerGrup: THREE.Group;
  private readonly CARPISMA_MARKER_MAX = 50;

  /** Cycle Time Rapor Component referansı */
  @ViewChild('cycleTimeRapor') cycleTimeRaporRef: any;

  // ─── Kütük Simülasyonu (Material Removal) ───────────────────────────
  // Referans: ModuleWorks GPU-accelerated simulation, three-bvh-csg (100x faster)
  // Akademik: "GPU accelerated voxel-based machining simulation" (2021)

  /** Kütük simülasyonu aktif mi */
  kutukSimAktif: boolean = false;
  /** Kütük simülasyonu modu */
  kutukSimModu: 'dexel' | 'voxel' | 'csg' | 'visual' | 'marchingcubes' | 'sdf' | 'zbuffer' = 'dexel';

  // ─── MOTOR AYARLARI (Kullanıcı tarafından runtime'da değiştirilebilir) ────
  /** BBox padding (mm) */
  // Dexel grid payı: motor TÜM bbox'ı dolu malzeme başlatır — padding > 0 kütüğün
  // modelden taşmasına yol açar (10mm iken her yönde 10mm taşıyordu). Stok = kütük bbox'ı.
  simPadding = 0;
  /** 3D engine max voxel limit */
  simMaxVoxels3D = 50_000_000;
  /** 2D dexel max cell limit */
  simMaxCells2D = 500_000;
  /** 3D resolution (0=auto, >0=manual cells/mm) */
  simResolution3D = 0;
  /** 2D resolution (0=auto, >0=manual cells/mm) */
  simResolution2D = 0;
  /** GPU Mesh readback interval for voxel/sdf/zbuffer (ms) */
  simGPUMeshInterval = 200;
  /** Seçilen kütük mesh (stock) */
  private kutukMesh: THREE.Mesh | null = null;
  /** Kütük parent referansı (FikoKutuk) */
  private kutukParent: any = null;
  /** Orijinal kütük geometrisi (reset için) */
  private kutukOrijinalGeo: THREE.BufferGeometry | null = null;
  /** CSG Evaluator (three-bvh-csg) - singleton, reuse for performance */
  private csgEvaluator: Evaluator | null = null;
  /** CSG Brush (kütük) - güncel durum */
  private kutukBrush: Brush | null = null;

  // ─── Triple-Dexel Engine (EXISTING - KEPT AS IS) ───────────────────────────
  /** Triple-Dexel stock removal engine (CPU) */
  private dexelEngine: TripleDexelEngine | null = null;
  /** Mesh vertex layout sözleşmesi (engine ile paylaşım) */
  private _dexelLayout: DexelMeshLayout | null = null;
  /** WebGPU accelerated dexel engine */
  private dexelGPUEngine: DexelGPUEngine | null = null;
  /** GPU engine aktif mi? */
  useGPUDexel: boolean = false;
  /** WebWorker dexel bridge - main thread'i bloklamadan dexel işleme */
  private dexelWorkerBridge: DexelWorkerBridge | null = null;
  /** Worker aktif mi? (init başarılı oldu mu) */
  private useWorkerDexel: boolean = false;
  /** Worker'dan gelen son dirty heights (async güncelleme için) */
  private workerPendingUpdate: boolean = false;
  /** Dexel visualization mesh */
  private dexelMesh: THREE.Mesh | null = null;
  /** Dexel önceki pozisyon (swept move için) */
  private dexelOncekiPoz: THREE.Vector3 | null = null;
  /** Dexel güncelleme sayacı (her N adımda mesh güncelle) */
  private dexelGuncellemeSayac: number = 0;
  /** Dexel batch size (performans için) */
  dexelBatchSize = 10;

  // ─── Voxel CSG Engine (NEW ALTERNATIVE!) ───────────────────────────────────
  /** Voxel CSG stock removal engine (CPU with Octree) */
  private voxelEngine: VoxelCSGEngine | null = null;
  /** WebGPU accelerated voxel compute */
  private voxelGPUCompute: VoxelGPUCompute | null = null;
  /** GPU voxel aktif mi? */
  useGPUVoxel: boolean = false;
  /** WebWorker voxel bridge */
  private voxelWorkerBridge: VoxelWorkerBridge | null = null;
  /** Worker voxel aktif mi? */
  private useWorkerVoxel: boolean = false;
  /** Voxel visualization mesh */
  private voxelMesh: THREE.Mesh | null = null;
  /** Voxel önceki pozisyon (swept move için) */
  private voxelOncekiPoz: THREE.Vector3 | null = null;
  /** Voxel güncelleme sayacı */
  private voxelGuncellemeSayac: number = 0;
  /** Voxel batch size */
  voxelBatchSize = 50;
  /** Voxel engine tamamen hazır mı? (async init tamamlandı) */
  private voxelEngineReady: boolean = false;
  /** Voxel mesh güncelleme bekliyor mu? (throttle) */
  private voxelMeshUpdatePending: boolean = false;
  /** GPU voxel mesh update zamanlayıcısı (oynatma sırasında readback throttle) */
  private voxelGPUMeshTimerId: any = null;
  /** GPU voxel data değişti mi? (mesh update gerekli) */
  private voxelGPUDirty: boolean = false;
  /** Pre-allocated GPU batch positions buffer (256 segments x 6 floats) */
  private voxelGPUBatchPositions = new Float32Array(256 * 6);

  // ─── GPU Marching Cubes (zero-readback mesh from voxel data) ─────────────
  /** GPU Marching Cubes mesh extractor (shares voxelBuffer with VoxelGPUCompute) */
  private marchingCubesGPU: MarchingCubesGPU | null = null;

  // ─── SDF (Signed Distance Field) Engine ──────────────────────────────────
  /** SDF CPU engine (Float32 distance field) */
  private sdfEngine: SDFEngine | null = null;
  /** SDF GPU compute shader */
  private sdfGPUCompute: SDFGPUCompute | null = null;
  /** SDF GPU aktif mi? */
  useGPUSDF: boolean = false;
  /** SDF visualization mesh */
  private sdfMesh: THREE.Mesh | null = null;
  /** SDF engine tamamen hazır mı? */
  private sdfEngineReady: boolean = false;
  /** SDF GPU mesh update zamanlayıcısı */
  private sdfGPUMeshTimerId: any = null;
  /** SDF GPU data değişti mi? */
  private sdfGPUDirty: boolean = false;
  /** SDF GPU batch positions buffer */
  private sdfGPUBatchPositions = new Float32Array(256 * 6);

  // ─── Z-Buffer / Depth Map Engine ─────────────────────────────────────────
  /** Z-Buffer CPU engine (6-directional depth maps) */
  private zbufferEngine: ZBufferEngine | null = null;
  /** Z-Buffer GPU engine */
  private zbufferGPU: ZBufferGPUEngine | null = null;
  /** Z-Buffer GPU aktif mi? */
  useGPUZBuffer: boolean = false;
  /** Z-Buffer visualization mesh */
  private zbufferMesh: THREE.Mesh | null = null;
  /** Z-Buffer engine hazır mı? */
  private zbufferEngineReady: boolean = false;
  /** Z-Buffer mesh update zamanlayıcısı */
  private zbufferMeshTimerId: any = null;
  /** Z-Buffer dirty flag */
  private zbufferDirty: boolean = false;
  /** Z-Buffer GPU batch positions buffer */
  private zbufferGPUBatchPositions = new Float32Array(256 * 6);
  /** Z-Buffer GPU mesh update zamanlayıcısı */
  private zbufferGPUMeshTimerId: any = null;
  /** Z-Buffer GPU dirty flag */
  private zbufferGPUDirty: boolean = false;

  // ═══════════════════════════════════════════════════════════════════════════
  // ULTRA PERFORMANS: Batch Processing & Frame Budgeting
  // ═══════════════════════════════════════════════════════════════════════════
  /** Tool pozisyon batch buffer (toplu işleme için) */
  private toolPositionBatch: { x: number; y: number; z: number; i: number; j: number; k: number }[] = [];
  /** Frame time budget (ms) - 60fps için ~8ms hesaplama süresi */
  private readonly FRAME_BUDGET_MS = 8;
  /** Batch flush threshold - bu kadar pozisyon birikince işle (büyük batch = daha az overhead) */
  private readonly BATCH_FLUSH_SIZE = 100;
  /** Son mesh güncelleme zamanı */
  private lastMeshUpdateTime: number = 0;
  /** Minimum mesh güncelleme aralığı (ms) - 60fps için ~16ms */
  private readonly MIN_MESH_UPDATE_INTERVAL = 16;
  /** Mesh güncelleme pending flag */
  private meshUpdatePending: boolean = false;
  /** RAF ID for throttled mesh update */
  private meshUpdateRafId: number = 0;
  /** Normal hesaplama sayacı */
  private normalUpdateCounter: number = 0;
  /** Full normal hesaplama aralığı (her N update'te 1 - performans/kalite dengesi) */
  private readonly NORMAL_UPDATE_INTERVAL = 5;
  /** BoundingBox güncelleme aralığı (her 120 update'te 1, ~2 saniye) */
  private readonly BBOX_UPDATE_INTERVAL = 120;

  // ─── Profesyonel Simülasyon Işık Sistemi ─────────────────────────────────
  /** Simülasyona özel ışıklar (sahneye eklenir, temizlenirken kaldırılır) */
  private _simLights: THREE.Object3D[] = [];
  /** Simülasyon environment map (PMREMGenerator ile) */
  private _simEnvMap: THREE.Texture | null = null;

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAPSHOT SİSTEMİ - İleri/Geri Sarma Desteği (AYARLANABILIR)
  // ═══════════════════════════════════════════════════════════════════════════
  /** Dexel state snapshot'ları (pozisyon → state) */
  private dexelSnapshots: Map<number, { xyData: Float32Array; xySpanCounts: Uint8Array }> = new Map();
  /** Snapshot alma aralığı (her N pozisyonda bir) - AYARLANABILIR */
  snapshotAraligi: number = 25;
  /** Geri sararken doldur (true = işle, false = sadece snapshot'a git) */
  geriSarmaDoldur: boolean = true;
  /** Maximum snapshot sayısı (memory management) */
  maxSnapshotSayisi: number = 40;
  /** Mesh güncelleme aralığı (her N pozisyonda bir) */
  meshGuncellemeAraligi: number = 1;
  /** Height map yumuşatma (smoothing) iterasyon sayısı (0=kapalı, 1-5 arası) */
  dexelSmoothing: number = 3;
  /** Kenar koruma eşiği (0=otomatik, mm cinsinden). Düşük = form daha iyi korunur */
  dexelSmoothSigma: number = 0;
  /** Son snapshot alınan pozisyon */
  private lastSnapshotPoz: number = -1;
  /** Son işlenen simülasyon pozisyonu (ileri/geri takip) */
  private lastProcessedSimPoz: number = -1;

  /** Performans metrikleri */
  private perfMetrics = {
    totalProcessTime: 0,
    meshUpdateTime: 0,
    batchCount: 0,
    avgBatchTime: 0
  };

  /** Takım geometrisi cache (performans için) */
  private takimGeoCache: Map<string, THREE.CylinderGeometry> = new Map();
  /** Son işlenen pozisyon (duplicate işlem önleme) */
  private sonIslemPoz: number = -1;
  /** CSG batch sayacı (her N adımda mesh güncelle) */
  private csgBatchSayac: number = 0;
  /** CSG batch boyutu (performans vs görsel tradeoff) - 1 = her adımda güncelle */
  private readonly CSG_BATCH_SIZE = 1;
  /** Swept volume buffer (birden fazla adım biriktir) */
  private sweptVolumeBuffer: THREE.BufferGeometry[] = [];
  /** Kütük seçim modu aktif mi */
  kutukSecimModu: boolean = false;
  /** Kütük seçim subscription */
  private kutukSecimSub: any = null;
  /** Kütük seçim click handler referansı */
  private kutukClickHandler: ((e: MouseEvent) => void) | null = null;
  /** Material removal toplam hacim (m³) */
  kutukKaldirilanHacim: number = 0;
  /** Kütük başlangıç hacmi */
  private kutukBaslangicHacim: number = 0;
  /** Kütük materyali (görsel) */
  private kutukMaterial: THREE.MeshStandardMaterial | null = null;
  /** Pre-allocated vectors (zero-allocation hot path) */
  private readonly _kutukTempVec1 = new THREE.Vector3();
  private readonly _kutukTempVec2 = new THREE.Vector3();
  private readonly _kutukTempVec3 = new THREE.Vector3();
  private readonly _kutukTempQuat = new THREE.Quaternion();
  private readonly _kutukTempMat4 = new THREE.Matrix4();
  private readonly _kutukTempBox3 = new THREE.Box3();
  private readonly _kutukHamRenk = new THREE.Color(0xB8C0C8);
  private readonly _kutukUpVec = new THREE.Vector3(0, 1, 0);
  private readonly _kutukUnitScale = new THREE.Vector3(1, 1, 1);
  /** Pre-allocated simulation hot-path buffers (interpolasyon, takim, kamera, efekt) */
  private readonly _simTmpV1 = new THREE.Vector3();
  private readonly _simTmpV2 = new THREE.Vector3();
  private readonly _simTmpV3 = new THREE.Vector3();
  private readonly _simTmpV4 = new THREE.Vector3();
  private readonly _simTmpV5 = new THREE.Vector3();
  private readonly _simTmpV6 = new THREE.Vector3();
  private readonly _simTmpM4 = new THREE.Matrix4();
  private readonly _simTmpQ1 = new THREE.Quaternion();
  private readonly _simTmpQ2 = new THREE.Quaternion();
  /** Önceki takım pozisyonu (swept volume için) */
  private _kutukOncekiPoz: THREE.Vector3 | null = null;
  private _kutukOncekiYon: THREE.Vector3 | null = null;
  /** Debug: Takım silindiri görselleştirme */
  private kutukDebugCylinder: THREE.Mesh | null = null;

  // ─── Takım Bazlı Renklendirme ────────────────────────────────────────────
  /** Renklendirme modu: 'depth' (kesim derinliği) | 'tool' (takıma göre - varsayılan) */
  kutukRenklendirmeModu: 'depth' | 'tool' = 'tool';
  /** Kullanıcının seçtiği stok (ham malzeme) rengi - kesilmemiş yüzeyler */
  kutukStokRenk: string = '#B8C0C8';

  /** Takım yönü ters çevir - bazı kalıplar için kesim yönünü tersine çevirir */
  dexelTersYon: boolean = false;

  // ─── CATIA-Level Professional Rendering ─────────────────────────────────
  /** AO yoğunluğu (0-100, default 65) */
  aoYogunluk: number = 65;
  /** AO örnek yarıçapı (1-8, default 5) */
  aoRadius: number = 5;
  /** Kenar çizgileri aktif mi */
  kenarCizgileriAktif: boolean = false;
  /** Kenar çizgileri mesh */
  private kenarCizgileriMesh: THREE.LineSegments | null = null;
  /** İç duvar mesh (cep dikey yüzeyleri) */
  private icDuvarMesh: THREE.Mesh | null = null;
  /** Kenar çizgisi opaklığı (0-100, default 40) */
  kenarCizgisiOpaklik: number = 40;
  /** Kenar tespit eşiği çarpanı (0-100, default 50 → cellSize * 2.5) */
  kenarCizgisiEsik: number = 50;
  /** Malzeme preset: 'aluminum' | 'steel' | 'brass' | 'titanium' */
  malzemePreset: string = 'aluminum';
  /** Manuel PBR override (-1 = preset kullan, 0-100 = manuel) */
  manuelRoughness: number = -1;
  manuelMetalness: number = -1;
  manuelClearcoat: number = -1;
  manuelEnvMapIntensity: number = -1;
  /** Tone mapping exposure (30-200, default 95 → 0.95) */
  toneMappingExposure: number = 95;
  /** İç duvar oluşturma aktif (cep duvarları) */
  icDuvarAktif: boolean = false;
  /** Kalite preseti: 'performance' | 'balanced' | 'quality' */
  kalitePreseti: string = 'balanced';
  /** Malzeme presetleri PBR parametreleri */
  readonly MALZEME_PRESETLERI: Record<string, {
    roughness: number; metalness: number; clearcoat: number;
    clearcoatRoughness: number; envMapIntensity: number; renk: string;
  }> = {
    aluminum: { roughness: 0.40, metalness: 0.90, clearcoat: 0.08, clearcoatRoughness: 0.4, envMapIntensity: 0.85, renk: '#D4D8DC' },
    steel:    { roughness: 0.30, metalness: 0.95, clearcoat: 0.05, clearcoatRoughness: 0.3, envMapIntensity: 1.0,  renk: '#8A9098' },
    brass:    { roughness: 0.35, metalness: 0.92, clearcoat: 0.10, clearcoatRoughness: 0.3, envMapIntensity: 0.80, renk: '#C8A86E' },
    titanium: { roughness: 0.35, metalness: 0.88, clearcoat: 0.06, clearcoatRoughness: 0.4, envMapIntensity: 0.85, renk: '#9AA0A8' },
  };
  /** Takım ID → renk haritası */
  private takimRenkHaritasi: Map<number, THREE.Color> = new Map();
  /** Takım ID → isim haritası (legend için) */
  takimIsimHaritasi: Map<number, string> = new Map();
  /** Takım ID sayacı */
  private takimIdSayac: number = 0;
  /** Son atanan takım referansı (takım değişikliği tespiti) */
  private sonTakimRef: any = null;
  /** Aktif takım ID'si */
  private aktifTakimId: number = 0;

  /** Takım renk paleti (profesyonel CAM renkleri - Catia/Tebis benzeri) */
  private readonly TAKIM_RENK_PALETI: number[] = [
    0x4CAF50, // Yeşil
    0x2196F3, // Mavi
    0xFF9800, // Turuncu
    0xE91E63, // Pembe
    0x9C27B0, // Mor
    0x00BCD4, // Cyan
    0xFFC107, // Sarı
    0x795548, // Kahverengi
    0x607D8B, // Gri-mavi
    0xFF5722, // Koyu turuncu
    0x3F51B5, // İndigo
    0x8BC34A, // Açık yeşil
    0xF44336, // Kırmızı
    0x009688, // Teal
    0xCDDC39, // Lime
    0x673AB7, // Koyu mor
  ];

  /** Template için takım listesi getter */
  get takimListesi(): { id: number, isim: string, renk: string }[] {
    const liste: { id: number, isim: string, renk: string }[] = [];
    this.takimIsimHaritasi.forEach((isim, id) => {
      const renk = this.takimRenkHaritasi.get(id);
      liste.push({ id, isim, renk: renk ? '#' + renk.getHexString() : '#888' });
    });
    return liste;
  }

  /** Kütük mesh seçili mi (template için getter) */
  get kutukMeshSecili(): boolean { return !!this.kutukMesh; }
  /** GPU dexel aktif mi (template için) */
  get gpuAktif(): boolean { return this.useGPUDexel; }
  /** Worker dexel aktif mi (template için) */
  get workerAktif(): boolean { return this.useWorkerDexel; }

  /** Template için snapshot sayısı getter */
  get snapshotSayisi(): number { return this.dexelSnapshots.size; }

  /** Template için son snapshot pozisyonu getter */
  get sonSnapshotPoz(): number { return this.lastSnapshotPoz; }

  /**
   * Silindir yön modu - test için
   * 0: Tip→Spindle (takım ekseninin tersi, -i,-j,-k)
   * 1: Spindle→Tip (takım ekseni yönünde, +i,+j,+k)
   * 2: Sabit +Z (yukarı)
   * 3: Sabit -Z (aşağı)
   */
  silindirYonModu: number = 0;

  /** Simülasyon oynatılıyor mu (Director Mode için) */
  simOynatiliyor: boolean = false;
  /** CD.detectChanges throttle sayacı (oynatma sırasında her 2 tick'te 1 CD çalışır) */
  private _cdThrottleCounter = 0;

  /** Efekt modu: 0=kapalı, 1=lazer izi (Tron), 2=hayalet takım */
  efektModu: number = 0;
  /** Lazer İzi (Tron Effect) - neon parlayan kuyruk */
  private lazerIzCizgisi: THREE.Line;
  private lazerIzGeo: THREE.BufferGeometry;
  private lazerIzBazRenkler: Float32Array;
  private lazerIzSayac: number = 0;
  private readonly LAZER_IZ_BOYUT = 400;
  /** Hayalet Takım (Ghost) - gelecek pozisyon göstergesi */
  private hayaletGrup: THREE.Group;
  private hayaletCizgi: THREE.Line;
  hayaletMesafe: number = 50; // slider: 0-100 → 10-200 adım ileri

  /** 3D HUD Gösterge (holografik kokpit) */
  hudAktif: boolean = false;
  hudBoyut: number = 50; // slider: 0-100 → 0.3x - 3x ölçek
  private hudGrup: THREE.Group;
  private hudDevirArcGeo: THREE.BufferGeometry;
  private hudHizArcGeo: THREE.BufferGeometry;
  private hudMetinler: { sprite: THREE.Sprite, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, tex: THREE.CanvasTexture }[] = [];
  /** HUD metin cache - sadece değişince GPU texture upload yap */
  private hudOncekiMetinler: string[] = [];
  private readonly HUD_ARC_SEGMENTS = 64;

  /** Sonar Dalga Efekti (genişleyen halka dalgaları) */
  sonarAktif: boolean = false;
  sonarHiz: number = 50; // slider: 0-100 → dalga frekansı

  /** Araçlar popup paneli açık/kapalı */
  araclarPanelAcik: boolean = false;
  araclarPopupTop: number = 0;
  araclarPopupLeft: number = 0;

  private sonarGrup: THREE.Group;
  private sonarHalkalar: { mesh: THREE.Mesh, yas: number, aktif: boolean }[] = [];
  private sonarFrame: number = 0;
  private readonly SONAR_MAX = 12;
  private readonly SONAR_OMUR = 60;

  /** GPU çarpışma kontrolü */
  private carpismaRT: THREE.WebGLRenderTarget;
  private carpismaKamera: THREE.OrthographicCamera;
  private carpismaPixelBuffer: Uint8Array;
  private readonly CARPISMA_RT_BOYUT = 8;
  private carpismaOverrideMat: THREE.MeshBasicMaterial;

  constructor(public readonly FikoYT: FikoYTService, public readonly CD: ChangeDetectorRef) {
    this.similasyonCizgisi.frustumCulled = false;
  }

  /** Nesne veya parent zincirinden sahne bulur */
  private sahneAl(nesne: any): any {
    let n = nesne;
    while (n) {
      if (n.sahne) return n.sahne;
      n = n.parent;
    }
    return null;
  }

  /** aktifTakim objesini sahne icerisinden bulur (kaynakca veya direkt sahne) */
  private aktifTakimAl(): THREE.Object3D | null {
    return this._sahne?.kaynakca?.getObjectByName("aktifTakim")
      ?? this._sahne?.getObjectByName("aktifTakim")
      ?? null;
  }

  /** Simulasyonu baslatir (anaNesne set edildikten sonra) */
  private baslat() {
    this._sahne = this.sahneAl(this._anaNesne);
    if (!this._sahne) { console.warn('Similasyon: sahne bulunamadi'); return; }

    this.sifirla();
    this.takimYollariniAl(this._anaNesne);

    this.similasyonCizgisi.geometry.setDrawRange(0, 0);
    this.simGeoBufferNoktalar.setDrawRange(0, 0);
    this._sahne.anaGosterim?.ciz();

    this.CD.detectChanges();

    this.tiklandiIleriOynat();
    this.tiklandiSimilasyonCizgisi();
  }

  tiklandiSimilasyonCizgisi() {
    this.renderListeners.set("pointerup", this._sahne?.anaGosterim.renderer.listen(this._sahne?.anaGosterim.canvas.nativeElement, "pointerup", ($event: any) => {
      if ($event.button == 0)
        if (this.aktifTakimTasima) {
          let x = ($event.offsetX) / (this._sahne?.anaGosterim.canvas.nativeElement.clientWidth) * 2 - 1;
          let y = - ($event.offsetY / this._sahne?.anaGosterim.canvas.nativeElement.clientHeight) * 2 + 1;

          let raycaster = new THREE.Raycaster();

          raycaster.params.Line = { threshold: .1 };

          let mouseVektor = new FikoVektor3D().set(x, y, -1);
          mouseVektor.unproject(this._sahne?.anaGosterim.aktifKamera);
          let yon = new FikoVektor3D().set(0, 0, -1).transformDirection(this._sahne?.anaGosterim.aktifKamera.matrixWorld);
          raycaster.set(mouseVektor, yon);
          raycaster.camera = this._sahne?.anaGosterim.aktifKamera;

          let odaktakiler = raycaster.intersectObject(this.similasyonCizgisi, false);
          if (odaktakiler.length > 0) {
            if ("point" in odaktakiler[0]) {
              this.tiklandiDuraklat();
              let indexNokta = this.noktalar[Math.min(Math.floor(odaktakiler[0].index / 2) + 1, this.noktalar.length - 1)];
              this.aktifNokta = indexNokta;

              const myDirectionVector = this._simTmpV1.copy(indexNokta.takimYonu);
              const mx = this._simTmpM4.lookAt(myDirectionVector, this._simTmpV2.set(0, 0, 0), this._simTmpV3.set(0, 0, 1));
              const qt = this._simTmpQ1.setFromRotationMatrix(mx);
              indexNokta.takim.quaternion.copy(qt);

              indexNokta.takim.position.copy(odaktakiler[0].point);
              let aktifTakim = this.aktifTakimAl();
              if (!aktifTakim) return;

              aktifTakim.clear()
              aktifTakim.add(indexNokta.takim);
              aktifTakim.updateMatrixWorld();

              this.gostergeNokta = odaktakiler[0].point.clone();
              this.gostergeNokta.sub(this.islemeEkseni.merkez);
              this.gostergeNokta.applyQuaternion(this.islemeEkseni.quaternion.clone().invert());

              this.CD.detectChanges()
              this._sahne?.anaGosterim.ciz();
            }
          }
        }

    })
    );
  }

  tiklandiGeriOynat() {
    this.tiklandiDuraklat();
    this.simOynatiliyor = true;

    this.similasyonIntervalId = setInterval(() => {
      if (this.cizgiUzerindeIlerle) {
        // Çizgi üzerinde yumuşak geri ilerleme
        this.interpolasyonAdimla(-1);
      } else {
        // Noktadan noktaya atlama
        if (this.poz <= this.pozMin) {
          this.tiklandiDuraklat();
          this.poz = this.pozMin;
        } else {
          this.poz -= this.simHizi;
        }
        this.pozisyonDegisti();
      }
    }, 60);
  }

  tiklandiIleriOynat() {
    this.tiklandiDuraklat();
    this.simOynatiliyor = true;

    this.similasyonIntervalId = setInterval(() => {
      if (this.cizgiUzerindeIlerle) {
        // Çizgi üzerinde yumuşak ileri ilerleme
        this.interpolasyonAdimla(1);
      } else {
        // Noktadan noktaya atlama
        if (this.poz >= this.pozMax) {
          this.tiklandiDuraklat();
          this.poz = this.pozMax;
        } else {
          this.poz += this.simHizi;
        }
        this.pozisyonDegisti();
      }
    }, 60);
  }

  /** Çizgi üzerinde yumuşak ilerleme - her frame çağrılır */
  private interpolasyonAdimla(yon: number) {
    if (!this.noktalar?.length) return;

    const baslangicPoz = Math.floor(this.poz);
    const bitisPoz = yon > 0 ? baslangicPoz + 1 : baslangicPoz - 1;

    // Sınır kontrolü
    if (yon > 0 && baslangicPoz >= this.pozMax) {
      this.tiklandiDuraklat();
      this.poz = this.pozMax;
      this.pozisyonDegisti();
      return;
    }
    if (yon < 0 && baslangicPoz <= this.pozMin) {
      this.tiklandiDuraklat();
      this.poz = this.pozMin;
      this.pozisyonDegisti();
      return;
    }

    const noktaA = this.noktalar[baslangicPoz];
    const noktaB = this.noktalar[Math.max(this.pozMin, Math.min(bitisPoz, this.pozMax))];
    if (!noktaA || !noktaB) {
      this.pozisyonDegisti();
      return;
    }

    // İki nokta arası mesafeye göre hız ayarla
    const dx = noktaB.x - noktaA.x;
    const dy = noktaB.y - noktaA.y;
    const dz = noktaB.z - noktaA.z;
    const mesafe = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Hız: simHizi * sabit / mesafe (mesafe arttıkça daha yavaş ilerler frame bazında)
    // Minimum 0.5mm, maksimum 100mm varsayalım normalize için
    const normalMesafe = Math.max(0.5, Math.min(mesafe, 100));
    const adimBuyuklugu = (this.simHizi * 2) / normalMesafe; // simHizi=1 için ~0.02-4 arası

    this.interpolasyonT += adimBuyuklugu * yon;

    // Segment tamamlandı mı?
    if (yon > 0 && this.interpolasyonT >= 1) {
      this.poz = bitisPoz;
      this.interpolasyonT = 0;
      // Segment sonu: interpolasyonPoz'u hedef noktaya ayarla
      this.interpolasyonPoz.set(noktaB.x, noktaB.y, noktaB.z);
      this.interpolasyonBasladi = true;
      this.pozisyonDegisti();
    } else if (yon < 0 && this.interpolasyonT <= 0) {
      this.poz = bitisPoz;
      this.interpolasyonT = 1;
      // Geri gidiş tamamlandı: interpolasyonPoz'u hedef noktaya (noktaB) ayarla
      this.interpolasyonPoz.set(noktaB.x, noktaB.y, noktaB.z);
      this.interpolasyonBasladi = true;
      this.pozisyonDegisti();
    } else {
      // Segment ortasında - interpolasyon pozisyonu hesapla
      const t = yon > 0 ? this.interpolasyonT : (1 - this.interpolasyonT);
      this.interpolasyonPoz.set(
        noktaA.x + dx * t,
        noktaA.y + dy * t,
        noktaA.z + dz * t
      );
      this.interpolasyonBasladi = true; // İnterpolasyon aktif
      this.interpolasyonPozisyonuUygula(noktaA, noktaB, t);
    }
  }

  /** Çizgi üzerinde ilerleme modunu aç/kapat */
  tiklandiCizgiModuToggle() {
    this.cizgiUzerindeIlerle = !this.cizgiUzerindeIlerle;
    if (!this.cizgiUzerindeIlerle) {
      // Mod kapandığında sıfırla
      this.interpolasyonBasladi = false;
      this.interpolasyonT = 0;
      this.interpolasyonCizgisiGizle();
    }
    this.CD.detectChanges();
  }

  /** İnterpolasyon pozisyonunu takıma ve efektlere uygula */
  private interpolasyonPozisyonuUygula(noktaA: any, noktaB: any, t: number) {
    // Çizgi çizim aralığını güncelle (tam poz değil, ara değer)
    const cizimPoz = Math.floor(this.poz) + t;
    this.similasyonCizgisi.geometry.setDrawRange(this.pozMin * 2, Math.floor((cizimPoz - this.pozMin) * 2));
    this.simGeoBufferNoktalar.setDrawRange(this.pozMin, Math.floor(cizimPoz - this.pozMin) + 1);

    this.oncekiNokta = noktaA;
    this.aktifNokta = noktaB; // Hedef nokta - hareket türü ve UI bilgisi buradan gelir

    if (!this.aktifNokta) return;

    // Takım yönü interpolasyonu (slerp benzeri basit lerp)
    let takimYonuX = noktaA.takimYonu?.x || 0;
    let takimYonuY = noktaA.takimYonu?.y || 0;
    let takimYonuZ = noktaA.takimYonu?.z || 1;
    if (noktaB.takimYonu) {
      takimYonuX += (noktaB.takimYonu.x - takimYonuX) * t;
      takimYonuY += (noktaB.takimYonu.y - takimYonuY) * t;
      takimYonuZ += (noktaB.takimYonu.z - takimYonuZ) * t;
    }

    // Takımı interpolasyon pozisyonuna yerleştir (pre-allocated buffers)
    const interpTakimYonu = this._simTmpV1.set(takimYonuX, takimYonuY, takimYonuZ).normalize();
    const mx = this._simTmpM4.lookAt(interpTakimYonu, this._simTmpV2.set(0, 0, 0), this._simTmpV3.set(0, 0, 1));
    const qt = this._simTmpQ1.setFromRotationMatrix(mx);

    if (this.aktifNokta.takim) {
      this.aktifNokta.takim.quaternion.copy(qt);
      this.aktifNokta.takim.position.copy(this.interpolasyonPoz);

      let aktifTakim = this.aktifTakimAl();
      if (aktifTakim) {
        aktifTakim.clear();
        aktifTakim.add(this.aktifNokta.takim);
        aktifTakim.updateMatrixWorld();
      }
    }

    // İnterpolasyon çizgisi: son NC noktasından takım pozisyonuna kadar çiz
    const baslangicNokta = this._simTmpV4.set(noktaA.x, noktaA.y, noktaA.z);
    // Hareket türü hedef noktada (noktaB) tanımlıdır - A'dan B'ye giderken B'nin hareket türü geçerli
    const hareket = this.hareketTuruAl(noktaB);
    let cizgiRenk: THREE.Color;
    switch (hareket) {
      case 'İşleme': case 'ISLEME': case 'Isleme':
        cizgiRenk = this.renkIsleme; break;
      case 'Yaklaşma': case 'YAKLASMA': case 'Yaklasma':
        cizgiRenk = this.renkYaklasma; break;
      case 'Uzaklaşma': case 'UZAKLASMA': case 'Uzaklasma':
        cizgiRenk = this.renkUzaklasma; break;
      case 'Boşta': case 'BOSTA': case 'Bosta':
        cizgiRenk = this.renkBosta; break;
      default:
        cizgiRenk = this.renkBilinmiyor;
    }
    this.interpolasyonCizgisiGuncelle(baslangicNokta, this.interpolasyonPoz, cizgiRenk);

    // Göstergeleri güncelle (interpolasyon pozisyonu ile)
    this.operasyonEksenAl();
    if (this.islemeEkseni) {
      if (!this.gostergeNokta) { this.gostergeNokta = new THREE.Vector3(); }
      (this.gostergeNokta as THREE.Vector3).copy(this.interpolasyonPoz);
      this.gostergeNokta.sub(this.islemeEkseni.merkez);
      this.gostergeNokta.applyQuaternion(this._simTmpQ2.copy(this.islemeEkseni.quaternion).invert());
    }

    // Yön okunu güncelle
    if (this.yonOku && this.yonOkuGorunur) {
      const dir = this._simTmpV4.set(
        noktaB.x - noktaA.x,
        noktaB.y - noktaA.y,
        noktaB.z - noktaA.z
      );
      if (dir.lengthSq() > 0.0001) {
        dir.normalize();
        this.yonOku.position.copy(this.interpolasyonPoz);
        this.yonOku.setDirection(dir);
        this.yonOku.visible = true;
      }
    }

    // Efektleri güncelle
    this.sesGuncelle();
    this.talasGuncelle();
    this.titresimGuncelle();
    this.efektGuncelle();
    this.hudGuncelle();
    this.sonarGuncelle();

    // Kamera takip modu: interpolasyon pozisyonunu takip et
    if (this.kameraModu > 0) {
      const gosterim = this._sahne?.anaGosterim;
      const kamera = gosterim?.aktifKamera;
      const kontrol = gosterim?.aktifKontrol;
      if (kamera && kontrol) {
        if (this.kameraModu === 2) {
          // Sinematik drone kamera - interpolasyon pozisyonunu kullan
          this.droneGuncelle(kamera, kontrol, this.interpolasyonPoz);
        } else {
          // Takip kilidi - interpolasyon pozisyonunu takip et (pre-allocated)
          const hedef = this._simTmpV4.copy(this.interpolasyonPoz);
          const lerpF = this.kameraYumusaklik > 0
            ? Math.pow(0.015, this.kameraYumusaklik / 100)
            : 1.0;
          const offset = this._simTmpV5.copy(kamera.position).sub(kontrol.target);
          kontrol.target.lerp(hedef, lerpF);
          kamera.position.copy(kontrol.target).add(offset);
          kamera.updateProjectionMatrix();
          kontrol.update?.();
        }
      }
    }

    // PERF: İnterpolasyon sırasında CD'yi throttle et
    if (++this._cdThrottleCounter % 2 === 0) {
      this.CD.detectChanges();
    }
    this._sahne?.anaGosterim?.ciz();
  }

  tiklandiDuraklat() {
    if (this.similasyonIntervalId) {
      clearInterval(this.similasyonIntervalId);
      this.similasyonIntervalId = null;
    }
    this.simOynatiliyor = false;
    // Interval temizlendikten sonra sesi sustur + titresimi temizle
    this.sesGuncelle();
    this.titresimGuncelle();
    // Duraklatmada bounding box'ı son haline güncelle
    if (this.dexelMesh?.geometry) {
      this.dexelMesh.geometry.computeBoundingBox();
      this.dexelMesh.geometry.computeBoundingSphere();
    }
  }


  operasyonEksenAl() {
    let fikop = this._anaNesne;
    if (!this.islemeEkseni) {
      while (fikop) {
        if (fikop.type == "Fikoperasyon") {
          this.islemeEkseni = (fikop as FikoOperasyon).islemeEkseni;
          this.islemeEkseni?.updateMatrixWorld();
          break;
        }
        fikop = fikop.parent;
      }
    }
    return this.islemeEkseni
  }

  pozisyonDegisti() {
    // İnterpolasyon modunda manuel pozisyon değişikliğinde sıfırla
    this.interpolasyonT = 0;
    this.interpolasyonCizgisiGizle(); // Tam noktaya ulaşıldığında dinamik çizgiyi gizle

    this.similasyonCizgisi.geometry.setDrawRange(this.pozMin * 2, (this.poz - this.pozMin) * 2);
    this.simGeoBufferNoktalar.setDrawRange(this.pozMin, (this.poz - this.pozMin) + 1);
    this.oncekiNokta = this.aktifNokta;
    this.aktifNokta = this.noktalar[this.poz];

    if (!this.aktifNokta) return;
    this.operasyonEksenAl();

    this.gostergeleriAyarla();

    this.takimAyarla();

    // Gidis yonu okunu guncelle (pre-allocated buffers)
    if (this.yonOku) {
      if (this.yonOkuGorunur && this.aktifNokta) {
        const pos = this._simTmpV4.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
        const dir = this._simTmpV5;
        let dirValid = false;
        if (this.poz + 1 < this.noktalar.length) {
          const sonraki = this.noktalar[this.poz + 1];
          dir.set(sonraki.x - this.aktifNokta.x, sonraki.y - this.aktifNokta.y, sonraki.z - this.aktifNokta.z);
          dirValid = true;
        } else if (this.poz > 0) {
          const onceki = this.noktalar[this.poz - 1];
          dir.set(this.aktifNokta.x - onceki.x, this.aktifNokta.y - onceki.y, this.aktifNokta.z - onceki.z);
          dirValid = true;
        }
        if (dirValid && dir.lengthSq() > 0.0001) {
          dir.normalize();
          this.yonOku.position.copy(pos);
          this.yonOku.setDirection(dir);
          this.yonOku.visible = true;
        } else {
          this.yonOku.visible = false;
        }
      } else {
        this.yonOku.visible = false;
      }
    }

    // Kamera modu: 1=takip kilidi (lerp), 2=sinematik drone (orbit)
    if (this.kameraModu > 0 && this.aktifNokta) {
      const gosterim = this._sahne?.anaGosterim;
      const kamera = gosterim?.aktifKamera;
      const kontrol = gosterim?.aktifKontrol;
      if (kamera && kontrol) {
        if (this.kameraModu === 2) {
          // Sinematik drone kamera
          this.droneGuncelle(kamera, kontrol);
        } else {
          // Takip kilidi (lerp) - pre-allocated
          const hedef = this._simTmpV4.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
          const lerpF = this.kameraYumusaklik > 0
            ? Math.pow(0.015, this.kameraYumusaklik / 100)
            : 1.0;
          const offset = this._simTmpV5.copy(kamera.position).sub(kontrol.target);
          kontrol.target.lerp(hedef, lerpF);
          kamera.position.copy(kontrol.target).add(offset);
          kamera.updateProjectionMatrix();
          kontrol.update?.();
        }
      }
    }

    this.sesGuncelle();
    this.talasGuncelle();
    this.titresimGuncelle();
    this.carpismaKontrol();
    this.efektGuncelle();
    this.hudGuncelle();
    this.sonarGuncelle();

    // ═══════════════════════════════════════════════════════════════════════
    // İLERİ/GERİ SARMA DESTEĞİ - Slider veya buton ile pozisyon değiştiğinde
    // Dexel modunda snapshot-based seeking kullan
    // ═══════════════════════════════════════════════════════════════════════
    if (this.kutukSimModu === 'dexel' && this.dexelEngine) {
      // Seeking sistemi: otomatik olarak ileri veya geri sar
      this.handleDexelSeek(this.poz);
    } else if ((this.kutukSimModu === 'voxel' || this.kutukSimModu === 'marchingcubes') && this.voxelEngine) {
      // Voxel CSG / Marching Cubes material removal (same voxel data, different mesh gen)
      this.handleVoxelProcessing(this.poz);
    } else if (this.kutukSimModu === 'sdf' && this.sdfEngine) {
      // SDF material removal
      this.handleSDFProcessing(this.poz);
    } else if (this.kutukSimModu === 'zbuffer' && this.zbufferEngine) {
      // Z-Buffer material removal
      this.handleZBufferProcessing(this.poz);
    } else {
      // Diğer modlarda normal material removal
      this.kutukMaterialRemoval();
    }

    // PERF: Oynatma sırasında CD'yi her 2 tick'te 1 çalıştır (~8fps UI, yeterli)
    if (!this.simOynatiliyor || ++this._cdThrottleCounter % 2 === 0) {
      this.CD.detectChanges();
    }
    this._sahne?.anaGosterim?.ciz();
  }

  takimAyarla() {
    const myDirectionVector = this._simTmpV1.copy(this.aktifNokta.takimYonu);
    const mx = this._simTmpM4.lookAt(myDirectionVector, this._simTmpV2.set(0, 0, 0), this._simTmpV3.set(0, 0, 1));
    const qt = this._simTmpQ1.setFromRotationMatrix(mx);
    this.aktifNokta.takim.quaternion.copy(qt);

    this.aktifNokta.takim.position.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
    let aktifTakim = this.aktifTakimAl();
    if (!aktifTakim) return;

    aktifTakim.clear()
    aktifTakim.add(this.aktifNokta.takim);
    aktifTakim.updateMatrixWorld();
  }

  tiklandiTutucuTanimla() {
    this.aktifNokta?.takim?.tutucu?.hazirla()
  }

  /** Kamera modu: 0=serbest, 1=takip kilidi, 2=sinematik drone. Tiklayinca dongu yapar. */
  tiklandiKameraKilitle() {
    this.kameraModu = (this.kameraModu + 1) % 3;
    if (this.kameraModu === 0) this.droneAci = 0; // drone'dan cikinca sifirla
    this.CD.detectChanges();

    // Sadece takip kilidine (1) girildiginde takima zoom yap
    if (this.kameraModu !== 1 || !this.aktifNokta) return;

    const gosterim = this._sahne?.anaGosterim;
    if (!gosterim) return;

    const kamera = gosterim.aktifKamera;
    const kontrol = gosterim.aktifKontrol;
    if (!kamera || !kontrol) return;

    const hedef = this._simTmpV4.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
    const offset = this._simTmpV5.copy(kamera.position).sub(kontrol.target);
    kontrol.target.copy(hedef);
    kamera.position.copy(hedef).add(offset);

    const takimCap = this.aktifNokta.takim?.cap || 20;
    const canvasH = gosterim.canvas?.nativeElement?.clientHeight || 600;
    const hedefZoom = canvasH / (takimCap * 10);
    if (hedefZoom > kamera.zoom) {
      kamera.zoom = hedefZoom;
    }
    kamera.updateProjectionMatrix();
    kontrol.update?.();
    gosterim.ciz?.();
  }

  // ─── Sinematik Drone Kamera ──────────────────────────────────────────

  /** Drone kamera: takim etrafinda otomatik orbit, kesim turune adaptif */
  private droneGuncelle(kamera: any, kontrol: any, pozisyonOverride?: THREE.Vector3) {
    if (!this.aktifNokta) return;

    // Hareket turune gore adaptif hiz/mesafe
    const isleme = this.aktifNokta.hareketTuru === 'İşleme' || this.aktifNokta.hareketTuru === 'Isleme';
    const hizCarpan = isleme ? 0.6 : 1.5; // kesimde yavas, bosta hizli
    const mesafeCarpan = isleme ? 0.7 : 1.3; // kesimde yakin, bosta uzak

    // Orbit hizi: slider 0-100 → 0.002 - 0.06 rad/frame
    const orbitHiz = (0.002 + (this.droneHiz / 100) * 0.058) * hizCarpan;
    this.droneAci += orbitHiz;
    if (this.droneAci > Math.PI * 2) this.droneAci -= Math.PI * 2;

    // Takim pozisyonu = orbit merkezi (pre-allocated buffers)
    const merkez = this._simTmpV5;
    if (pozisyonOverride) { merkez.copy(pozisyonOverride); }
    else { merkez.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z); }

    // Takim yonu (eksen) - orbit duzlemi buna dik
    const takimYonu = this._simTmpV1;
    if (this.aktifNokta.takimYonu) { takimYonu.copy(this.aktifNokta.takimYonu).normalize(); }
    else { takimYonu.set(0, 0, 1); }

    // Takim eksenine dik iki vektor olustur (orbit duzlemi)
    const referans = this._simTmpV2;
    referans.set(Math.abs(takimYonu.y) > 0.9 ? 1 : 0, Math.abs(takimYonu.y) > 0.9 ? 0 : 1, 0);
    const dikU = this._simTmpV3.crossVectors(takimYonu, referans).normalize();
    const dikV = this._simTmpV4.crossVectors(takimYonu, dikU).normalize();

    // Mesafe: slider 0-100 → 20-300 birim (takim capina oranli)
    const takimCap = this.aktifNokta.takim?.cap || 20;
    const baz = takimCap * 3;
    const mesafe = (baz + (this.droneMesafe / 100) * baz * 4) * mesafeCarpan;

    // Yukseklik acisi: slider 0-100 → 5° - 85° (takim ekseninden sapma)
    const yukAciDerece = 5 + (this.droneYukseklik / 100) * 80;
    const yukAciRad = yukAciDerece * Math.PI / 180;

    // Orbit pozisyonu hesapla: yatay daire + dikey kavis
    const yatayR = mesafe * Math.sin(yukAciRad);
    const dikeyH = mesafe * Math.cos(yukAciRad);

    const orbitPos = this._simTmpV6.set(0, 0, 0)
      .addScaledVector(dikU, Math.cos(this.droneAci) * yatayR)
      .addScaledVector(dikV, Math.sin(this.droneAci) * yatayR)
      .addScaledVector(takimYonu, dikeyH)
      .add(merkez);

    // Yumusak gecis (lerp) - sabit 0.08 faktoru (sinematik his)
    const lerpF = 0.08;
    kontrol.target.lerp(merkez, lerpF);
    kamera.position.lerp(orbitPos, lerpF);
    kamera.lookAt(kontrol.target);
    kamera.updateProjectionMatrix();
    kontrol.update?.();
  }

  // ─── Lazer İzi (Tron Effect) + Hayalet Takım (Ghost) ─────────────────

  /** Efekt toggle: 0=kapalı → 1=lazer izi → 2=hayalet → 0 */
  tiklandiEfektToggle() {
    // Onceki modu temizle
    if (this.efektModu === 1) this.lazerIzGizle();
    if (this.efektModu === 2) this.hayaletGizle();

    this.efektModu = (this.efektModu + 1) % 3;
    this.CD.detectChanges();
  }

  /** Her frame cagirilir - aktif efekti gunceller */
  private efektGuncelle() {
    if (this.efektModu === 1) {
      this.lazerIziGuncelle();
    } else if (this.efektModu === 2) {
      this.hayaletGuncelle();
    }
  }

  // ── Lazer İzi (Tron) ──

  /** Lazer iz trail baslat (lazy init) */
  private lazerIzBaslat() {
    this.lazerIzGeo = new THREE.BufferGeometry();
    const pozArr = new Float32Array(this.LAZER_IZ_BOYUT * 3);
    const renkArr = new Float32Array(this.LAZER_IZ_BOYUT * 3);
    this.lazerIzBazRenkler = new Float32Array(this.LAZER_IZ_BOYUT * 3);
    this.lazerIzGeo.setAttribute('position', new THREE.Float32BufferAttribute(pozArr, 3));
    this.lazerIzGeo.setAttribute('color', new THREE.Float32BufferAttribute(renkArr, 3));
    this.lazerIzGeo.setDrawRange(0, 0);

    const mat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.lazerIzCizgisi = new THREE.Line(this.lazerIzGeo, mat);
    this.lazerIzCizgisi.frustumCulled = false;
    this.lazerIzCizgisi.renderOrder = 999;
    this._sahne?.add(this.lazerIzCizgisi);
  }

  /** Her frame: yeni pozisyon ekle, renkleri fade et */
  private lazerIziGuncelle() {
    if (!this.aktifNokta) return;
    if (!this.lazerIzCizgisi) this.lazerIzBaslat();

    const pozAttr = this.lazerIzGeo.getAttribute('position') as THREE.BufferAttribute;
    const renkAttr = this.lazerIzGeo.getAttribute('color') as THREE.BufferAttribute;

    // Buffer doluysa ilk yarıyı at, ikinci yarıyı basa kopyala
    if (this.lazerIzSayac >= this.LAZER_IZ_BOYUT) {
      const half = Math.floor(this.LAZER_IZ_BOYUT / 2);
      (pozAttr.array as Float32Array).copyWithin(0, half * 3);
      this.lazerIzBazRenkler.copyWithin(0, half * 3);
      this.lazerIzSayac -= half;
    }

    // Yeni pozisyon ekle
    const idx = this.lazerIzSayac;
    pozAttr.setXYZ(idx, this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);

    // Renk: İşleme=cyan, rapid=turuncu
    const isleme = this.aktifNokta.hareketTuru === 'İşleme' || this.aktifNokta.hareketTuru === 'Isleme';
    const bi = idx * 3;
    if (isleme) {
      this.lazerIzBazRenkler[bi] = 0; this.lazerIzBazRenkler[bi + 1] = 1; this.lazerIzBazRenkler[bi + 2] = 1;
    } else {
      this.lazerIzBazRenkler[bi] = 1; this.lazerIzBazRenkler[bi + 1] = 0.5; this.lazerIzBazRenkler[bi + 2] = 0;
    }

    this.lazerIzSayac++;

    // Tum renkleri yasa gore fade et (yeni=parlak, eski=karanlik)
    const renkArr = renkAttr.array as Float32Array;
    for (let i = 0; i < this.lazerIzSayac; i++) {
      const yas = (this.lazerIzSayac - 1 - i) / this.LAZER_IZ_BOYUT;
      const parlaklik = Math.pow(1 - yas, 2); // kuadratik solma
      const ri = i * 3;
      renkArr[ri] = this.lazerIzBazRenkler[ri] * parlaklik;
      renkArr[ri + 1] = this.lazerIzBazRenkler[ri + 1] * parlaklik;
      renkArr[ri + 2] = this.lazerIzBazRenkler[ri + 2] * parlaklik;
    }

    pozAttr.needsUpdate = true;
    renkAttr.needsUpdate = true;
    this.lazerIzGeo.setDrawRange(0, this.lazerIzSayac);
    this.lazerIzCizgisi.visible = true;
  }

  /** Lazer izi gizle (mod degistiginde) */
  private lazerIzGizle() {
    if (this.lazerIzCizgisi) this.lazerIzCizgisi.visible = false;
  }

  /** Lazer izi tamamen temizle */
  private lazerIzTemizle() {
    if (this.lazerIzCizgisi) {
      this.lazerIzCizgisi.parent?.remove(this.lazerIzCizgisi);
      this.lazerIzGeo?.dispose();
      (this.lazerIzCizgisi.material as THREE.Material)?.dispose();
      this.lazerIzCizgisi = null;
      this.lazerIzGeo = null;
      this.lazerIzBazRenkler = null;
      this.lazerIzSayac = 0;
    }
  }

  // ── Hayalet Takım (Ghost) ──

  /** Hayalet takım baslat (lazy init): wireframe konik mesh + kesikli cizgi */
  private hayaletBaslat() {
    const cap = this.aktifNokta?.takim?.cap || 20;
    const radius = cap / 2;
    const boy = cap * 3;

    this.hayaletGrup = new THREE.Group();

    // Konik takim temsili (wireframe, yari-saydam, neon yesil)
    const geo = new THREE.ConeGeometry(radius, boy, 12);
    geo.translate(0, 0, -boy / 2);
    geo.rotateX(Math.PI / 2);

    const mat = new THREE.MeshBasicMaterial({
      color: 0x00ffaa,
      transparent: true,
      opacity: 0.25,
      wireframe: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geo, mat);
    this.hayaletGrup.add(mesh);
    this.hayaletGrup.renderOrder = 998;
    this._sahne?.add(this.hayaletGrup);

    // Kesikli cizgi: mevcut pozisyondan hayalete (2 nokta)
    const cizgiGeo = new THREE.BufferGeometry();
    cizgiGeo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(6), 3));
    const cizgiMat = new THREE.LineDashedMaterial({
      color: 0x00ffaa,
      transparent: true,
      opacity: 0.4,
      dashSize: 3,
      gapSize: 3,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.hayaletCizgi = new THREE.Line(cizgiGeo, cizgiMat);
    this.hayaletCizgi.frustumCulled = false;
    this.hayaletCizgi.renderOrder = 997;
    this._sahne?.add(this.hayaletCizgi);
  }

  /** Her frame: hayaleti N adım ilerideki pozisyona yerlestir */
  private hayaletGuncelle() {
    if (!this.aktifNokta || !this.noktalar?.length) return;

    // Slider 0-100 → 10-200 adım ileri
    const adim = Math.round(10 + (this.hayaletMesafe / 100) * 190);
    const gelecekIdx = Math.min(this.poz + adim, this.noktalar.length - 1);
    const gelecekNokta = this.noktalar[gelecekIdx];
    if (!gelecekNokta) return;

    if (!this.hayaletGrup) this.hayaletBaslat();

    // Hayalet pozisyon ve yön (pre-allocated buffers)
    const takimYonu = this._simTmpV1;
    if (gelecekNokta.takimYonu) { takimYonu.copy(gelecekNokta.takimYonu); } else { takimYonu.set(0, 0, 1); }
    const mx = this._simTmpM4.lookAt(takimYonu, this._simTmpV2.set(0, 0, 0), this._simTmpV3.set(0, 0, 1));
    const qt = this._simTmpQ1.setFromRotationMatrix(mx);

    this.hayaletGrup.position.set(gelecekNokta.x, gelecekNokta.y, gelecekNokta.z);
    this.hayaletGrup.quaternion.copy(qt);
    this.hayaletGrup.visible = true;

    // Opacity: yaklaştıkça daha belirgin
    const mesafeOrani = Math.abs(gelecekIdx - this.poz) / Math.max(adim, 1);
    const opacity = 0.1 + (1 - mesafeOrani) * 0.3;
    ((this.hayaletGrup.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = opacity;

    // Kesikli cizgi guncelle: mevcut → hayalet
    if (this.hayaletCizgi) {
      const pozAttr = this.hayaletCizgi.geometry.getAttribute('position') as THREE.BufferAttribute;
      pozAttr.setXYZ(0, this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
      pozAttr.setXYZ(1, gelecekNokta.x, gelecekNokta.y, gelecekNokta.z);
      pozAttr.needsUpdate = true;
      this.hayaletCizgi.geometry.computeBoundingSphere();
      (this.hayaletCizgi as any).computeLineDistances();
      this.hayaletCizgi.visible = true;
    }
  }

  /** Hayalet gizle (mod degistiginde) */
  private hayaletGizle() {
    if (this.hayaletGrup) this.hayaletGrup.visible = false;
    if (this.hayaletCizgi) this.hayaletCizgi.visible = false;
  }

  /** Hayalet tamamen temizle */
  private hayaletTemizle() {
    if (this.hayaletGrup) {
      this.hayaletGrup.parent?.remove(this.hayaletGrup);
      this.hayaletGrup.children.forEach((c: any) => { c.geometry?.dispose(); c.material?.dispose(); });
      this.hayaletGrup = null;
    }
    if (this.hayaletCizgi) {
      this.hayaletCizgi.parent?.remove(this.hayaletCizgi);
      this.hayaletCizgi.geometry?.dispose();
      (this.hayaletCizgi.material as THREE.Material)?.dispose();
      this.hayaletCizgi = null;
    }
  }

  /** Tum efektleri temizle */
  private efektTemizle() {
    this.lazerIzTemizle();
    this.hayaletTemizle();
    this.efektModu = 0;
  }

  // ─── / Lazer İzi + Hayalet Takım ───────────────────────────────────────

  // ─── 3D HUD Gösterge (Holografik Kokpit) ───────────────────────────

  /** HUD toggle - butondan cagirilir */
  tiklandiHudToggle() {
    this.hudAktif = !this.hudAktif;
    if (!this.hudAktif) this.hudGizle();
    this.CD.detectChanges();
  }

  /** HUD lazy init: halka gauge'lar, nisangah, metin sprite'lari */
  private hudBaslat() {
    const cap = this.aktifNokta?.takim?.cap || 20;
    this.hudGrup = new THREE.Group();
    this.hudGrup.renderOrder = 1000;

    const additiveMat = (renk: number, opa: number) => new THREE.LineBasicMaterial({
      color: renk, transparent: true, opacity: opa,
      blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
    });

    // ── Dış halka (RPM gauge) ──
    const disR = cap * 4;
    this.hudGrup.add(this.hudArcOlustur(disR, 0, Math.PI * 2, additiveMat(0x00ffff, 0.12)));
    const devirArc = this.hudArcOlustur(disR, -Math.PI / 2, Math.PI * 1.5, additiveMat(0x00ffff, 0.9));
    this.hudDevirArcGeo = devirArc.geometry as THREE.BufferGeometry;
    this.hudGrup.add(devirArc);

    // ── İç halka (Hız gauge) ──
    const icR = cap * 3;
    this.hudGrup.add(this.hudArcOlustur(icR, 0, Math.PI * 2, additiveMat(0xffcc00, 0.12)));
    const hizArc = this.hudArcOlustur(icR, -Math.PI / 2, Math.PI * 1.5, additiveMat(0xffcc00, 0.9));
    this.hudHizArcGeo = hizArc.geometry as THREE.BufferGeometry;
    this.hudGrup.add(hizArc);

    // ── Nişangah (crosshair) ──
    const nR = cap * 1.2;
    const nGeo = new THREE.BufferGeometry();
    nGeo.setAttribute('position', new THREE.Float32BufferAttribute([
      -nR, 0, 0, nR, 0, 0,
      0, -nR, 0, 0, nR, 0
    ], 3));
    const nisangah = new THREE.LineSegments(nGeo, additiveMat(0x00ffff, 0.4));
    nisangah.frustumCulled = false;
    this.hudGrup.add(nisangah);

    // ── Köşe çizgileri (tactical bracket) ──
    const kR = disR * 1.05;
    const kBoy = cap * 0.8;
    const kosePozlar = new Float32Array([
      // sol üst
      -kR, kR - kBoy, 0, -kR, kR, 0, -kR + kBoy, kR, 0,
      // sağ üst
      kR - kBoy, kR, 0, kR, kR, 0, kR, kR - kBoy, 0,
      // sağ alt
      kR, -kR + kBoy, 0, kR, -kR, 0, kR - kBoy, -kR, 0,
      // sol alt
      -kR + kBoy, -kR, 0, -kR, -kR, 0, -kR, -kR + kBoy, 0,
    ]);
    const koseGeo = new THREE.BufferGeometry();
    koseGeo.setAttribute('position', new THREE.Float32BufferAttribute(kosePozlar, 3));
    const kose = new THREE.Line(koseGeo, additiveMat(0x00ffff, 0.3));
    kose.frustumCulled = false;
    this.hudGrup.add(kose);

    // ── Metin sprite'ları ──
    // 0=RPM (sağ üst), 1=Hız (sağ alt), 2=Koordinat (üst-iç), 3=Hareket türü (üst)
    const metinAyar = [
      { x: disR * 1.3, y: cap * 2.8, w: 320, h: 64, hiza: 'left' },
      { x: disR * 1.3, y: -cap * 1.8, w: 320, h: 64, hiza: 'left' },
      { x: 0, y: -disR * 0.6, w: 640, h: 64, hiza: 'center' },  // yukarı taşındı
      { x: 0, y: disR * 1.5, w: 320, h: 64, hiza: 'center' },
    ];

    this.hudMetinler = [];
    for (const ma of metinAyar) {
      const canvas = document.createElement('canvas');
      canvas.width = ma.w;
      canvas.height = ma.h;
      const ctx = canvas.getContext('2d');
      const tex = new THREE.CanvasTexture(canvas);
      tex.minFilter = THREE.LinearFilter;
      const mat = new THREE.SpriteMaterial({
        map: tex, transparent: true,
        blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.set(ma.x, ma.y, 0);
      const oran = ma.w / ma.h;
      sprite.scale.set(cap * 4 * (oran / 5), cap * 4 / 5, 1);
      this.hudGrup.add(sprite);
      this.hudMetinler.push({ sprite, canvas, ctx, tex });
    }

    this._sahne?.add(this.hudGrup);
  }

  /** Arc (yay) geometrisi olustur - tam veya kismi daire */
  private hudArcOlustur(radius: number, baslangic: number, bitis: number, mat: THREE.Material): THREE.Line {
    const geo = new THREE.BufferGeometry();
    const pozArr = new Float32Array((this.HUD_ARC_SEGMENTS + 1) * 3);
    for (let i = 0; i <= this.HUD_ARC_SEGMENTS; i++) {
      const a = baslangic + (bitis - baslangic) * (i / this.HUD_ARC_SEGMENTS);
      pozArr[i * 3] = Math.cos(a) * radius;
      pozArr[i * 3 + 1] = Math.sin(a) * radius;
      pozArr[i * 3 + 2] = 0;
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pozArr, 3));
    const line = new THREE.Line(geo, mat);
    line.frustumCulled = false;
    return line;
  }

  /** Her frame: HUD pozisyon, arc doluluk, metin guncelle */
  private hudGuncelle() {
    if (!this.hudAktif || !this.aktifNokta) return;
    if (!this.hudGrup) this.hudBaslat();

    // Takım pozisyonu: interpolasyon modunda interpolasyonPoz kullan
    const pozX = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.x : this.aktifNokta.x;
    const pozY = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.y : this.aktifNokta.y;
    const pozZ = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.z : this.aktifNokta.z;

    // Takım ucuna yerleştir
    this.hudGrup.position.set(pozX, pozY, pozZ);

    // Billboard: kameraya bak
    const kamera = this._sahne?.anaGosterim?.aktifKamera;
    if (kamera) {
      this.hudGrup.quaternion.copy(kamera.quaternion);
    }

    // Boyut: slider 0-100 → 0.3x - 3x
    const olcek = 0.3 + (this.hudBoyut / 100) * 2.7;
    this.hudGrup.scale.setScalar(olcek);

    // RPM arc doluluk (devir / max)
    const devir = this.aktifNokta.devir || 0;
    const devirOran = Math.min(devir / 12000, 1); // max 12000 rpm
    this.hudDevirArcGeo.setDrawRange(0, Math.round(this.HUD_ARC_SEGMENTS * devirOran) + 1);

    // Hız arc doluluk (ilerleme / max)
    const hiz = this.aktifNokta.hiz || 0;
    const reelHiz = hiz * (this.reelIlerleme / 100);
    const hizOran = Math.min(hiz / 5000, 1); // max 5000 mm/dk
    this.hudHizArcGeo.setDrawRange(0, Math.round(this.HUD_ARC_SEGMENTS * hizOran) + 1);

    // Metin güncelle
    const isleme = this.aktifNokta.hareketTuru === 'İşleme' || this.aktifNokta.hareketTuru === 'Isleme';
    this.hudMetinYaz(0, `${devir} rpm`, '#00ffff', 'left');
    this.hudMetinYaz(1, `F ${reelHiz.toFixed(0)} mm/dk`, '#ffcc00', 'left');
    this.hudMetinYaz(2,
      `X:${pozX.toFixed(2)}  Y:${pozY.toFixed(2)}  Z:${pozZ.toFixed(2)}`,
      '#88ddff', 'center');
    this.hudMetinYaz(3,
      this.aktifNokta.hareketTuru || '—',
      isleme ? '#00ff88' : '#ff8844', 'center');

    this.hudGrup.visible = true;
  }

  /** Canvas sprite'a metin yaz */
  private hudMetinYaz(idx: number, metin: string, renk: string, hiza: string) {
    const item = this.hudMetinler[idx];
    if (!item) return;
    // Cache: sadece metin veya renk değiştiyse GPU texture upload yap
    const key = metin + '|' + renk;
    if (this.hudOncekiMetinler[idx] === key) return;
    this.hudOncekiMetinler[idx] = key;
    const { canvas, ctx, tex } = item;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 32px monospace';
    ctx.fillStyle = renk;
    ctx.shadowColor = renk;
    ctx.shadowBlur = 10;
    ctx.textAlign = hiza as CanvasTextAlign;
    const x = hiza === 'center' ? canvas.width / 2 : hiza === 'right' ? canvas.width - 4 : 4;
    ctx.fillText(metin, x, 42);
    ctx.shadowBlur = 0;
    tex.needsUpdate = true;
  }

  /** HUD gizle */
  private hudGizle() {
    if (this.hudGrup) this.hudGrup.visible = false;
  }

  /** HUD tamamen temizle */
  private hudTemizle() {
    if (this.hudGrup) {
      this.hudGrup.parent?.remove(this.hudGrup);
      this.hudGrup.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (obj.material.map) obj.material.map.dispose();
          obj.material.dispose();
        }
      });
      this.hudGrup = null;
    }
    this.hudMetinler = [];
    this.hudOncekiMetinler = [];
    this.hudDevirArcGeo = null;
    this.hudHizArcGeo = null;
  }

  // ─── / 3D HUD Gösterge ────────────────────────────────────────────────

  /** Araçlar popup panelini aç/kapat - buton pozisyonuna göre konumlandır */
  tiklandiAraclarToggle(event: MouseEvent) {
    this.araclarPanelAcik = !this.araclarPanelAcik;
    if (this.araclarPanelAcik) {
      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      // Butonun altına, sola yasla
      this.araclarPopupTop = rect.bottom + 4;
      this.araclarPopupLeft = Math.max(8, rect.left);
      // Ekran sınırlarını kontrol et
      const popupWidth = 230;
      if (this.araclarPopupLeft + popupWidth > window.innerWidth - 8) {
        this.araclarPopupLeft = window.innerWidth - popupWidth - 8;
      }
    }
    this.CD.detectChanges();
  }

  // ─── Sonar Dalga Efekti (Pulse Wave Rings) ────────────────────────────

  /** Sonar toggle - butondan cagirilir */
  tiklandiSonarToggle() {
    this.sonarAktif = !this.sonarAktif;
    if (!this.sonarAktif) this.sonarGizle();
    this.CD.detectChanges();
  }

  /** Sonar lazy init: halka mesh havuzu olustur */
  private sonarBaslat() {
    const cap = this.aktifNokta?.takim?.cap || 20;
    const baseR = cap * 0.5;

    this.sonarGrup = new THREE.Group();
    this.sonarGrup.renderOrder = 999;
    this._sahne?.add(this.sonarGrup);

    for (let i = 0; i < this.SONAR_MAX; i++) {
      // Ince halka: ic yaricap = dis yaricap * 0.88
      const geo = new THREE.RingGeometry(baseR * 0.88, baseR, 48);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.visible = false;
      mesh.frustumCulled = false;
      this.sonarGrup.add(mesh);
      this.sonarHalkalar.push({ mesh, yas: 0, aktif: false });
    }
  }

  /** Her frame: yeni halka spawn et, mevcut halkalari genislet + soldir */
  private sonarGuncelle() {
    if (!this.sonarAktif || !this.aktifNokta) return;
    if (!this.sonarGrup) this.sonarBaslat();
    if (!this.sonarGrup.visible) this.sonarGrup.visible = true;

    // Takım pozisyonu: interpolasyon modunda interpolasyonPoz kullan
    const pozX = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.x : this.aktifNokta.x;
    const pozY = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.y : this.aktifNokta.y;
    const pozZ = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.z : this.aktifNokta.z;

    this.sonarFrame++;

    // Dalga frekansı: slider 0-100 → her 14-2 frame'de yeni halka
    const aralik = Math.max(2, Math.round(14 - (this.sonarHiz / 100) * 12));

    if (this.sonarFrame % aralik === 0) {
      // En eski inaktif halkayi bul, yoksa en yaslisini recycle et
      let halka = this.sonarHalkalar.find(h => !h.aktif);
      if (!halka) {
        halka = this.sonarHalkalar.reduce((a, b) => a.yas > b.yas ? a : b);
      }

      halka.yas = 0;
      halka.aktif = true;

      // Pozisyon: takım ucuna yerleştir (interpolasyon pozisyonu)
      halka.mesh.position.set(pozX, pozY, pozZ);

      // Renk: İşleme=cyan, rapid=turuncu
      const isleme = this.aktifNokta.hareketTuru === 'İşleme' || this.aktifNokta.hareketTuru === 'Isleme';
      (halka.mesh.material as THREE.MeshBasicMaterial).color.set(isleme ? 0x00ffff : 0xff8800);

      // Takım eksenine dik yönlendir (pre-allocated buffers)
      const takimYonu = this._simTmpV1;
      if (this.aktifNokta.takimYonu) { takimYonu.copy(this.aktifNokta.takimYonu); } else { takimYonu.set(0, 0, 1); }
      const up = this._simTmpV2;
      up.set(Math.abs(takimYonu.y) > 0.99 ? 1 : 0, Math.abs(takimYonu.y) > 0.99 ? 0 : 1, 0);
      const mx = this._simTmpM4.lookAt(this._simTmpV3.set(0, 0, 0), takimYonu, up);
      halka.mesh.quaternion.setFromRotationMatrix(mx);

      halka.mesh.scale.setScalar(0.1);
      halka.mesh.visible = true;
    }

    // Tüm aktif halkaları güncelle: genişlet + soldir
    const cap = this.aktifNokta?.takim?.cap || 20;
    const maxOlcek = cap * 0.12; // son büyüklük

    for (const h of this.sonarHalkalar) {
      if (!h.aktif) continue;
      h.yas++;

      if (h.yas >= this.SONAR_OMUR) {
        h.aktif = false;
        h.mesh.visible = false;
        continue;
      }

      // Yaş oranı 0→1
      const t = h.yas / this.SONAR_OMUR;
      // Büyüme: ease-out (hızlı başla, yavaşla)
      const olcek = 0.1 + Math.pow(t, 0.6) * maxOlcek;
      h.mesh.scale.setScalar(olcek);

      // Opacity: hızlı parlak başla, yavaşça sol
      const opacity = 0.65 * Math.pow(1 - t, 1.5);
      (h.mesh.material as THREE.MeshBasicMaterial).opacity = opacity;
    }
  }

  /** Sonar gizle (toggle off) */
  private sonarGizle() {
    for (const h of this.sonarHalkalar) {
      h.aktif = false;
      h.mesh.visible = false;
    }
    if (this.sonarGrup) this.sonarGrup.visible = false;
    this.sonarFrame = 0;
  }

  /** Sonar tamamen temizle */
  private sonarTemizle() {
    if (this.sonarGrup) {
      this.sonarGrup.parent?.remove(this.sonarGrup);
      this.sonarGrup.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
      this.sonarGrup = null;
    }
    this.sonarHalkalar = [];
    this.sonarFrame = 0;
  }

  // ─── / Sonar Dalga Efekti ─────────────────────────────────────────────

  /** Template'den cagrilabilir 3D sahne yenileme */
  ciz() {
    this._sahne?.anaGosterim?.ciz();
  }

  // ─── Kesim Sesi Simulasyonu ───────────────────────────────────────────

  /** Ses toggle - butondan cagirilir */
  tiklandiSesToggle() {
    this.sesAktif = !this.sesAktif;
    if (this.sesAktif) {
      this.sesBaslat();
    } else {
      this.sesDurdur();
    }
    this.CD.detectChanges();
  }

  /** AudioContext ve oscillator'lari olustur */
  private sesBaslat() {
    if (this.audioCtx) return;
    this.audioCtx = new AudioContext();

    // Master gain: tum kaynaklar buraya baglanir
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.gain.value = this.sesMasterVolume / 100;
    this.masterGain.connect(this.audioCtx.destination);

    // Ana osilatör: testere dalga = kesim tonu
    this.oscMain = this.audioCtx.createOscillator();
    this.oscMain.type = 'sawtooth';
    this.oscMain.frequency.value = 0;
    this.gainMain = this.audioCtx.createGain();
    this.gainMain.gain.value = 0;
    this.oscMain.connect(this.gainMain);
    this.gainMain.connect(this.masterGain);
    this.oscMain.start();

    // Harmonik osilatör: kare dalga = metalik tini
    this.oscHarmonic = this.audioCtx.createOscillator();
    this.oscHarmonic.type = 'square';
    this.oscHarmonic.frequency.value = 0;
    this.gainHarmonic = this.audioCtx.createGain();
    this.gainHarmonic.gain.value = 0;
    this.oscHarmonic.connect(this.gainHarmonic);
    this.gainHarmonic.connect(this.masterGain);
    this.oscHarmonic.start();

    // Beyaz gürültü: talaş/grinding dokusu
    const bufferSize = this.audioCtx.sampleRate * 2;
    const noiseBuffer = this.audioCtx.createBuffer(1, bufferSize, this.audioCtx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    this.noiseSource = this.audioCtx.createBufferSource();
    this.noiseSource.buffer = noiseBuffer;
    this.noiseSource.loop = true;
    this.noiseGain = this.audioCtx.createGain();
    this.noiseGain.gain.value = 0;
    this.noiseSource.connect(this.noiseGain);
    this.noiseGain.connect(this.masterGain);
    this.noiseSource.start();

    // Mevcut pozisyona gore sesi ayarla
    this.sesGuncelle();
  }

  /** Sesi tamamen durdur ve kaynaklari temizle */
  private sesDurdur() {
    try { this.oscMain?.stop(); } catch { }
    try { this.oscHarmonic?.stop(); } catch { }
    try { this.noiseSource?.stop(); } catch { }
    try { this.audioCtx?.close(); } catch { }
    this.audioCtx = null;
    this.oscMain = null;
    this.oscHarmonic = null;
    this.gainMain = null;
    this.gainHarmonic = null;
    this.noiseGain = null;
    this.noiseSource = null;
    this.masterGain = null;
  }

  /** Kullanici ses ayarlarini canli olarak uygula */
  sesAyarlariGuncelle() {
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this.sesMasterVolume / 100, this.audioCtx?.currentTime || 0, 0.05);
    }
    this.sesGuncelle();
  }

  /** Hareket turune ve yon degisimine gore ses parametrelerini guncelle */
  sesGuncelle() {
    if (!this.sesAktif || !this.audioCtx || !this.aktifNokta) {
      if (this.gainMain) this.gainMain.gain.value = 0;
      if (this.gainHarmonic) this.gainHarmonic.gain.value = 0;
      if (this.noiseGain) this.noiseGain.gain.value = 0;
      return;
    }

    // Oynatma durmussa sessiz
    if (!this.similasyonIntervalId) {
      if (this.gainMain) this.gainMain.gain.value = 0;
      if (this.gainHarmonic) this.gainHarmonic.gain.value = 0;
      if (this.noiseGain) this.noiseGain.gain.value = 0;
      return;
    }

    const hareket = this.hareketTuruAl(this.aktifNokta);
    const devir = this.aktifNokta.devir || 1000;
    const hiz = this.aktifNokta.hiz || 100;
    const t = this.audioCtx.currentTime;

    // Simulasyon hiz faktoru: simHizi=1→0, 10→0.33, 100→0.67, 1000→1.0
    const hizFaktor = Math.min(Math.log10(Math.max(this.simHizi, 1)) / 3, 1);

    // Yon degisim acisi: ardisik yonler arasindaki aci (0=duz, 1=180° donus)
    let yonDegisim = 0;
    if (this.poz > 0 && this.poz + 1 < this.noktalar.length) {
      const onceki = this.noktalar[this.poz - 1];
      const simdi = this.noktalar[this.poz];
      const sonraki = this.noktalar[this.poz + 1];
      const v1x = simdi.x - onceki.x, v1y = simdi.y - onceki.y, v1z = simdi.z - onceki.z;
      const v2x = sonraki.x - simdi.x, v2y = sonraki.y - simdi.y, v2z = sonraki.z - simdi.z;
      const len1 = Math.sqrt(v1x * v1x + v1y * v1y + v1z * v1z);
      const len2 = Math.sqrt(v2x * v2x + v2y * v2y + v2z * v2z);
      if (len1 > 0.001 && len2 > 0.001) {
        const dot = (v1x * v2x + v1y * v2y + v1z * v2z) / (len1 * len2);
        const aci = Math.acos(Math.max(-1, Math.min(1, dot)));
        yonDegisim = aci / Math.PI; // 0-1 arasi normalize
      }
    }

    // Kullanici mix carpanlari (0-1)
    const mKesim = this.sesKesimMix / 100;
    const mHarmonik = this.sesHarmonikMix / 100;
    const mGurultu = this.sesGurultuMix / 100;
    const mServo = this.sesServoMix / 100;
    // Pitch kaydirma: 50=1x, 0=0.5x, 100=2x
    const pitchMul = 0.5 + (this.sesPitch / 100) * 1.5;

    switch (hareket) {
      case 'İşleme':
      case 'ISLEME':
        // Kesim: testere dalga + harmonik + gürültü
        // Frekans: devir bazli (300-1200Hz) + yon degisimi + hiz faktoru
        const kesimFreq = (300 + Math.min(devir / 12000, 1) * 900
          + yonDegisim * 400 + hizFaktor * 200) * pitchMul;
        this.oscMain.frequency.setTargetAtTime(kesimFreq, t, 0.05);
        this.gainMain.gain.setTargetAtTime((0.08 + yonDegisim * 0.04 + hizFaktor * 0.03) * mKesim, t, 0.05);
        this.oscHarmonic.frequency.setTargetAtTime(kesimFreq * 2.02, t, 0.05);
        this.gainHarmonic.gain.setTargetAtTime((0.03 + yonDegisim * 0.02 + hizFaktor * 0.015) * mHarmonik, t, 0.05);
        // Gürültü: ilerleme hizi + yon degisimi + sim hizi
        const noiseVol = (0.02 + Math.min(hiz / 5000, 1) * 0.04
          + yonDegisim * 0.06 + hizFaktor * 0.03) * mGurultu;
        this.noiseGain.gain.setTargetAtTime(noiseVol, t, 0.05);
        break;

      case 'Yaklaşma':
      case 'YAKLASMA':
        // Yaklasma: dusuk tonlu vizilti + yon degisimi + hiz faktoru
        this.oscMain.frequency.setTargetAtTime((180 + yonDegisim * 100 + hizFaktor * 120) * pitchMul, t, 0.05);
        this.gainMain.gain.setTargetAtTime((0.03 + hizFaktor * 0.02) * mKesim, t, 0.05);
        this.oscHarmonic.frequency.setTargetAtTime((360 + yonDegisim * 200 + hizFaktor * 240) * pitchMul, t, 0.05);
        this.gainHarmonic.gain.setTargetAtTime((0.01 + hizFaktor * 0.008) * mHarmonik, t, 0.05);
        this.noiseGain.gain.setTargetAtTime((0.005 + yonDegisim * 0.01 + hizFaktor * 0.01) * mGurultu, t, 0.05);
        break;

      case 'Uzaklaşma':
      case 'UZAKLASMA':
        // Uzaklasma: farkli perde + yon degisimi + hiz faktoru
        this.oscMain.frequency.setTargetAtTime((220 + yonDegisim * 80 + hizFaktor * 100) * pitchMul, t, 0.05);
        this.gainMain.gain.setTargetAtTime((0.025 + hizFaktor * 0.015) * mKesim, t, 0.05);
        this.oscHarmonic.frequency.setTargetAtTime((440 + yonDegisim * 160 + hizFaktor * 200) * pitchMul, t, 0.05);
        this.gainHarmonic.gain.setTargetAtTime((0.008 + hizFaktor * 0.006) * mHarmonik, t, 0.05);
        this.noiseGain.gain.setTargetAtTime((0.003 + yonDegisim * 0.008 + hizFaktor * 0.008) * mGurultu, t, 0.05);
        break;

      case 'Boşta':
      case 'BOSTA':
      default:
        // Bosta: servo viziltisi + hiz faktoru (hizli hareket = daha tiz servo)
        this.oscMain.frequency.setTargetAtTime((80 + hizFaktor * 60) * pitchMul, t, 0.05);
        this.gainMain.gain.setTargetAtTime((0.01 + hizFaktor * 0.008) * mServo, t, 0.05);
        this.oscHarmonic.frequency.setTargetAtTime((160 + hizFaktor * 120) * pitchMul, t, 0.05);
        this.gainHarmonic.gain.setTargetAtTime(hizFaktor * 0.005 * mServo, t, 0.05);
        this.noiseGain.gain.setTargetAtTime(hizFaktor * 0.003 * mServo, t, 0.05);
        break;
    }
  }

  // ─── / Kesim Sesi ─────────────────────────────────────────────────────

  // ─── Talaş Partikül Efekti ──────────────────────────────────────────

  /** Partikül havuzunu olustur ve sahneye ekle */
  private talasBaslat() {
    this.talasPozisyonlar = new Float32Array(this.TALAS_SAYISI * 3);
    this.talasHizlar = new Float32Array(this.TALAS_SAYISI * 3);
    this.talasOmurleri = new Float32Array(this.TALAS_SAYISI);
    this.talasRenkler = new Float32Array(this.TALAS_SAYISI * 3);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.talasPozisyonlar, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(this.talasRenkler, 3));

    this.talasNoktalar = new THREE.Points(geo, new THREE.PointsMaterial({
      size: 2.5,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    }));
    this.talasNoktalar.frustumCulled = false;
    this.talasNoktalar.visible = false;
    this._sahne?.add(this.talasNoktalar);
  }

  // ─── İnterpolasyon Çizgisi ─────────────────────────────────────────────

  /** Takıma kadar olan dinamik çizgiyi oluştur */
  private interpolasyonCizgisiOlustur() {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(6); // 2 nokta x 3 koordinat
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const mat = new THREE.LineBasicMaterial({
      color: 0x00ffff, // Cyan renk (aktif segment)
      linewidth: 2,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });

    this.interpolasyonCizgisi = new THREE.Line(geo, mat);
    this.interpolasyonCizgisi.frustumCulled = false;
    this.interpolasyonCizgisi.renderOrder = 1000;
    this.interpolasyonCizgisi.visible = false;
    this._sahne?.add(this.interpolasyonCizgisi);
  }

  /** İnterpolasyon çizgisini güncelle (başlangıç noktasından takım pozisyonuna) */
  private interpolasyonCizgisiGuncelle(baslangic: THREE.Vector3, bitis: THREE.Vector3, renk?: THREE.Color) {
    if (!this.interpolasyonCizgisi) return;

    const posAttr = this.interpolasyonCizgisi.geometry.attributes.position as THREE.BufferAttribute;
    posAttr.setXYZ(0, baslangic.x, baslangic.y, baslangic.z);
    posAttr.setXYZ(1, bitis.x, bitis.y, bitis.z);
    posAttr.needsUpdate = true;

    // Hareket türüne göre renk uygula (color is a uniform - no material recompile needed)
    if (renk) {
      (this.interpolasyonCizgisi.material as THREE.LineBasicMaterial).color.copy(renk);
    }

    this.interpolasyonCizgisi.visible = true;
  }

  /** İnterpolasyon çizgisini gizle */
  private interpolasyonCizgisiGizle() {
    if (this.interpolasyonCizgisi) {
      this.interpolasyonCizgisi.visible = false;
    }
  }

  /** Her frame: mevcut parcaciklari guncelle + yenilerini dogur */
  private talasGuncelle() {
    if (!this.talasNoktalar) return;
    this.talasNoktalar.visible = this.talasAktif;
    if (!this.talasAktif || !this.aktifNokta) return;

    const hareket = this.hareketTuruAl(this.aktifNokta);
    const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME';

    // Mevcut parcaciklari guncelle
    for (let i = 0; i < this.TALAS_SAYISI; i++) {
      if (this.talasOmurleri[i] > 0) {
        this.talasOmurleri[i] -= 0.06;
        const idx = i * 3;
        this.talasPozisyonlar[idx] += this.talasHizlar[idx];
        this.talasPozisyonlar[idx + 1] += this.talasHizlar[idx + 1];
        this.talasPozisyonlar[idx + 2] += this.talasHizlar[idx + 2];
        // Yercekim
        this.talasHizlar[idx + 2] -= 0.015;
        // Renk solma: sicak turuncu/sari → koyu kirmizi
        const fade = Math.max(this.talasOmurleri[i], 0);
        this.talasRenkler[idx] = fade;                    // R
        this.talasRenkler[idx + 1] = 0.6 * fade * fade;  // G (hizli solar)
        this.talasRenkler[idx + 2] = 0.05 * fade;        // B
      }
    }

    // Isleme sirasinda yeni parcacik dogur
    if (isIsleme && this.similasyonIntervalId) {
      const takimCap = this.aktifNokta.takim?.cap || 10;
      const carpan = this.talasSiddetCarpan / 50; // 0=yok, 1=normal, 2=maks
      const velScale = takimCap * 0.04 * carpan;
      const spawnCount = Math.round((3 + Math.floor(Math.random() * 5)) * carpan);

      // Takım pozisyonu: interpolasyon modunda interpolasyonPoz kullan
      const takimPozX = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.x : this.aktifNokta.x;
      const takimPozY = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.y : this.aktifNokta.y;
      const takimPozZ = (this.cizgiUzerindeIlerle && this.interpolasyonBasladi) ? this.interpolasyonPoz.z : this.aktifNokta.z;

      if (this.talasGercekFizik) {
        // ═══ GERCEKCI FIZIK MODU ═══
        // Takim hareket yonune ve donusune gore talas firlatma

        // 1. Ilerleme yonu (feed direction): onceki noktadan aktif noktaya
        let feedX = 0, feedY = 0, feedZ = 0;
        if (this.oncekiNokta) {
          feedX = this.aktifNokta.x - this.oncekiNokta.x;
          feedY = this.aktifNokta.y - this.oncekiNokta.y;
          feedZ = this.aktifNokta.z - this.oncekiNokta.z;
        } else if (this.poz + 1 < this.noktalar.length) {
          const sonraki = this.noktalar[this.poz + 1];
          feedX = sonraki.x - this.aktifNokta.x;
          feedY = sonraki.y - this.aktifNokta.y;
          feedZ = sonraki.z - this.aktifNokta.z;
        }
        // Normalize feed
        const feedLen = Math.sqrt(feedX * feedX + feedY * feedY + feedZ * feedZ) || 1;
        feedX /= feedLen; feedY /= feedLen; feedZ /= feedLen;

        // 2. Takim ekseni (tool axis): takimYonu veya default Z+
        let axisX = 0, axisY = 0, axisZ = 1;
        if (this.aktifNokta.takimYonu) {
          axisX = this.aktifNokta.takimYonu.x || 0;
          axisY = this.aktifNokta.takimYonu.y || 0;
          axisZ = this.aktifNokta.takimYonu.z || 1;
          const axLen = Math.sqrt(axisX * axisX + axisY * axisY + axisZ * axisZ) || 1;
          axisX /= axLen; axisY /= axLen; axisZ /= axLen;
        }

        // 3. Tegetsel yon (tangent): feed x axis (cross product)
        let tangentX = feedY * axisZ - feedZ * axisY;
        let tangentY = feedZ * axisX - feedX * axisZ;
        let tangentZ = feedX * axisY - feedY * axisX;
        const tanLen = Math.sqrt(tangentX * tangentX + tangentY * tangentY + tangentZ * tangentZ) || 1;
        tangentX /= tanLen; tangentY /= tanLen; tangentZ /= tanLen;

        // Feed ve axis paralel ise rastgele teget olustur
        if (tanLen < 0.01) {
          const angle = Math.random() * Math.PI * 2;
          tangentX = Math.cos(angle);
          tangentY = Math.sin(angle);
          tangentZ = 0;
        }

        for (let s = 0; s < spawnCount; s++) {
          for (let i = 0; i < this.TALAS_SAYISI; i++) {
            if (this.talasOmurleri[i] <= 0) {
              const idx = i * 3;

              // Kesici kenar uzerinde rastgele aci (takim cevresi)
              const kenarAcisi = Math.random() * Math.PI * 2;
              const kenarR = takimCap * 0.5 * (0.7 + Math.random() * 0.3);

              // Kesici kenar pozisyonu (takim ekseni etrafinda daire)
              let perp1X = tangentX, perp1Y = tangentY, perp1Z = tangentZ;
              let perp2X = axisY * tangentZ - axisZ * tangentY;
              let perp2Y = axisZ * tangentX - axisX * tangentZ;
              let perp2Z = axisX * tangentY - axisY * tangentX;
              const p2Len = Math.sqrt(perp2X * perp2X + perp2Y * perp2Y + perp2Z * perp2Z) || 1;
              perp2X /= p2Len; perp2Y /= p2Len; perp2Z /= p2Len;

              const cosA = Math.cos(kenarAcisi), sinA = Math.sin(kenarAcisi);
              const offsetX = (perp1X * cosA + perp2X * sinA) * kenarR;
              const offsetY = (perp1Y * cosA + perp2Y * sinA) * kenarR;
              const offsetZ = (perp1Z * cosA + perp2Z * sinA) * kenarR;

              // Talas dogum pozisyonu (interpolasyon modunda takimPoz kullan)
              this.talasPozisyonlar[idx] = takimPozX + offsetX;
              this.talasPozisyonlar[idx + 1] = takimPozY + offsetY;
              this.talasPozisyonlar[idx + 2] = takimPozZ + offsetZ;

              // GERCEKCI HIZ VEKTORU:
              // 1. Tegetsel (donus yonunde) %60
              // 2. Ilerleme tersine (geri itme) %25
              // 3. Takim ekseni boyunca (helis) %15
              const randFactor = 0.7 + Math.random() * 0.6;
              const vTangent = velScale * 1.2 * randFactor;
              const vFeedBack = velScale * 0.5 * randFactor;
              const vAxial = velScale * 0.4 * randFactor;

              const radX = offsetX / kenarR, radY = offsetY / kenarR, radZ = offsetZ / kenarR;
              // Donus yonunde tegetsel (saat yonu = -cross(axis, radial))
              const rotTangentX = -(axisY * radZ - axisZ * radY);
              const rotTangentY = -(axisZ * radX - axisX * radZ);
              const rotTangentZ = -(axisX * radY - axisY * radX);

              this.talasHizlar[idx] = rotTangentX * vTangent - feedX * vFeedBack + axisX * vAxial + (Math.random() - 0.5) * velScale * 0.3;
              this.talasHizlar[idx + 1] = rotTangentY * vTangent - feedY * vFeedBack + axisY * vAxial + (Math.random() - 0.5) * velScale * 0.3;
              this.talasHizlar[idx + 2] = rotTangentZ * vTangent - feedZ * vFeedBack + axisZ * vAxial + (Math.random() - 0.5) * velScale * 0.3;

              this.talasOmurleri[i] = 0.4 + Math.random() * 0.6;
              this.talasRenkler[idx] = 1.0;
              this.talasRenkler[idx + 1] = 0.6 + Math.random() * 0.4;
              this.talasRenkler[idx + 2] = 0.05;
              break;
            }
          }
        }
      } else {
        // ═══ BASIT RASTGELE MOD ═══
        for (let s = 0; s < spawnCount; s++) {
          for (let i = 0; i < this.TALAS_SAYISI; i++) {
            if (this.talasOmurleri[i] <= 0) {
              const idx = i * 3;
              // Takim ucunda dogur (interpolasyon modunda takimPoz kullan)
              this.talasPozisyonlar[idx] = takimPozX + (Math.random() - 0.5) * takimCap * 0.3;
              this.talasPozisyonlar[idx + 1] = takimPozY + (Math.random() - 0.5) * takimCap * 0.3;
              this.talasPozisyonlar[idx + 2] = takimPozZ + (Math.random() - 0.5) * takimCap * 0.3;
              // Rastgele yon + hafif yukari sapma
              this.talasHizlar[idx] = (Math.random() - 0.5) * velScale;
              this.talasHizlar[idx + 1] = (Math.random() - 0.5) * velScale;
              this.talasHizlar[idx + 2] = Math.random() * velScale * 0.8;
              this.talasOmurleri[i] = 0.4 + Math.random() * 0.6;
              this.talasRenkler[idx] = 1.0;
              this.talasRenkler[idx + 1] = 0.6 + Math.random() * 0.4;
              this.talasRenkler[idx + 2] = 0.05;
              break;
            }
          }
        }
      }
    }

    // Buffer guncelle
    (this.talasNoktalar.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.talasNoktalar.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Partikül sistemini temizle */
  private talasDurdur() {
    if (this.talasNoktalar) {
      this.talasNoktalar.geometry?.dispose();
      (this.talasNoktalar.material as THREE.PointsMaterial)?.dispose();
      this.talasNoktalar.parent?.remove(this.talasNoktalar);
      this.talasNoktalar = null;
    }
    this.talasPozisyonlar = null;
    this.talasHizlar = null;
    this.talasOmurleri = null;
    this.talasRenkler = null;
  }

  // ─── / Talaş Partikül ───────────────────────────────────────────────

  // ─── Titreşim Efekti ────────────────────────────────────────────────

  /** Kesim sirasinda kameraya yonlu titresim uygula (gercekci CNC chatter) */
  private titresimGuncelle() {
    const kamera = this._sahne?.anaGosterim?.aktifKamera;
    if (!kamera) return;

    // Onceki offset'i geri al
    if (this.titresimOffset.lengthSq() > 0) {
      kamera.position.sub(this.titresimOffset);
      this.titresimOffset.set(0, 0, 0);
    }

    if (!this.titresimAktif || !this.similasyonIntervalId || !this.aktifNokta) return;

    const hareket = this.hareketTuruAl(this.aktifNokta);
    const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME';
    if (!isIsleme) return;

    // Siddet: ilerleme hizi + devir bazli
    const hiz = this.aktifNokta.hiz || 100;
    const devir = this.aktifNokta.devir || 1000;
    const bazSiddet = 0.8 + (hiz / 3000) * 2.5 + (devir / 15000) * 1.5;
    const siddet = bazSiddet * (this.titresimSiddetCarpan / 50);

    // Zaman sayaci (chatter frekansi icin)
    this.titresimZaman += 0.3 + (devir / 10000) * 0.5;

    // ─── Kesim yonu ve dik eksen hesapla ───
    // Kesim yonu: onceki noktadan simdikine
    let kesimX = 0, kesimY = 0, kesimZ = 0;
    if (this.poz > 0) {
      const onceki = this.noktalar[this.poz - 1];
      const simdi = this.noktalar[this.poz];
      kesimX = simdi.x - onceki.x;
      kesimY = simdi.y - onceki.y;
      kesimZ = simdi.z - onceki.z;
      const len = Math.sqrt(kesimX * kesimX + kesimY * kesimY + kesimZ * kesimZ);
      if (len > 0.0001) { kesimX /= len; kesimY /= len; kesimZ /= len; }
      else { kesimX = 1; kesimY = 0; kesimZ = 0; }
    } else {
      kesimX = 1; kesimY = 0; kesimZ = 0;
    }

    // Takim ekseni (genelde Z yukari)
    const takimYonu = this.aktifNokta.takimYonu;
    let tX = takimYonu?.x ?? 0, tY = takimYonu?.y ?? 0, tZ = takimYonu?.z ?? 1;
    const tLen = Math.sqrt(tX * tX + tY * tY + tZ * tZ);
    if (tLen > 0.0001) { tX /= tLen; tY /= tLen; tZ /= tLen; }

    // Radyal yon: kesim yonu x takim ekseni (cross product) → kesim kuvveti yonu
    let rX = kesimY * tZ - kesimZ * tY;
    let rY = kesimZ * tX - kesimX * tZ;
    let rZ = kesimX * tY - kesimY * tX;
    const rLen = Math.sqrt(rX * rX + rY * rY + rZ * rZ);
    if (rLen > 0.0001) { rX /= rLen; rY /= rLen; rZ /= rLen; }
    else { rX = 0; rY = 1; rZ = 0; } // fallback

    // ─── Gercekci titresim bilesenleri ───
    // Ana chatter: sinusoidal (radyal yonde, kesim kuvvetine dik)
    const chatterFreq1 = 7.3; // ana chatter frekansi
    const chatterFreq2 = 13.1; // harmonik
    const chatter = Math.sin(this.titresimZaman * chatterFreq1) * 0.7
                   + Math.sin(this.titresimZaman * chatterFreq2) * 0.3;

    // Radyal bilesen: en buyuk - kesim kuvveti yonunde (chatter + noise)
    const radyal = chatter * siddet * 0.7 + (Math.random() - 0.5) * siddet * 0.3;

    // Aksiyal bilesen: takim ekseni boyunca - daha kucuk
    const aksiyal = Math.sin(this.titresimZaman * 5.7) * siddet * 0.15
                  + (Math.random() - 0.5) * siddet * 0.1;

    // Tanjansiyel bilesen: kesim yonu boyunca - en kucuk
    const tanjansiyel = Math.sin(this.titresimZaman * 9.4) * siddet * 0.1
                      + (Math.random() - 0.5) * siddet * 0.05;

    // Toplam offset = radyal * radyalYon + aksiyal * takimYonu + tanjansiyel * kesimYonu
    this.titresimOffset.set(
      rX * radyal + tX * aksiyal + kesimX * tanjansiyel,
      rY * radyal + tY * aksiyal + kesimY * tanjansiyel,
      rZ * radyal + tZ * aksiyal + kesimZ * tanjansiyel
    );
    kamera.position.add(this.titresimOffset);
  }

  /** Titreşim offset'ini sifirla (temizlik) */
  private titresimTemizle() {
    const kamera = this._sahne?.anaGosterim?.aktifKamera;
    if (kamera && this.titresimOffset.lengthSq() > 0) {
      kamera.position.sub(this.titresimOffset);
    }
    this.titresimOffset.set(0, 0, 0);
  }

  // ─── / Titreşim ─────────────────────────────────────────────────────

  // ─── Çarpışma Kontrolü ──────────────────────────────────────────────

  /** Toggle butonundan çağrılır */
  tiklandiCarpismaToggle() {
    this.carpismaKontrolAktif = !this.carpismaKontrolAktif;
    this.carpismaSayisi = 0;
    this.carpismaFlash = 0;
    this.carpismaMeshCache = null;
    // Kapatınca marker'ları ve listeyi temizle
    if (!this.carpismaKontrolAktif) {
      this.carpismaNoktalar = [];
      this.carpismaMarkerTemizle();
    }
    this.CD.detectChanges();
  }

  /** Çarpışma listesi popup toggle */
  tiklandiCarpismaListe() {
    this.carpismaListeAcik = !this.carpismaListeAcik;
    this.CD.detectChanges();
  }

  /** Listeden çarpışma noktasına tıklandığında kamerayı oraya uçur */
  tiklandiCarpismaNoktasi(nokta: { x: number, y: number, z: number, pozIdx: number }) {
    // Pozisyonu o noktaya getir
    this.poz = nokta.pozIdx;
    this.pozisyonDegisti();

    // Kamerayı o noktaya uçur
    const gosterim = this._sahne?.anaGosterim;
    const kamera = gosterim?.aktifKamera;
    const kontrol = gosterim?.aktifKontrol;
    if (kamera && kontrol) {
      const hedef = new THREE.Vector3(nokta.x, nokta.y, nokta.z);
      const cap = this.aktifNokta?.takim?.cap || 20;
      // Kamerayı hedefin biraz yukarısından baktır
      const kameraPoz = hedef.clone().add(new THREE.Vector3(cap * 3, cap * 3, cap * 5));
      kamera.position.copy(kameraPoz);
      kontrol.target.copy(hedef);
      kontrol.update();
      gosterim.ciz();
    }

    // Popup'ı kapat
    this.carpismaListeAcik = false;
    this.CD.detectChanges();
  }

  /** Sahne mesh'lerini topla (simülasyon, takım, partikül hariç) - 2sn cache */
  private carpismaMeshleriAl(): THREE.Mesh[] {
    const now = Date.now();
    if (this.carpismaMeshCache && now - this.carpismaCacheZaman < 2000) {
      return this.carpismaMeshCache;
    }
    const meshler: THREE.Mesh[] = [];
    const hariTut = new Set<THREE.Object3D>();
    hariTut.add(this.similasyonCizgisi);
    if (this.talasNoktalar) hariTut.add(this.talasNoktalar);
    if (this.yonOku) hariTut.add(this.yonOku);
    const aktifTakim = this.aktifTakimAl();
    if (aktifTakim) hariTut.add(aktifTakim);

    this._sahne?.traverse((child: any) => {
      if (child instanceof THREE.Mesh && child.visible) {
        let excluded = false;
        let p = child as THREE.Object3D;
        while (p) {
          if (hariTut.has(p)) { excluded = true; break; }
          p = p.parent;
        }
        if (!excluded) meshler.push(child);
      }
    });

    this.carpismaMeshCache = meshler;
    this.carpismaCacheZaman = now;
    return meshler;
  }

  /** Çarpışma dispatcher: GPU varsa GPU, yoksa CPU fallback */
  private carpismaKontrol() {
    if (!this.carpismaKontrolAktif || !this.aktifNokta) return;

    const renderer = this._sahne?.anaGosterim?.sunucu as THREE.WebGLRenderer;
    const carpismaBulundu = renderer
      ? this.carpismaKontrolGPU(renderer)
      : this.carpismaKontrolCPU();

    if (carpismaBulundu && this.carpismaFlash <= 0) {
      this.carpismaSayisi++;
      this.carpismaFlash = 10;
      this.carpismaSesiCal();
      // Çarpışma noktasını kaydet ve marker ekle
      this.carpismaNoktaEkle();
    }
    if (this.carpismaFlash > 0) this.carpismaFlash--;
  }

  /** Çarpışma noktasını listeye ekle ve 3D marker oluştur */
  private carpismaNoktaEkle() {
    if (!this.aktifNokta) return;
    // Max limit kontrolü
    if (this.carpismaNoktalar.length >= this.CARPISMA_MARKER_MAX) {
      // En eski noktayı sil
      this.carpismaNoktalar.shift();
    }
    // Yeni nokta ekle
    const simdi = new Date();
    this.carpismaNoktalar.push({
      x: this.aktifNokta.x,
      y: this.aktifNokta.y,
      z: this.aktifNokta.z,
      pozIdx: this.poz,
      zaman: simdi.toLocaleTimeString('tr-TR')
    });
    // 3D marker oluştur
    this.carpismaMarkerOlustur(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
  }

  /** Tüm simülasyonu tarayıp çarpışma noktalarını bulan metot */
  carpismaTaramaYapiliyor: boolean = false;
  carpismaTaramaIlerleme: number = 0;
  private taramaAnimId: number = 0;
  private taramaIptal: boolean = false;
  private gpuDevice: any = null;

  /** Taramayı iptal et */
  taramaIptalEt() {
    if (this.taramaAnimId) {
      cancelAnimationFrame(this.taramaAnimId);
      this.taramaAnimId = 0;
    }
    this.taramaIptal = true;
    this.carpismaTaramaYapiliyor = false;
    this.CD.detectChanges();
  }

  /** WebGPU cihazını al veya oluştur */
  private async getGPUDevice(): Promise<any> {
    if (this.gpuDevice) return this.gpuDevice;
    const nav = navigator as any;
    if (!nav.gpu) return null;
    try {
      const adapter = await nav.gpu.requestAdapter();
      if (!adapter) return null;
      this.gpuDevice = await adapter.requestDevice();
      return this.gpuDevice;
    } catch {
      return null;
    }
  }

  /**
   * WebGPU Hızlandırmalı Çarpışma Tarama
   * GPU'da paralel bbox filtresi → CPU'da sadece adaylar için raycasting
   */
  async tumCarpismalariTara() {
    if (!this.noktalar?.length) return;
    if (this.carpismaTaramaYapiliyor) return;

    const baslangic = performance.now();

    // Temizle
    this.carpismaNoktalar = [];
    this.carpismaMarkerTemizle();
    this.carpismaSayisi = 0;
    this.carpismaTaramaYapiliyor = true;
    this.carpismaTaramaIlerleme = 0;
    this.taramaIptal = false;
    this.CD.detectChanges();

    // Mesh'leri al
    const tumMeshler = this.carpismaMeshleriAl();
    const meshler = tumMeshler?.filter(m => m.type === 'Mesh') || [];

    if (!meshler.length) {
      this.taramaTamamla(0, 0, 0);
      return;
    }

    // Mesh bilgilerini hazırla
    const meshInfos: { mesh: THREE.Mesh, worldBox: THREE.Box3, genisBox: THREE.Box3, matArr: THREE.Material[], orijSides: THREE.Side[] }[] = [];
    const bboxData: number[] = []; // [minX, minY, minZ, maxX, maxY, maxZ, ...] per mesh

    // İlk noktadan takım çapını al (varsayılan genişletme için)
    const defaultCap = this.noktalar[0]?.takim?.cap || 20;
    const expansion = defaultCap * 5; // Çok geniş tut - %100 güvenli filtre

    for (const mesh of meshler) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      const worldBox = new THREE.Box3().copy(mesh.geometry.boundingBox!).applyMatrix4(mesh.matrixWorld);
      const genisBox = worldBox.clone().expandByScalar(expansion);
      const matArr = Array.isArray(mesh.material) ? mesh.material as THREE.Material[] : [mesh.material as THREE.Material];
      const orijSides = matArr.map(m => m.side);
      meshInfos.push({ mesh, worldBox, genisBox, matArr, orijSides });
      bboxData.push(genisBox.min.x, genisBox.min.y, genisBox.min.z, genisBox.max.x, genisBox.max.y, genisBox.max.z);
    }

    const toplam = this.noktalar.length;

    // WebGPU dene
    const device = await this.getGPUDevice();

    if (device) {
      await this.taramaWebGPU(device, meshInfos, bboxData, baslangic);
    } else {
      this.taramaCPU(meshInfos, baslangic);
    }
  }

  /** WebGPU ile TAM GPU tarama - Gerçek nokta-üçgen mesafe hesabı */
  private async taramaWebGPU(device: any, meshInfos: any[], _bboxData: number[], baslangic: number) {
    const noktalar = this.noktalar;
    const toplam = noktalar.length;

    // Tüm mesh üçgenlerini topla (world space)
    const triangles: number[] = [];
    for (const info of meshInfos) {
      const mesh = info.mesh as THREE.Mesh;
      const geo = mesh.geometry;
      const posAttr = geo.attributes.position;
      const idx = geo.index;
      const mat = mesh.matrixWorld;
      const v = new THREE.Vector3();

      if (idx) {
        for (let i = 0; i < idx.count; i += 3) {
          for (let j = 0; j < 3; j++) {
            const vi = idx.getX(i + j);
            v.set(posAttr.getX(vi), posAttr.getY(vi), posAttr.getZ(vi)).applyMatrix4(mat);
            triangles.push(v.x, v.y, v.z);
          }
        }
      } else {
        for (let i = 0; i < posAttr.count; i++) {
          v.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i)).applyMatrix4(mat);
          triangles.push(v.x, v.y, v.z);
        }
      }
    }

    const triCount = Math.floor(triangles.length / 9);

    // Nokta verilerini hazırla
    const noktaData = new Float32Array(toplam * 8);
    for (let i = 0; i < toplam; i++) {
      const n = noktalar[i];
      const offset = i * 8;
      const cap = n.takim?.cap || 20;
      noktaData[offset] = n.x;
      noktaData[offset + 1] = n.y;
      noktaData[offset + 2] = n.z;
      noktaData[offset + 3] = cap;
      const ty = n.takimYonu;
      if (ty) {
        const len = Math.sqrt(ty.x * ty.x + ty.y * ty.y + ty.z * ty.z) || 1;
        noktaData[offset + 4] = ty.x / len;
        noktaData[offset + 5] = ty.y / len;
        noktaData[offset + 6] = ty.z / len;
      } else {
        noktaData[offset + 4] = 0;
        noktaData[offset + 5] = 0;
        noktaData[offset + 6] = 1;
      }
      noktaData[offset + 7] = cap / 2; // yarıçap
    }

    const triData = new Float32Array(triangles);

    // GPU Buffers
    const noktaBuffer = device.createBuffer({
      size: noktaData.byteLength,
      usage: 0x0080 | 0x0008,
      mappedAtCreation: true,
    });
    new Float32Array(noktaBuffer.getMappedRange()).set(noktaData);
    noktaBuffer.unmap();

    const triBuffer = device.createBuffer({
      size: triData.byteLength,
      usage: 0x0080 | 0x0008,
      mappedAtCreation: true,
    });
    new Float32Array(triBuffer.getMappedRange()).set(triData);
    triBuffer.unmap();

    const resultBuffer = device.createBuffer({
      size: toplam * 4,
      usage: 0x0080 | 0x0004,
    });

    const readBuffer = device.createBuffer({
      size: toplam * 4,
      usage: 0x0001 | 0x0008,
    });

    // GPU Compute Shader - GERÇEK SİLİNDİR-ÜÇGEN KESİŞİM KONTROLÜ
    // Takım ekseni (segment) ile her üçgen arasındaki minimum mesafe hesaplanır
    // Mesafe < yarıçap ise silindir üçgeni kesiyor demektir
    const shaderCode = `
      struct Nokta {
        x: f32, y: f32, z: f32, cap: f32,
        ex: f32, ey: f32, ez: f32, radius: f32
      }

      @group(0) @binding(0) var<storage, read> noktalar: array<Nokta>;
      @group(0) @binding(1) var<storage, read> tris: array<f32>;
      @group(0) @binding(2) var<storage, read_write> result: array<u32>;

      const TRI_COUNT: u32 = ${triCount}u;

      // İki segment arasındaki minimum mesafe
      fn segmentSegmentDist(a1: vec3f, a2: vec3f, b1: vec3f, b2: vec3f) -> f32 {
        let d1 = a2 - a1;
        let d2 = b2 - b1;
        let r = a1 - b1;
        let a = dot(d1, d1);
        let e = dot(d2, d2);
        let f = dot(d2, r);
        var s: f32 = 0.0;
        var t: f32 = 0.0;

        if (a < 0.0001 && e < 0.0001) { return length(a1 - b1); }
        if (a < 0.0001) { t = clamp(f / e, 0.0, 1.0); }
        else {
          let c = dot(d1, r);
          if (e < 0.0001) { s = clamp(-c / a, 0.0, 1.0); }
          else {
            let b = dot(d1, d2);
            let denom = a * e - b * b;
            if (abs(denom) > 0.0001) { s = clamp((b * f - c * e) / denom, 0.0, 1.0); }
            t = (b * s + f) / e;
            if (t < 0.0) { t = 0.0; s = clamp(-c / a, 0.0, 1.0); }
            else if (t > 1.0) { t = 1.0; s = clamp((b - c) / a, 0.0, 1.0); }
          }
        }
        return length((a1 + d1 * s) - (b1 + d2 * t));
      }

      // Nokta ile segment arasındaki mesafe
      fn pointSegmentDist(p: vec3f, a: vec3f, b: vec3f) -> f32 {
        let ab = b - a;
        let t = clamp(dot(p - a, ab) / dot(ab, ab), 0.0, 1.0);
        return length(p - (a + ab * t));
      }

      // Nokta-üçgen mesafesi
      fn pointTriDist(p: vec3f, a: vec3f, b: vec3f, c: vec3f) -> f32 {
        let ab = b - a; let ac = c - a; let ap = p - a;
        let d1 = dot(ab, ap); let d2 = dot(ac, ap);
        if (d1 <= 0.0 && d2 <= 0.0) { return length(p - a); }
        let bp = p - b; let d3 = dot(ab, bp); let d4 = dot(ac, bp);
        if (d3 >= 0.0 && d4 <= d3) { return length(p - b); }
        let vc = d1 * d4 - d3 * d2;
        if (vc <= 0.0 && d1 >= 0.0 && d3 <= 0.0) { return length(p - (a + ab * (d1 / (d1 - d3)))); }
        let cp = p - c; let d5 = dot(ab, cp); let d6 = dot(ac, cp);
        if (d6 >= 0.0 && d5 <= d6) { return length(p - c); }
        let vb = d5 * d2 - d1 * d6;
        if (vb <= 0.0 && d2 >= 0.0 && d6 <= 0.0) { return length(p - (a + ac * (d2 / (d2 - d6)))); }
        let va = d3 * d6 - d5 * d4;
        if (va <= 0.0 && (d4 - d3) >= 0.0 && (d5 - d6) >= 0.0) {
          let w = (d4 - d3) / ((d4 - d3) + (d5 - d6));
          return length(p - (b + (c - b) * w));
        }
        let denom = 1.0 / (va + vb + vc);
        return length(p - (a + ab * (vb * denom) + ac * (vc * denom)));
      }

      // Segment ile üçgen arasındaki minimum mesafe (tam geometrik kontrol)
      fn segmentTriDist(s1: vec3f, s2: vec3f, v0: vec3f, v1: vec3f, v2: vec3f) -> f32 {
        var d: f32 = 1e10;
        // Segment uçları - üçgen
        d = min(d, pointTriDist(s1, v0, v1, v2));
        d = min(d, pointTriDist(s2, v0, v1, v2));
        // Üçgen köşeleri - segment
        d = min(d, pointSegmentDist(v0, s1, s2));
        d = min(d, pointSegmentDist(v1, s1, s2));
        d = min(d, pointSegmentDist(v2, s1, s2));
        // Segment - üçgen kenarları
        d = min(d, segmentSegmentDist(s1, s2, v0, v1));
        d = min(d, segmentSegmentDist(s1, s2, v1, v2));
        d = min(d, segmentSegmentDist(s1, s2, v2, v0));
        return d;
      }

      @compute @workgroup_size(64)
      fn main(@builtin(global_invocation_id) gid: vec3u) {
        let idx = gid.x;
        if (idx >= arrayLength(&noktalar)) { return; }

        let n = noktalar[idx];
        let toolTip = vec3f(n.x, n.y, n.z);  // Takım ucu
        let axis = vec3f(n.ex, n.ey, n.ez);   // Takım ekseni (normalize)
        let toolLen = n.cap * 4.0;            // Silindir uzunluğu (4 çap)
        let radius = n.radius;                // Silindir yarıçapı

        // Takım silindiri: uçtan (toolTip) eksene doğru (toolLen) uzunluğunda
        let toolEnd = toolTip + axis * toolLen;

        var collision: u32 = 0u;

        // Her üçgene karşı silindir-üçgen mesafe kontrolü
        for (var i: u32 = 0u; i < TRI_COUNT; i++) {
          let ti = i * 9u;
          let v0 = vec3f(tris[ti], tris[ti + 1u], tris[ti + 2u]);
          let v1 = vec3f(tris[ti + 3u], tris[ti + 4u], tris[ti + 5u]);
          let v2 = vec3f(tris[ti + 6u], tris[ti + 7u], tris[ti + 8u]);

          // Segment (takım ekseni) ile üçgen arasındaki minimum mesafe
          let dist = segmentTriDist(toolTip, toolEnd, v0, v1, v2);

          // Mesafe < yarıçap ise silindir üçgeni kesiyor
          if (dist < radius) {
            collision = 1u;
            break;
          }
        }

        result[idx] = collision;
      }
    `;

    const shaderModule = device.createShaderModule({ code: shaderCode });

    const pipeline = device.createComputePipeline({
      layout: 'auto',
      compute: { module: shaderModule, entryPoint: 'main' }
    });

    const bindGroup = device.createBindGroup({
      layout: pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: noktaBuffer } },
        { binding: 1, resource: { buffer: triBuffer } },
        { binding: 2, resource: { buffer: resultBuffer } },
      ]
    });

    this.carpismaTaramaIlerleme = 30;
    this.CD.detectChanges();

    const workgroups = Math.ceil(toplam / 64);
    const commandEncoder = device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.dispatchWorkgroups(workgroups);
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(resultBuffer, 0, readBuffer, 0, toplam * 4);
    device.queue.submit([commandEncoder.finish()]);

    await readBuffer.mapAsync(1);
    const resultArray = new Uint32Array(readBuffer.getMappedRange());

    const hits: { x: number, y: number, z: number, idx: number }[] = [];
    for (let i = 0; i < toplam; i++) {
      if (resultArray[i] === 1) {
        const n = noktalar[i];
        hits.push({ x: n.x, y: n.y, z: n.z, idx: i });
      }
    }
    readBuffer.unmap();

    const gpuSure = (performance.now() - baslangic).toFixed(0);

    // Cleanup
    noktaBuffer.destroy();
    triBuffer.destroy();
    resultBuffer.destroy();
    readBuffer.destroy();

    this.taramaSonuclandir(hits, baslangic, meshInfos.length);
  }

  /** CPU'da Three.js Raycaster ile kesin çarpışma kontrolü (carpismaKontrolCPU ile aynı mantık) */
  private async taramaAdaylarCPU(adayIndices: number[], meshInfos: any[], baslangic: number) {
    const hits: { x: number, y: number, z: number, idx: number }[] = [];
    const raycaster = new THREE.Raycaster();
    raycaster.near = 0;
    const testYon = new THREE.Vector3(1, 0, 0);
    const pos = new THREE.Vector3();
    const eksen = new THREE.Vector3();
    const kontNokta = new THREE.Vector3();
    const up = new THREE.Vector3();
    const r1 = new THREE.Vector3();
    const r2 = new THREE.Vector3();
    const radyalYon = new THREE.Vector3();

    const toplam = adayIndices.length;
    let processed = 0;

    const frame = () => {
      if (this.taramaIptal) return;

      const t0 = performance.now();
      while (processed < toplam && (performance.now() - t0) < 12) {
        const idx = adayIndices[processed++];
        const n = this.noktalar[idx];
        if (!n) continue;

        const takimCap = n.takim?.cap || 20;
        const takimR = takimCap / 2;
        pos.set(n.x, n.y, n.z);

        const ty = n.takimYonu;
        if (ty) {
          eksen.set(ty.x, ty.y, ty.z).normalize();
        } else {
          eksen.set(0, 0, 1);
        }

        let carpiyor = false;

        meshLoop:
        for (const info of meshInfos) {
          const { mesh, genisBox, matArr, orijSides } = info;

          for (let t = 0; t <= 4; t++) {
            kontNokta.copy(pos).addScaledVector(eksen, takimCap * t);
            if (!genisBox.containsPoint(kontNokta)) continue;

            // DoubleSide
            matArr.forEach((m: any) => m.side = THREE.DoubleSide);
            raycaster.set(kontNokta, testYon);
            raycaster.far = Infinity;
            const rayHits = raycaster.intersectObject(mesh, false);
            matArr.forEach((m: any, i: number) => m.side = orijSides[i]);

            if (rayHits.length > 0 && rayHits.length % 2 === 1) {
              carpiyor = true;
              break meshLoop;
            }

            // Radyal
            if (Math.abs(eksen.y) < 0.9) up.set(0, 1, 0); else up.set(1, 0, 0);
            r1.crossVectors(eksen, up).normalize();
            r2.crossVectors(eksen, r1).normalize();

            for (let i = 0; i < 4; i++) {
              const aci = (i / 4) * Math.PI * 2;
              radyalYon.copy(r1).multiplyScalar(Math.cos(aci)).addScaledVector(r2, Math.sin(aci)).normalize();
              raycaster.set(kontNokta, radyalYon);
              raycaster.far = takimR * 1.5;
              if (raycaster.intersectObject(mesh, false).length > 0) {
                carpiyor = true;
                break meshLoop;
              }
            }
          }
        }

        if (carpiyor) {
          hits.push({ x: n.x, y: n.y, z: n.z, idx });
        }
      }

      this.carpismaTaramaIlerleme = 50 + Math.round((processed / toplam) * 50);
      this.CD.detectChanges();

      if (processed < toplam && !this.taramaIptal) {
        this.taramaAnimId = requestAnimationFrame(frame);
      } else if (!this.taramaIptal) {
        this.taramaSonuclandir(hits, baslangic, meshInfos.length);
      }
    };

    this.taramaAnimId = requestAnimationFrame(frame);
  }

  /** CPU-only tarama (WebGPU yoksa fallback) */
  private taramaCPU(meshInfos: any[], baslangic: number) {
    const hits: { x: number, y: number, z: number, idx: number }[] = [];
    const raycaster = new THREE.Raycaster();
    raycaster.near = 0;
    const testYon = new THREE.Vector3(1, 0, 0);
    const pos = new THREE.Vector3();
    const eksen = new THREE.Vector3();
    const kontNokta = new THREE.Vector3();
    const up = new THREE.Vector3();
    const r1 = new THREE.Vector3();
    const r2 = new THREE.Vector3();
    const radyalYon = new THREE.Vector3();

    const toplam = this.noktalar.length;
    let idx = 0;
    const SAMPLE = 3; // Her 3 noktadan 1

    const frame = () => {
      if (this.taramaIptal) return;

      const t0 = performance.now();
      while (idx < toplam && (performance.now() - t0) < 10) {
        const n = this.noktalar[idx];
        idx += SAMPLE;
        if (!n) continue;

        const takimCap = n.takim?.cap || 20;
        const takimR = takimCap / 2;
        pos.set(n.x, n.y, n.z);

        const ty = n.takimYonu;
        if (ty) eksen.set(ty.x, ty.y, ty.z).normalize();
        else eksen.set(0, 0, 1);

        let carpiyor = false;

        meshLoop:
        for (const info of meshInfos) {
          const { mesh, genisBox, matArr, orijSides } = info;

          for (let t = 0; t <= 4; t++) {
            kontNokta.copy(pos).addScaledVector(eksen, takimCap * t);
            if (!genisBox.containsPoint(kontNokta)) continue;

            matArr.forEach((m: any) => m.side = THREE.DoubleSide);
            raycaster.set(kontNokta, testYon);
            raycaster.far = Infinity;
            const rayHits = raycaster.intersectObject(mesh, false);
            matArr.forEach((m: any, i: number) => m.side = orijSides[i]);

            if (rayHits.length > 0 && rayHits.length % 2 === 1) {
              carpiyor = true;
              break meshLoop;
            }

            if (Math.abs(eksen.y) < 0.9) up.set(0, 1, 0); else up.set(1, 0, 0);
            r1.crossVectors(eksen, up).normalize();
            r2.crossVectors(eksen, r1).normalize();

            for (let i = 0; i < 4; i++) {
              const aci = (i / 4) * Math.PI * 2;
              radyalYon.copy(r1).multiplyScalar(Math.cos(aci)).addScaledVector(r2, Math.sin(aci)).normalize();
              raycaster.set(kontNokta, radyalYon);
              raycaster.far = takimR * 1.5;
              if (raycaster.intersectObject(mesh, false).length > 0) {
                carpiyor = true;
                break meshLoop;
              }
            }
          }
        }

        if (carpiyor) hits.push({ x: n.x, y: n.y, z: n.z, idx: idx - SAMPLE });
      }

      this.carpismaTaramaIlerleme = Math.round((idx / toplam) * 100);
      this.CD.detectChanges();

      if (idx < toplam && !this.taramaIptal) {
        this.taramaAnimId = requestAnimationFrame(frame);
      } else if (!this.taramaIptal) {
        this.taramaSonuclandir(hits, baslangic, meshInfos.length);
      }
    };

    this.taramaAnimId = requestAnimationFrame(frame);
  }

  /** Tarama sonuçlarını işle */
  private taramaSonuclandir(hits: { x: number, y: number, z: number, idx: number }[], baslangic: number, meshSayisi: number) {
    // Gruplama - GRID küçültüldü (5mm)
    const GRID = 5;
    const hash = new Map<string, typeof hits[0]>();
    for (const h of hits) {
      const key = `${Math.floor(h.x / GRID)},${Math.floor(h.y / GRID)},${Math.floor(h.z / GRID)}`;
      if (!hash.has(key)) hash.set(key, h);
    }

    const gruplu = Array.from(hash.values());

    for (const p of gruplu) {
      this.carpismaNoktalar.push({ x: p.x, y: p.y, z: p.z, pozIdx: p.idx, zaman: `Poz ${p.idx}` });
    }
    this.carpismaSayisi = gruplu.length;

    const maxM = Math.min(gruplu.length, 50);
    for (let i = 0; i < maxM; i++) {
      this.carpismaMarkerOlustur(gruplu[i].x, gruplu[i].y, gruplu[i].z);
    }

    this.taramaTamamla(this.noktalar.length, meshSayisi, gruplu.length);
  }

  private taramaTamamla(noktaSayisi: number, meshSayisi: number, carpismaSayisi: number) {
    this.carpismaTaramaYapiliyor = false;
    this.carpismaTaramaIlerleme = 100;
    this.CD.detectChanges();

    setTimeout(() => {
      const popup = document.getElementById('carpisma-liste-popup') as HTMLElement;
      popup?.showPopover?.();
    }, 100);
  }

  // Shared marker geometry & materials (performance)
  private markerGeo: THREE.SphereGeometry;
  private markerMat: THREE.MeshBasicMaterial;

  /** 3D uyarı marker'ı oluştur - optimized with shared resources */
  private carpismaMarkerOlustur(x: number, y: number, z: number) {
    if (!this.carpismaMarkerGrup) {
      this.carpismaMarkerGrup = new THREE.Group();
      this.carpismaMarkerGrup.name = 'carpismaMarkerlar';
      this._sahne?.add(this.carpismaMarkerGrup);
    }

    // Lazy init shared resources
    if (!this.markerGeo) {
      this.markerGeo = new THREE.SphereGeometry(1, 8, 8); // unit sphere, will be scaled
      this.markerMat = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.7,
        depthWrite: false,
      });
    }

    const cap = this.aktifNokta?.takim?.cap || 20;
    const scale = cap * 0.4;

    // Single sphere with shared geo/mat
    const marker = new THREE.Mesh(this.markerGeo, this.markerMat);
    marker.position.set(x, y, z);
    marker.scale.setScalar(scale);

    this.carpismaMarkerGrup.add(marker);
  }

  /** Tüm çarpışma marker'larını temizle */
  private carpismaMarkerTemizle() {
    if (this.carpismaMarkerGrup) {
      this.carpismaMarkerGrup.clear(); // Just clear, shared geo/mat kept for reuse
    }
  }

  /** Shared marker resources'ı dispose et (ngOnDestroy'da çağrılır) */
  private markerResourcesTemizle() {
    if (this.markerGeo) { this.markerGeo.dispose(); this.markerGeo = null; }
    if (this.markerMat) { this.markerMat.dispose(); this.markerMat = null; }
  }

  /** GPU çarpışma: render target & kamera oluştur (lazy init) */
  private carpismaGPUBaslat() {
    if (this.carpismaRT) return;
    this.carpismaRT = new THREE.WebGLRenderTarget(this.CARPISMA_RT_BOYUT, this.CARPISMA_RT_BOYUT, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
    });
    this.carpismaKamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1);
    this.carpismaPixelBuffer = new Uint8Array(this.CARPISMA_RT_BOYUT * this.CARPISMA_RT_BOYUT * 4);
    this.carpismaOverrideMat = new THREE.MeshBasicMaterial({ color: 0xff0000, side: THREE.DoubleSide });
  }

  /** GPU çarpışma kontrolü: ortho kamera ile takım shaft hacmini render et,
   *  pixel okuyarak çarpışma tespit et (2 dik görüş açısı).
   *  Kamera 10000 birim uzakta → inside-mesh sorunu yok.
   *  overrideMaterial ile tüm mesh'ler kırmızı → siyah piksel olmayan = çarpışma.
   */
  private carpismaKontrolGPU(renderer: THREE.WebGLRenderer): boolean {
    this.carpismaGPUBaslat();

    const pos = new THREE.Vector3(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
    const takimYonu = this.aktifNokta.takimYonu;
    const takimCap = this.aktifNokta.takim?.cap || 10;
    const takimR = takimCap / 2;

    // Takım ekseni
    const eksen = new THREE.Vector3(
      takimYonu?.x ?? 0, takimYonu?.y ?? 0, takimYonu?.z ?? 1
    ).normalize();

    // İşleme sırasında tip kontrolünü atla
    const hareket = this.hareketTuruAl(this.aktifNokta);
    const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME';

    // Shaft kontrol bölgesi: 4 çap boyunda
    const shaftBoy = takimCap * 4;
    const shaftBaslangic = isIsleme ? takimCap : 0;
    const shaftMerkez = pos.clone().addScaledVector(eksen, shaftBaslangic + shaftBoy / 2);

    // Eksen'e dik 2 radyal yön
    const up = Math.abs(eksen.y) < 0.9
      ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const r1 = new THREE.Vector3().crossVectors(eksen, up).normalize();
    const r2 = new THREE.Vector3().crossVectors(eksen, r1).normalize();

    // Sim nesnelerini gizle (render'da görünmesin)
    const gizlenenler: THREE.Object3D[] = [];
    const gizle = (obj: THREE.Object3D) => {
      if (obj?.visible) { gizlenenler.push(obj); obj.visible = false; }
    };
    gizle(this.similasyonCizgisi);
    if (this.talasNoktalar) gizle(this.talasNoktalar);
    if (this.yonOku) gizle(this.yonOku);
    if (this.hudGrup) gizle(this.hudGrup);
    if (this.lazerIzCizgisi) gizle(this.lazerIzCizgisi);
    if (this.hayaletGrup) gizle(this.hayaletGrup);
    if (this.hayaletCizgi) gizle(this.hayaletCizgi);
    if (this.sonarGrup) gizle(this.sonarGrup);
    if (this.carpismaMarkerGrup) gizle(this.carpismaMarkerGrup);
    const aktifTakim = this.aktifTakimAl();
    if (aktifTakim) gizle(aktifTakim);

    // Renderer durumunu kaydet
    const oncekiRT = renderer.getRenderTarget();
    const oncekiAutoClear = renderer.autoClear;
    const oncekiBackground = this._sahne.background;
    const oncekiOverride = this._sahne.overrideMaterial;

    renderer.autoClear = true;
    this._sahne.background = null; // siyah arka plan → 0 piksel = çarpışma yok
    this._sahne.overrideMaterial = this.carpismaOverrideMat; // tüm mesh'ler kırmızı

    const kamera = this.carpismaKamera;
    const uzaklik = 10000;
    let gpuCarpismaBulundu = false;

    // 2 dik açıdan render (r1 ve r2 yönünden)
    for (let vi = 0; vi < 2 && !gpuCarpismaBulundu; vi++) {
      const radyal = vi === 0 ? r1 : r2;

      // Kamera: shaft merkezinden çok uzakta, radyal yönde
      kamera.position.copy(shaftMerkez).addScaledVector(radyal, uzaklik);
      kamera.up.copy(eksen);
      kamera.lookAt(shaftMerkez);

      // Ortho frustum: shaft boyunu ve çapını kapsa
      kamera.left = -takimR * 1.5;
      kamera.right = takimR * 1.5;
      kamera.top = shaftBoy / 2 + takimR;
      kamera.bottom = -(shaftBoy / 2 + takimR);

      // Near/Far: sadece shaft çapı mesafesindeki objeleri yakala
      kamera.near = uzaklik - takimR * 1.5;
      kamera.far = uzaklik + takimR * 1.5;

      kamera.updateProjectionMatrix();
      kamera.updateMatrixWorld(true);

      // Render to offscreen target
      renderer.setRenderTarget(this.carpismaRT);
      renderer.clear();
      renderer.render(this._sahne, kamera);

      // Pixel oku
      renderer.readRenderTargetPixels(
        this.carpismaRT, 0, 0,
        this.CARPISMA_RT_BOYUT, this.CARPISMA_RT_BOYUT,
        this.carpismaPixelBuffer
      );

      // Non-zero piksel kontrolü (kırmızı = mesh var = çarpışma)
      for (let i = 0; i < this.carpismaPixelBuffer.length; i += 4) {
        if (this.carpismaPixelBuffer[i] > 0 ||
          this.carpismaPixelBuffer[i + 1] > 0 ||
          this.carpismaPixelBuffer[i + 2] > 0) {
          gpuCarpismaBulundu = true;
          break;
        }
      }
    }

    // Renderer durumunu geri yükle
    renderer.setRenderTarget(oncekiRT);
    renderer.autoClear = oncekiAutoClear;
    this._sahne.background = oncekiBackground;
    this._sahne.overrideMaterial = oncekiOverride;

    // Gizlenen nesneleri geri aç
    for (const obj of gizlenenler) obj.visible = true;

    return gpuCarpismaBulundu;
  }

  /** GPU çarpışma kaynakları temizle */
  private carpismaGPUTemizle() {
    if (this.carpismaRT) {
      this.carpismaRT.dispose();
      this.carpismaRT = null;
    }
    if (this.carpismaOverrideMat) {
      this.carpismaOverrideMat.dispose();
      this.carpismaOverrideMat = null;
    }
    this.carpismaKamera = null;
    this.carpismaPixelBuffer = null;
  }

  /** CPU çarpışma fallback: raycaster ile shaft boyunca test */
  private carpismaKontrolCPU(): boolean {

    const pos = new THREE.Vector3(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
    const takimYonu = this.aktifNokta.takimYonu;
    const takimCap = this.aktifNokta.takim?.cap || 10;
    const takimR = takimCap / 2;

    // Takım ekseni
    const eksen = new THREE.Vector3(
      takimYonu?.x ?? 0, takimYonu?.y ?? 0, takimYonu?.z ?? 1
    ).normalize();

    const meshler = this.carpismaMeshleriAl();
    if (!meshler.length) return false;

    // İşleme sırasında tip kontrolünü atla (uç kesim yapıyor, normal)
    const hareket = this.hareketTuruAl(this.aktifNokta);
    const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME';

    // Shaft boyunca kontrol noktaları: tip, +1D, +2D, +3D, +4D
    const kontNokt: THREE.Vector3[] = [];
    const baslangicT = isIsleme ? 1 : 0;
    for (let t = baslangicT; t <= 4; t++) {
      kontNokt.push(pos.clone().add(eksen.clone().multiplyScalar(takimCap * t)));
    }

    // Radyal yönler (yakınlık kontrolü için)
    const up = Math.abs(eksen.y) < 0.9
      ? new THREE.Vector3(0, 1, 0)
      : new THREE.Vector3(1, 0, 0);
    const r1 = new THREE.Vector3().crossVectors(eksen, up).normalize();
    const r2 = new THREE.Vector3().crossVectors(eksen, r1).normalize();
    const radyalYonler: THREE.Vector3[] = [];
    for (let i = 0; i < 4; i++) {
      const aci = (i / 4) * Math.PI * 2;
      radyalYonler.push(
        r1.clone().multiplyScalar(Math.cos(aci))
          .add(r2.clone().multiplyScalar(Math.sin(aci)))
          .normalize()
      );
    }

    const testYon = new THREE.Vector3(1, 0, 0); // inside testi için sabit yön
    let carpismaBulundu = false;

    for (const mesh of meshler) {
      if (carpismaBulundu) break;

      // BoundingBox hesapla (world space)
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      const worldBox = mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld);
      const genisBox = worldBox.clone().expandByScalar(takimR * 2);

      // Materyal(ler)i geçici DoubleSide yap (inside testi arka yüzleri de algılamalı)
      const matArr = Array.isArray(mesh.material)
        ? mesh.material as THREE.Material[]
        : [mesh.material as THREE.Material];
      const orijinalSides = matArr.map(m => m.side);

      for (const nokta of kontNokt) {
        // BBox pre-filter
        if (!genisBox.containsPoint(nokta)) continue;

        // --- 1) "Nokta mesh içinde mi?" testi ---
        // DoubleSide aç: ray hem ön hem arka yüzden geçsin
        matArr.forEach(m => m.side = THREE.DoubleSide);

        this.carpismaRaycaster.set(nokta, testYon);
        this.carpismaRaycaster.far = Infinity;
        this.carpismaRaycaster.near = 0;
        const hits = this.carpismaRaycaster.intersectObject(mesh, false);

        // Orijinal side geri yükle
        matArr.forEach((m, i) => m.side = orijinalSides[i]);

        if (hits.length > 0 && hits.length % 2 === 1) {
          // Tek sayı kesişim → nokta mesh yüzeyinin içinde
          carpismaBulundu = true;
          break;
        }

        // --- 2) Radyal yakınlık kontrolü (mesh dışında ama çok yakın) ---
        for (const yon of radyalYonler) {
          this.carpismaRaycaster.set(nokta, yon);
          this.carpismaRaycaster.far = takimR * 1.5;
          this.carpismaRaycaster.near = 0;
          const prHits = this.carpismaRaycaster.intersectObject(mesh, false);
          if (prHits.length > 0) {
            carpismaBulundu = true;
            break;
          }
        }
        if (carpismaBulundu) break;
      }

      // Orijinal side'ları geri yükle (güvenlik - loop break durumunda)
      matArr.forEach((m, i) => m.side = orijinalSides[i]);
    }

    return carpismaBulundu;
  }

  /** Çarpışma uyarı sesi çal (kısa keskin bip) */
  private carpismaSesiCal() {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 880;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
      setTimeout(() => ctx.close(), 200);
    } catch { }
  }

  /** Çarpışma verilerini sıfırla */
  private carpismaTemizle() {
    this.carpismaKontrolAktif = false;
    this.carpismaSayisi = 0;
    this.carpismaFlash = 0;
    this.carpismaMeshCache = null;
    this.carpismaGPUTemizle();
  }

  // ─── / Çarpışma Kontrolü ────────────────────────────────────────────

  // ─── Cycle Time Analizi (Component'e taşındı) ─────────────────────

  /** Cycle Time component'ini tetikle */
  cycleTimeAnaliziHesapla() {
    if (this.cycleTimeRaporRef) {
      this.cycleTimeRaporRef.analizHesapla();
    }
  }

  // ─── Director Mode ─────────────────────────────────────────────────

  /** Director Mode penceresini aç */
  directorModeAc() {
    import('../fiko-director-mode/fiko-director-mode.component').then(m => {
      const gosterim = this._sahne?.anaGosterim;
      if (!gosterim?.DialogFiko) { console.warn('DialogFiko bulunamadi'); return; }

      // Director Mode penceresini aç
      gosterim.DialogFiko.ac({
        bilesen: m.FikoDirectorModeComponent,
        parametreler: {
          sahne: this._sahne,
          noktalar: this.noktalar,
          poz: this.poz,
          aktifNokta: this.aktifNokta,
          oynatiliyor: this.simOynatiliyor,
          simComponent: this // Canlı güncelleme için simülasyon referansı
        },
        ayarlar: { baslik: 'Director Mode', simgeResim: './simgeler/director-mode.svg' }
      });

      // Araçlar popover'ını kapat (Native Popover API)
      (document.getElementById('sim-araclar-popup') as any)?.hidePopover?.();
    });
  }

  // ─── Sim Motor Dialog ────────────────────────────────────────────────

  /** Simülasyon Motor Ayarları penceresini aç */
  simMotorDialogAc() {
    import('../fiko-sim-motor-dialog/fiko-sim-motor-dialog.component').then(m => {
      const gosterim = this._sahne?.anaGosterim;
      if (!gosterim?.DialogFiko) { console.warn('DialogFiko bulunamadi'); return; }

      gosterim.DialogFiko.ac({
        bilesen: m.FikoSimMotorDialogComponent,
        parametreler: {
          simComponent: this
        },
        ayarlar: { baslik: 'Simülasyon Motoru' }
      });
    });
  }

  gostergeleriAyarla() {
    if (!this.aktifNokta || !this.islemeEkseni) return;
    this.gostergeNoktaAyarla();

    if (this.oncekiNokta && (!this.oncekiNokta.takimYonu.equals(this.aktifNokta.takimYonu) || !this.gostergeYon)) {
      this.gostergeYonAyarla();
      this.gostergeAcilariAl();
      this.gostergeKacmaDegerleriniAl();
    }

    if (this.oncekiNokta)
      if (this.oncekiNokta.takim != this.aktifNokta.takim)
        this.gostergeKacmaDegerleriniAl();


  }

  gostergeNoktaAyarla() {
    if (!this.aktifNokta || !this.islemeEkseni) return;
    this.gostergeNokta = this.aktifNokta.clone();

    this.gostergeNokta.sub(this.islemeEkseni.merkez);
    this.gostergeNokta.applyQuaternion(this._simTmpQ2.copy(this.islemeEkseni.quaternion).invert());

  }

  gostergeYonAyarla() {
    if (!this.aktifNokta || !this.islemeEkseni) return;
    this.gostergeYon = this.aktifNokta.takimYonu.clone();
    this.gostergeYon.transformDirection(this.islemeEkseni.matrixWorld);
  }

  gostergeAcilariAl() {
    const ijk = this._simTmpV1.copy(this.gostergeYon);
    ijk.applyAxisAngle(this._simTmpV2.set(0, 0, 1), THREE.MathUtils.degToRad(180));
    const crsProject = this._simTmpV3.copy(ijk).cross(this._simTmpV2.set(0, 0, 1));
    let ustAciRad = 0;
    if (crsProject.x || crsProject.y) {
      ustAciRad = Math.atan2(crsProject.y, crsProject.x);
    }
    if (ustAciRad < 0) {
      ustAciRad += 2 * Math.PI;
    }
    let ustAci = 360 - (THREE.MathUtils.radToDeg(ustAciRad))
    const altAci = THREE.MathUtils.radToDeg(this._simTmpV2.set(0, 0, 1).angleTo(ijk));
    if (altAci.toFixed(3) == "0.000") ustAci = 0.0;
    this.gostergeAci = { ustAci: ustAci, altAci: altAci }
  }

  gostergeKacmaDegerleriniAl() {
    let aktifTakimGrup = this.aktifTakimAl();
    if (!aktifTakimGrup?.children?.length) return;
    let aktifTakim = aktifTakimGrup.children[0].clone();
    let _tkm = aktifTakim

    /** */
    _tkm.position.sub(this.islemeEkseni.merkez);
    _tkm.applyQuaternion(this.islemeEkseni.quaternion.clone().invert());
    _tkm.position.set(0, 0, 0)
    /** */
    let box = new THREE.Box3().setFromObject(_tkm);

    if (!this.gostergeKacmaDegerleri)
      this.gostergeKacmaDegerleri = {};

    this.gostergeKacmaDegerleri.x = Math.abs(box.min.x) > this.aktifNokta.takim.cap ? box.max.x : box.min.x;
    this.gostergeKacmaDegerleri.y = Math.abs(box.min.y) > this.aktifNokta.takim.cap ? box.max.y : box.min.y;
    this.gostergeKacmaDegerleri.z = Math.abs(box.min.z) > this.aktifNokta.takim.cap ? box.max.z : box.min.z;
  }



  takimYollariniAl(val: any) {
    if (!val) return;
    if (!this.noktalar) this.noktalar = [];

    this._sahne.add(this.similasyonCizgisi);

    // v1 tipi: FikoAPTv1 → isle() ile parse
    if (val.type == "FikoAPTv1")
      if (val.children.length < 1)
        val.isle();

    // Orijinal tip: FikoApt → parcala() ile parse
    if (val.type == "FikoApt")
      if (val.children.length < 1)
        val.parcala?.();

    // Traverse - hem v1 hem orijinal tipleri destekle
    val.traverse((elem: any) => {
      if (elem.type == "FikoTakimYoluv1") {
        // v1 takim yolu
        if (elem.noktalar?.length)
          this.noktalar = [...this.noktalar, ...elem.noktalar];
      } else if (elem.type == "FikoTakimYolu") {
        // Orijinal takim yolu - once jsonParcala ile veriyi coz
        elem.jsonParcala?.();
        if (elem.noktalar?.length)
          this.noktalar = [...this.noktalar, ...elem.noktalar];
      }
    });

    /** Renkleri once hesapla (segment olusturmak icin) */
    const noktaRenkleri: number[][] = [];
    const noktaPozisyonlar: number[] = [];

    let _toplamSure = 0;
    for (let i = 0; i < this.noktalar.length; i++) {
      const nokta = this.noktalar[i];
      if (i > 0) {
        const oncekiNokta = this.noktalar[i - 1];
        const hesap = (oncekiNokta.distanceTo(nokta) / (nokta.hiz / 60));
        _toplamSure += hesap != Infinity ? hesap : 0;
        nokta.guncelSure = hesap != Infinity ? oncekiNokta.guncelSure + hesap : oncekiNokta.guncelSure;
      }

      noktaPozisyonlar.push(nokta.x, nokta.y, nokta.z);

      // Renk: orijinal tipte renk property varsa kullan, yoksa hareketTuru (v1)
      let r: number, g: number, b: number;
      if (nokta.renk) {
        r = nokta.renk.r; g = nokta.renk.g; b = nokta.renk.b;
      } else {
        switch (nokta.hareketTuru) {
          case "ISLEME":
            r = this.renkIsleme.r; g = this.renkIsleme.g; b = this.renkIsleme.b;
            break;
          case "YAKLASMA":
            r = this.renkYaklasma.r; g = this.renkYaklasma.g; b = this.renkYaklasma.b;
            break;
          case "UZAKLASMA":
            r = this.renkUzaklasma.r; g = this.renkUzaklasma.g; b = this.renkUzaklasma.b;
            break;
          case "BOSTA":
            r = this.renkBosta.r; g = this.renkBosta.g; b = this.renkBosta.b;
            break;
          default:
            r = this.renkBilinmiyor.r; g = this.renkBilinmiyor.g; b = this.renkBilinmiyor.b;
            break;
        }
      }
      noktaRenkleri.push([r, g, b]);
    }

    /** LineSegments buffer: her segment icin 2 vertex (flat renk, gradient yok) */
    const segmentPozisyonlar: number[] = [];
    for (let i = 1; i < this.noktalar.length; i++) {
      const onceki = this.noktalar[i - 1];
      const nokta = this.noktalar[i];
      const [r, g, b] = noktaRenkleri[i];
      // Segment: onceki → nokta (2 vertex, ayni renk = flat)
      segmentPozisyonlar.push(onceki.x, onceki.y, onceki.z);
      segmentPozisyonlar.push(nokta.x, nokta.y, nokta.z);
      this.cokluRenkDepo.push(r, g, b, r, g, b);
      this.tekRenkDepo.push(
        this.renkIsleme.r, this.renkIsleme.g, this.renkIsleme.b,
        this.renkIsleme.r, this.renkIsleme.g, this.renkIsleme.b
      );
    }

    for (let i = 0; i < this.noktalar.length; i++) {
      const nokta = this.noktalar[i];
      nokta.toplamSure = _toplamSure;
    }

    this.pozMax = this.noktalar.length - 1;

    /** Operasyon sinirlarini hesapla (timeline isaretleri) */
    this.operasyonIsaretleriHesapla();

    /** LineSegments geometrisi (segment ciftleri) */
    this.simGeoBuffer.setAttribute('position', new THREE.Float32BufferAttribute(segmentPozisyonlar, 3));
    this.simGeoBuffer.setAttribute('color', new THREE.Float32BufferAttribute(this.cokluRenkDepo, 3));

    /** Points icin ayri geometri (orijinal noktalar) */
    const noktaRenkFlat: number[] = [];
    for (const [r, g, b] of noktaRenkleri) noktaRenkFlat.push(r, g, b);
    this.simGeoBufferNoktalar.setAttribute('position', new THREE.Float32BufferAttribute(noktaPozisyonlar, 3));
    this.simGeoBufferNoktalar.setAttribute('color', new THREE.Float32BufferAttribute(noktaRenkFlat, 3));

    this.similasyonNoktalar = new THREE.Points(this.simGeoBufferNoktalar, new THREE.PointsMaterial({ size: 5, vertexColors: true, }));
    this.similasyonNoktalar.frustumCulled = false;
    this.similasyonNoktalar.visible = false;

    this.similasyonCizgisi.add(this.similasyonNoktalar)

    // Gidis yonu oku (her zaman en uste cizilir - simCizgiOnde ile ayni teknik)
    this.yonOku = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1), new THREE.Vector3(), 20, 0x00ffff, 6, 3
    );
    // ArrowHelper icindeki line (govde) ve cone (uc) nesnelerine dogrudan eris
    // transparent=true SART: similasyonCizgisi materyali transparent oldugu icin
    // opaque pass ONCE, transparent pass SONRA cizilir.
    // Arrow opaque kalirsa transparent cizginin ALTINDA kalir.
    const okLine = (this.yonOku as any).line;
    const okCone = (this.yonOku as any).cone;
    if (okLine) {
      okLine.renderOrder = 999;
      okLine.material.depthTest = false;
      okLine.material.depthWrite = false;
      okLine.material.transparent = true;
    }
    if (okCone) {
      okCone.renderOrder = 999;
      okCone.material.depthTest = false;
      okCone.material.depthWrite = false;
      okCone.material.transparent = true;
    }
    this.yonOku.renderOrder = 999;
    this.yonOku.visible = this.yonOkuGorunur;
    this.similasyonCizgisi.add(this.yonOku);

    // Talaş partikül sistemi olustur
    this.talasBaslat();

    // İnterpolasyon çizgisi (takıma kadar olan dinamik segment)
    this.interpolasyonCizgisiOlustur();

    // aktifTakim ekle - kaynakca varsa orada, yoksa sahne'ye direkt
    const aktifTakimKonteyner = this._sahne.kaynakca ?? this._sahne;
    const aktifTakimGrp = new FikoGrup(this._sahne, "aktifTakim");
    aktifTakimGrp.userData.FikoArayuz.simge = "./simgeler/aktif-takim.svg";
    aktifTakimKonteyner.add(aktifTakimGrp);

    this.CD.detectChanges();
  }


  /** Noktalardan hareket turu segmentleri + operasyon sinirlarini tespit eder */
  private operasyonIsaretleriHesapla() {
    const HAREKET_RENKLERI: Record<string, string> = {
      'İşleme': '#3cb44b', 'ISLEME': '#3cb44b',
      'Yaklaşma': '#ffe119', 'YAKLASMA': '#ffe119',
      'Uzaklaşma': '#4363d8', 'UZAKLASMA': '#4363d8',
      'Boşta': '#e6194b', 'BOSTA': '#e6194b',
    };
    this.operasyonIsaretleri = [];
    this.operasyonSinirlari = [];
    if (!this.noktalar?.length) return;

    let aktifHareket = this.hareketTuruAl(this.noktalar[0]);
    let aktifOp = this.operasyonIsmiAl(this.noktalar[0]);
    let baslangic = 0;

    for (let i = 1; i < this.noktalar.length; i++) {
      const hareket = this.hareketTuruAl(this.noktalar[i]);
      const op = this.operasyonIsmiAl(this.noktalar[i]);

      // Operasyon siniri degisti
      if (op !== aktifOp) {
        this.operasyonIsaretleri.push({
          isim: aktifOp, hareketTuru: aktifHareket, baslangic, bitis: i - 1,
          renk: HAREKET_RENKLERI[aktifHareket] || '#888'
        });
        this.operasyonSinirlari.push({ pozisyon: i, isim: op });
        aktifOp = op;
        aktifHareket = hareket;
        baslangic = i;
      } else if (hareket !== aktifHareket) {
        // Hareket turu degisti (ayni operasyon icinde)
        this.operasyonIsaretleri.push({
          isim: aktifOp, hareketTuru: aktifHareket, baslangic, bitis: i - 1,
          renk: HAREKET_RENKLERI[aktifHareket] || '#888'
        });
        aktifHareket = hareket;
        baslangic = i;
      }
    }
    // Son segment
    this.operasyonIsaretleri.push({
      isim: aktifOp, hareketTuru: aktifHareket, baslangic, bitis: this.noktalar.length - 1,
      renk: HAREKET_RENKLERI[aktifHareket] || '#888'
    });
  }

  /** Noktadan operasyon ismini al (v1 veya APTCAT) */
  private operasyonIsmiAl(nokta: any): string {
    return nokta?.aktivite?.name || nokta?.ekBilgiler?.ismi || nokta?.program?.name || '';
  }

  /** Aktif noktanin hareket turu (template icin) */
  get aktifHareketTuru(): string {
    return this.aktifNokta ? this.hareketTuruAl(this.aktifNokta) : '';
  }

  /** Aktif noktanin operasyon ismi (template icin) */
  get aktifOperasyonIsmi(): string {
    return this.aktifNokta ? this.operasyonIsmiAl(this.aktifNokta) : '';
  }

  /** Noktadan hareket turunu al (v1: hareketTuru, APTCAT: renk karsilastirmasi) */
  private hareketTuruAl(nokta: any): string {
    if (nokta?.hareketTuru) return nokta.hareketTuru;
    if (nokta?.renk) {
      if (nokta.renk.equals(this.renkIsleme)) return 'İşleme';
      if (nokta.renk.equals(this.renkYaklasma)) return 'Yaklaşma';
      if (nokta.renk.equals(this.renkUzaklasma)) return 'Uzaklaşma';
      if (nokta.renk.equals(this.renkBosta)) return 'Boşta';
    }
    return 'İşleme';
  }

  /** Timeline'da operasyona tiklandi - o operasyonun baslangicina zipla */
  tiklandiOperasyon(isaret: { baslangic: number }) {
    this.poz = isaret.baslangic;
    this.pozisyonDegisti();
  }

  sifirla() {
    this.noktalar = [];
    this.pozMin = 0;
    this.pozMax = 0;
    this.poz = 0;
    this.similasyonCizgisi.clear();

    this.similasyonCizgisi.geometry.setDrawRange(0, 0);
    this.simGeoBufferNoktalar.setDrawRange(0, 0);

    this.similasyonCizgisi.geometry.setAttribute("position", new THREE.Float32BufferAttribute([], 3));
    this.similasyonCizgisi.geometry.setAttribute("color", new THREE.Float32BufferAttribute([], 3));
    this.simGeoBufferNoktalar.setAttribute("position", new THREE.Float32BufferAttribute([], 3));
    this.simGeoBufferNoktalar.setAttribute("color", new THREE.Float32BufferAttribute([], 3));

    this.tekRenkDepo = [];
    this.cokluRenkDepo = [];
    this.operasyonIsaretleri = [];
    this.operasyonSinirlari = [];
    this.yonOku = null;
    this.sesDurdur();
    this.sesAktif = false;
    this.talasDurdur();
    this.talasAktif = false;
    this.titresimTemizle();
    this.titresimAktif = false;
    this.carpismaTemizle();

    this.poz = 0;

    const aktifTakim = this.aktifTakimAl();
    if (aktifTakim) {
      aktifTakim.clear();
      aktifTakim.parent?.remove(aktifTakim);
    }

    this.gostergeYon = null;
    this.oncekiNokta = null;
    this.aktifNokta = null;

    // Snapshot sistemi sıfırla
    this.dexelSnapshots.clear();
    this.lastSnapshotPoz = -1;
    this.lastProcessedSimPoz = -1;

    // Takım renklendirme sıfırla
    this.takimIdSayac = 0;
    this.aktifTakimId = 0;
    this.sonTakimRef = null;
    this.takimRenkHaritasi.clear();
    this.takimIsimHaritasi.clear();

    this._sahne?.anaGosterim?.ciz();
  }


  public kapa() {
    this.tiklandiDuraklat();
    this.taramaIptalEt();   // tarama animasyonu RAF'ını iptal et (kapanışta sızıntı önle)

    this.sifirla();

    this.subsList.forEach(element => {
      element.unsubscribe();
    });

    this.renderListeners?.forEach((value: any, key: string) => {
      if (value) value();
    });

    // FikoDialogCore uzerinden kapat
    this._pencere?.kapa?.();
  }

  // ─── Kütük Simülasyonu (Material Removal) ───────────────────────────
  // Production-ready CSG-based material removal simulation
  // Referans: three-bvh-csg (100x faster), ModuleWorks GPU-accelerated

  /**
   * Kütük simülasyonu toggle
   * CSG (Constructive Solid Geometry) tabanlı gerçek geometri kesimi
   */
  tiklandiKutukSimToggle() {
    this.kutukSimAktif = !this.kutukSimAktif;

    if (this.kutukSimAktif) {
      // Kütük yoksa otomatik bul
      if (!this.kutukMesh) {
        this.kutukOtomatikBul();
        if (!this.kutukMesh) {
          this.kutukSimAktif = false;
          this.CD.detectChanges();
          return;
        }
      }
      // Seçili motoru başlat
      this.kutukDexelBaslat().catch(e => console.error('Motor başlatma hatası:', e));
    } else {
      // Kütüğü orijinal haline döndür
      this.kutukReset();
    }

    this.CD.detectChanges();
  }

  /**
   * Simülasyon motoru değiştir (aktif simülasyon sırasında da çalışır)
   * Eski motoru temizler, yeni motoru başlatır
   */
  kutukMotorDegistir(yeniMod: 'dexel' | 'voxel' | 'csg' | 'visual' | 'marchingcubes' | 'sdf' | 'zbuffer') {
    if (yeniMod === this.kutukSimModu) return;

    const eskiMod = this.kutukSimModu;
    this.kutukSimModu = yeniMod;

    // Simülasyon aktif değilse sadece modu değiştir
    if (!this.kutukSimAktif) {
      this.CD.detectChanges();
      return;
    }

    // Aktif simülasyonu temizle
    this.kutukMotorTemizle(eskiMod);

    // CSG simple box varsa kaldır ve orijinali geri yükle
    if (this.kutukOrijinalMesh) {
      this.kutukOrijinalMesh.visible = true;
      this.kutukMesh = this.kutukOrijinalMesh;
      this.kutukOrijinalMesh = null;
    }

    // Orijinal kütük mesh'i görünür yap ve geometrisini geri yükle
    if (this.kutukMesh) {
      this.kutukMesh.visible = true;
      if (this.kutukOrijinalGeo) {
        this.kutukMesh.geometry.dispose();
        this.kutukMesh.geometry = this.kutukOrijinalGeo.clone();
      }
    }

    // Simülasyon state'i sıfırla
    this.kutukSimAktif = false;
    this.lastProcessedSimPoz = -1;
    this.lastSnapshotPoz = -1;
    this.dexelOncekiPoz = null;
    this._kutukOncekiPoz = null;
    this._kutukOncekiYon = null;
    this.kutukKaldirilanHacim = 0;

    // Yeni motoru başlat
    this.kutukSimAktif = true;
    this.kutukDexelBaslat().catch(e => {
      console.error('Motor değiştirme hatası:', e);
      this.kutukSimModu = 'visual';
      this.kutukSimAktif = false;
      this.CD.detectChanges();
    });

    this.CD.detectChanges();
  }

  /**
   * Motor ayarları değiştiğinde simulasyonu yeniden başlat
   */
  motorAyarlariUygula(): void {
    if (!this.kutukSimAktif || !this.kutukMesh) return;
    // Mevcut motoru temizle ve aynı modla yeniden başlat
    this.kutukMotorTemizle(this.kutukSimModu);

    if (this.kutukOrijinalMesh) {
      this.kutukOrijinalMesh.visible = true;
      this.kutukMesh = this.kutukOrijinalMesh;
      this.kutukOrijinalMesh = null;
    }

    if (this.kutukMesh) {
      this.kutukMesh.visible = true;
      if (this.kutukOrijinalGeo) {
        this.kutukMesh.geometry.dispose();
        this.kutukMesh.geometry = this.kutukOrijinalGeo.clone();
      }
    }

    this.kutukSimAktif = false;
    this.lastProcessedSimPoz = -1;
    this.lastSnapshotPoz = -1;
    this.dexelOncekiPoz = null;
    this._kutukOncekiPoz = null;
    this._kutukOncekiYon = null;
    this.kutukKaldirilanHacim = 0;

    this.kutukSimAktif = true;
    this.kutukDexelBaslat().catch(e => {
      console.error('Motor ayarları uygulama hatası:', e);
    });
    this.CD.detectChanges();
  }

  /**
   * Belirli bir motor tipinin kaynaklarını temizle
   */
  private kutukMotorTemizle(mod: string) {
    if (mod === 'dexel') {
      this.dexelTemizle();
    }
    if (mod === 'voxel' || mod === 'marchingcubes') {
      this.disposeVoxelEngine();
    }
    if (mod === 'sdf') {
      this.disposeSDFEngine();
    }
    if (mod === 'zbuffer') {
      this.disposeZBufferEngine();
    }
    if (mod === 'csg') {
      this.kutukBrush?.geometry?.dispose();
      this.kutukBrush = null;
      if (this.kutukSimpleBoxMesh) {
        this._sahne?.remove(this.kutukSimpleBoxMesh);
        this.kutukSimpleBoxMesh.geometry?.dispose();
        (this.kutukSimpleBoxMesh.material as THREE.Material)?.dispose();
        this.kutukSimpleBoxMesh = null;
      }
    }

    // Swept volume temizle
    this.kutukSweptVolumeTemizle();

    // Simülasyon ışıklarını kaldır
    this.simIsikSisteminiKaldir();
  }

  /**
   * Silindir yön modunu değiştir ve engine'e aktar
   * Simülasyonu sıfırlar ve yeniden başlatır
   */
  silindirYonModuDegisti(mod: number) {
    this.silindirYonModu = mod;

    // GPU engine varsa sıfırla
    if (this.dexelGPUEngine) {
      this.dexelGPUEngine.reset();
    }

    // CPU Dexel engine varsa modu güncelle ve sıfırla
    if (this.dexelEngine) {
      this.dexelEngine.silindirYonModu = mod;
      this.dexelEngine.reset();
      this.dexelMeshOlustur();
      this.sonIslemPoz = -1;
    }

    this.CD.detectChanges();
  }

  /**
   * Takım yönü ters çevir toggle
   * Bazı kalıplar için kesim yönünü tersine çevirmek gerekebilir
   */
  tiklandiTersYonToggle() {
    this.dexelTersYon = !this.dexelTersYon;

    // GPU engine varsa sıfırla
    if (this.dexelGPUEngine) {
      this.dexelGPUEngine.reset();
    }

    // CPU Dexel engine varsa sıfırla
    if (this.dexelEngine) {
      this.dexelEngine.reset();
      this.dexelMeshOlustur();
      this.sonIslemPoz = -1;
    }

    // Snapshot'ları temizle
    this.snapshotlariTemizle();

    this.CD.detectChanges();

    // Mevcut pozisyona kadar simülasyonu yeniden hesapla
    if (this.poz > 0 && this.dexelEngine) {
      this.processPositionRange(0, this.poz);
    }
  }

  /**
   * Kütük seçimi için mod aktifleştir
   * Ürün ağacından veya 3D'den seçim yapılabilir
   */
  tiklandiKutukSec() {
    if (this.kutukSecimModu) {
      this.kutukSecimModuKapat();
      return;
    }

    this.kutukSecimModu = true;

    // Ürün ağacı seçim dinleyicisi
    const secimler = this._sahne?.secimler;
    if (secimler?.treeStateChanged) {
      this.kutukSecimSub = secimler.treeStateChanged.subscribe((state: any) => {
        if (!this.kutukSecimModu) return;

        const selectedUuids = state?.selectedUuids;
        if (!selectedUuids || selectedUuids.size === 0) return;

        const firstUuid = selectedUuids.values().next().value;
        if (!firstUuid) return;

        let bulunanNesne: THREE.Object3D | null = null;
        this._sahne?.traverse((child: THREE.Object3D) => {
          if (child.uuid === firstUuid) bulunanNesne = child;
        });

        if (bulunanNesne) {
          this.kutukNesneSec(bulunanNesne);
        }
      });
    }

    // 3D sahne click dinleyicisi
    const canvas = this._sahne?.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      // Önceki handler'ı temizle
      if (this.kutukClickHandler) {
        canvas.removeEventListener('click', this.kutukClickHandler);
      }

      this.kutukClickHandler = (event: MouseEvent) => {
        if (!this.kutukSecimModu) return;

        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this._sahne?.anaGosterim?.aktifKamera);

        const intersects = raycaster.intersectObjects(this._sahne?.children || [], true);
        if (intersects.length > 0) {
          // Seçim başarılı olursa handler'ı temizle
          if (this.kutukNesneSec(intersects[0].object)) {
            canvas.removeEventListener('click', this.kutukClickHandler!);
            this.kutukClickHandler = null;
          }
        }
      };
      canvas.addEventListener('click', this.kutukClickHandler);
    }

    this.CD.detectChanges();
  }

  /** Kütük seçim modunu kapat */
  private kutukSecimModuKapat() {
    this.kutukSecimModu = false;
    this.kutukSecimSub?.unsubscribe();
    this.kutukSecimSub = null;
    // Click handler'ı temizle
    if (this.kutukClickHandler) {
      const canvas = this._sahne?.anaGosterim?.sunucu?.domElement;
      canvas?.removeEventListener('click', this.kutukClickHandler);
      this.kutukClickHandler = null;
    }
    this.CD.detectChanges();
  }

  /** Seçilen nesneden kütük mesh'i bul ve ayarla */
  private kutukNesneSec(nesne: THREE.Object3D): boolean {
    // FikoKutuk tipini kontrol et (parent zincirinde)
    let current: THREE.Object3D | null = nesne;
    while (current) {
      if ((current as any).type === 'FikoKutuk') {
        const kutuk = current as any;
        this.kutukParent = kutuk;
        if (kutuk.roughStockResult?.stock) {
          const fillMesh = kutuk.roughStockResult.stock.children.find(
            (c: any) => c instanceof THREE.Mesh
          );
          if (fillMesh) {
            this.kutukMeshAyarla(fillMesh as THREE.Mesh);
            this.kutukSecimModuKapat();
            return true;
          }
        }
      }
      current = current.parent;
    }

    // Direkt mesh ise
    if (nesne instanceof THREE.Mesh) {
      this.kutukMeshAyarla(nesne);
      this.kutukSecimModuKapat();
      return true;
    }

    // Çocuklarında mesh ara
    nesne.traverse((child) => {
      if (child instanceof THREE.Mesh && !this.kutukMesh) {
        this.kutukMeshAyarla(child);
      }
    });

    if (this.kutukMesh) {
      this.kutukSecimModuKapat();
      return true;
    }

    return false;
  }

  /** Ust FikoOperasyon'u bul (anaNesne'den parent zinciri ile) */
  private fikoOperasyonBul(): FikoOperasyon | null {
    let current = this._anaNesne;
    while (current) {
      if (current.type === 'Fikoperasyon') {
        return current as FikoOperasyon;
      }
      current = current.parent;
    }
    return null;
  }

  /** Sahnede otomatik kütük bul - önce operasyonun seçili kütüğüne bak */
  private kutukOtomatikBul() {
    if (!this._sahne) return;

    // 1. Operasyonun secili kutugunu kontrol et
    const fikop = this.fikoOperasyonBul();
    if (fikop?.seciliKutuk?.roughStockResult?.stock) {
      const kutuk = fikop.seciliKutuk;
      this.kutukParent = kutuk;
      const fillMesh = kutuk.roughStockResult.stock.children.find(
        (c: any) => c instanceof THREE.Mesh
      );
      if (fillMesh) {
        this.kutukMeshAyarla(fillMesh as THREE.Mesh);
        return;
      }
    }

    // 2. Fallback: Sahne genelinde tara (eski davranis)
    this._sahne.traverse((child: any) => {
      if (child.type === 'FikoKutuk' && !this.kutukMesh) {
        this.kutukParent = child;
        if (child.roughStockResult?.stock) {
          const fillMesh = child.roughStockResult.stock.children.find(
            (c: any) => c instanceof THREE.Mesh
          );
          if (fillMesh) {
            this.kutukMeshAyarla(fillMesh as THREE.Mesh);
          }
        }
      }
    });
  }

  /** Kütük mesh'i ayarla ve orijinal geometriyi sakla */
  private kutukMeshAyarla(mesh: THREE.Mesh) {
    this.kutukMesh = mesh;
    this.kutukOrijinalGeo = mesh.geometry.clone();
    this.kutukMaterial = mesh.material as THREE.MeshStandardMaterial;

    // Kütük materyalini opak yap ve render sırasını ayarla
    if (this.kutukMaterial) {
      this.kutukMaterial.transparent = false;
      this.kutukMaterial.opacity = 1.0;
      this.kutukMaterial.color.copy(this._kutukHamRenk); // Kullanıcı stok rengi
      this.kutukMaterial.side = THREE.DoubleSide; // Her iki tarafı da göster
      this.kutukMaterial.depthTest = true;
      this.kutukMaterial.depthWrite = true;
      // Z-fighting önleme
      this.kutukMaterial.polygonOffset = true;
      this.kutukMaterial.polygonOffsetFactor = -1;
      this.kutukMaterial.polygonOffsetUnits = -1;
      this.kutukMaterial.needsUpdate = true;
    }

    // Render sırasını ayarla (diğer nesnelerin önünde)
    mesh.renderOrder = 100;
    mesh.frustumCulled = false; // Kütük her zaman render edilsin

    // Kütüğü sahnede görünür yap (visibility flag)
    mesh.visible = true;
    mesh.layers.enableAll();

    // Başlangıç hacmini hesapla
    mesh.geometry.computeBoundingBox();
    const box = mesh.geometry.boundingBox;
    const bboxSize = new THREE.Vector3();
    if (box) {
      box.getSize(bboxSize);
      this.kutukBaslangicHacim = bboxSize.x * bboxSize.y * bboxSize.z;
    }

    // Görsel geri bildirim: Seçili mesh'i vurgula (geçici yanıp sönme)
    this.kutukSecimHighlight(mesh);

    this.CD.detectChanges();
  }

  /** Seçili kütük'ü görsel olarak vurgula */
  private kutukSecimHighlight(mesh: THREE.Mesh) {
    const originalMaterial = mesh.material;
    const highlightMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      wireframe: true,
      transparent: true,
      opacity: 0.5
    });

    // 3 kez yanıp sön
    let count = 0;
    const interval = setInterval(() => {
      mesh.material = (count % 2 === 0) ? highlightMaterial : originalMaterial;
      count++;
      if (count >= 6) {
        clearInterval(interval);
        mesh.material = originalMaterial;
        highlightMaterial.dispose();
      }
    }, 150);
  }

  /**
   * Geometriyi tessellate et (daha fazla üçgene böl)
   * CSG işlemleri için yeterli vertex sayısı gerekli
   * Hem indexed hem non-indexed geometriyi destekler
   * @param geometry Orijinal geometri
   * @param iterations Tessellation tekrar sayısı (her iterasyon 4x üçgen)
   * @returns Tessellate edilmiş yeni geometri (indexed)
   */
  private tessellateGeometry(geometry: THREE.BufferGeometry, iterations: number = 2): THREE.BufferGeometry {
    const posAttr = geometry.getAttribute('position');
    if (!posAttr) return geometry;

    // Önce non-indexed geometriye çevir (indexed ise)
    let workGeo = geometry;
    if (geometry.index) {
      workGeo = geometry.toNonIndexed();
    }

    const posArray = workGeo.getAttribute('position').array;
    let positions = Array.from(posArray);

    // Her iterasyonda her üçgeni 4 üçgene böl
    for (let iter = 0; iter < iterations; iter++) {
      const newPositions: number[] = [];

      // Her üçgen için (3 vertex = 9 float)
      for (let i = 0; i < positions.length; i += 9) {
        // Üçgenin 3 köşesi
        const v0x = positions[i], v0y = positions[i + 1], v0z = positions[i + 2];
        const v1x = positions[i + 3], v1y = positions[i + 4], v1z = positions[i + 5];
        const v2x = positions[i + 6], v2y = positions[i + 7], v2z = positions[i + 8];

        // Kenar orta noktaları
        const m01x = (v0x + v1x) * 0.5, m01y = (v0y + v1y) * 0.5, m01z = (v0z + v1z) * 0.5;
        const m12x = (v1x + v2x) * 0.5, m12y = (v1y + v2y) * 0.5, m12z = (v1z + v2z) * 0.5;
        const m20x = (v2x + v0x) * 0.5, m20y = (v2y + v0y) * 0.5, m20z = (v2z + v0z) * 0.5;

        // 4 yeni üçgen oluştur
        // Üçgen 1: v0, m01, m20
        newPositions.push(v0x, v0y, v0z, m01x, m01y, m01z, m20x, m20y, m20z);
        // Üçgen 2: m01, v1, m12
        newPositions.push(m01x, m01y, m01z, v1x, v1y, v1z, m12x, m12y, m12z);
        // Üçgen 3: m20, m12, v2
        newPositions.push(m20x, m20y, m20z, m12x, m12y, m12z, v2x, v2y, v2z);
        // Üçgen 4: m01, m12, m20 (ortadaki)
        newPositions.push(m01x, m01y, m01z, m12x, m12y, m12z, m20x, m20y, m20z);
      }

      positions = newPositions;
    }

    // Yeni geometri oluştur (non-indexed)
    let newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    newGeometry.computeVertexNormals();

    // three-bvh-csg indexed geometri gerektirir - mergeVertices ile indexed yap
    try {
      const indexedGeometry = mergeVertices(newGeometry, 0.0001); // Düşük tolerance
      // Index oluştu mu kontrol et
      if (indexedGeometry.index) {
        newGeometry.dispose();
        newGeometry = indexedGeometry;
      } else {
        console.warn('⚠️ mergeVertices index oluşturamadı');
        // Manuel index oluştur
        const vertCount = positions.length / 3;
        const indices = new Uint32Array(vertCount);
        for (let i = 0; i < vertCount; i++) indices[i] = i;
        newGeometry.setIndex(new THREE.BufferAttribute(indices, 1));
      }
    } catch (e) {
      console.warn('⚠️ mergeVertices başarısız, manuel index oluşturuluyor:', e);
      // Manuel index oluştur
      const vertCount = positions.length / 3;
      const indices = new Uint32Array(vertCount);
      for (let i = 0; i < vertCount; i++) indices[i] = i;
      newGeometry.setIndex(new THREE.BufferAttribute(indices, 1));
    }

    // Temizlik
    if (workGeo !== geometry) {
      workGeo.dispose();
    }

    return newGeometry;
  }

  /**
   * CSG sistemini başlat
   * three-bvh-csg Evaluator ve Brush oluştur
   * NOT: Karmaşık geometriler CSG ile uyumsuz olduğundan HER ZAMAN basit kutu kullanıyoruz
   */
  private kutukCSGBaslat() {
    if (!this.kutukMesh) return;

    // CSG Evaluator (singleton, reuse for performance)
    if (!this.csgEvaluator) {
      this.csgEvaluator = new Evaluator();
      // CRITICAL: Evaluator varsayılan olarak ['position', 'uv', 'normal'] bekler
      // Biz UV kullanmıyoruz, sadece position ve normal
      this.csgEvaluator.attributes = ['position', 'normal'];
    }

    // HER ZAMAN basit kutu kullan (three-bvh-csg ile %100 uyumlu)
    this.kutukSimpleBoxOlustur();
    return;

    // ─── Aşağıdaki kod devre dışı (karmaşık geometri CSG uyumsuz) ───
    // Kütük Brush oluştur
    try {
      // MatrixWorld'ü güncelle (parent hierarchy dahil)
      this.kutukMesh.updateMatrixWorld(true);

      // Geometriyi klonla ve world koordinatlarına dönüştür
      let worldGeo = this.kutukMesh.geometry.clone();
      worldGeo.applyMatrix4(this.kutukMesh.matrixWorld);

      // Vertex sayısı kontrol et - çok azsa tessellate et
      const origVertCount = worldGeo.attributes.position?.count || 0;

      if (origVertCount < 500) {
        // Geometri çok basit, tessellate et (2 iterasyon = 16x üçgen)
        const tessellatedGeo = this.tessellateGeometry(worldGeo, 3); // 3 iterasyon = 64x üçgen
        worldGeo.dispose();
        worldGeo = tessellatedGeo;
      }

      // Geometrinin CSG için hazır olduğunu doğrula
      if (!worldGeo.index) {
        const vertCount = worldGeo.attributes.position.count;
        const indices = new Uint32Array(vertCount);
        for (let i = 0; i < vertCount; i++) indices[i] = i;
        worldGeo.setIndex(new THREE.BufferAttribute(indices, 1));
      }

      // Normal attribute yoksa hesapla
      if (!worldGeo.attributes.normal) {
        worldGeo.computeVertexNormals();
      }

      // Gereksiz attribute'ları sil (CSG sadece position, normal, index kullanır)
      const attrNames = Object.keys(worldGeo.attributes);
      for (const name of attrNames) {
        if (name !== 'position' && name !== 'normal') {
          worldGeo.deleteAttribute(name);
        }
      }

      // Brush oluştur (zaten world koordinatlarında)
      this.kutukBrush = new Brush(worldGeo);

      // Debug: Brush bilgilerini göster
      this.kutukBrush.geometry.computeBoundingBox();
      const bb = this.kutukBrush.geometry.boundingBox;
      const vertCount = this.kutukBrush.geometry.attributes.position?.count || 0;

      // Vertex sayısı yeterli mi kontrol et
      if (vertCount < 100) {
        console.warn('⚠️ Tessellate sonrası bile vertex sayısı düşük:', vertCount);
      }

      // CSG modu başarıyla aktif edildi
      this.kutukSimModu = 'csg';

    } catch (e) {
      // Geometri CSG uyumlu değilse, BASIT KUTU geometrisi kullan
      console.warn('⚠️ Orijinal geometri CSG uyumsuz, basit kutu oluşturuluyor:', e);
      this.kutukSimpleBoxOlustur();
    }

    // Sayaçları sıfırla
    this.sonIslemPoz = -1;
    this.csgBatchSayac = 0;
    this.kutukKaldirilanHacim = 0;
    this.sweptVolumeBuffer = [];
  }

  /**
   * Takım geometrisi al veya oluştur (cache'li)
   * @returns CylinderGeometry for tool
   */
  private takimGeometrisiAl(cap: number, uzunluk: number): THREE.CylinderGeometry {
    const key = `${cap.toFixed(2)}_${uzunluk.toFixed(2)}`;
    let geo = this.takimGeoCache.get(key);

    if (!geo) {
      // Yeni geometri oluştur (radial segments düşük tutularak performans artırılır)
      geo = new THREE.CylinderGeometry(cap / 2, cap / 2, uzunluk, 16, 1);
      this.takimGeoCache.set(key, geo);
    }

    return geo;
  }

  /**
   * Material removal - CSG Boolean Subtraction
   * Takım pozisyonuna göre kütükten gerçek geometri çıkar
   * Performance: Batch işlem, her N adımda mesh güncelleme
   */
  private kutukMaterialRemoval() {
    if (!this.kutukSimAktif || !this.kutukMesh || !this.aktifNokta) return;

    // Duplicate işlem önleme
    if (this.poz === this.sonIslemPoz) return;
    this.sonIslemPoz = this.poz;

    // Sadece işleme hareketinde material çıkar
    const hareket = this.hareketTuruAl(this.aktifNokta);
    const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME' || hareket === 'Isleme';
    if (!isIsleme) {
      // Rapid/boşta harekette önceki pozisyonları güncelle
      // Yoksa sonraki kesim eski pozisyondan swept move yapar (model içinden hayalet kesim!)
      // NOT: takim.getWorldPosition stale matrixWorld verebilir — seek path ile tutarlı olarak
      // CNC nokta koordinatı (aktifNokta.x/y/z) kullanılır
      if (this.aktifNokta.x != null && this.aktifNokta.y != null && this.aktifNokta.z != null) {
        if (this.dexelEngine) {
          if (!this.dexelOncekiPoz) {
            this.dexelOncekiPoz = new THREE.Vector3();
          }
          this.dexelOncekiPoz.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
        }
        // CSG swept: rapid sonrası eski kesim noktasından süpürme yapmasın
        if (this._kutukOncekiPoz) {
          this._kutukOncekiPoz.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);
        }
      }
      return;
    }

    // Takım mesh'ini bul ve world pozisyonunu al
    const takim = this.aktifNokta.takim;
    if (!takim) return;

    // Takım parametreleri
    const takimCap = takim.cap || 10;
    const takimUzunluk = takim.boy || takimCap * 3; // Kesici boyu (lc) — yoksa 3×çap varsayımı

    // ═══════════════════════════════════════════════════════════════════════
    // TUTARLILIK: Seek path ile aynı koordinat sistemi kullan
    // aktifNokta.x/y/z = CNC pozisyonu (lokal/world aynı - aktifTakim identity)
    // aktifNokta.takimYonu = CNC IJK vektörü (zaten dünya yönü)
    // transformDirection KULLANMA - takimYonu zaten CNC'den gelen mutlak yön
    // ═══════════════════════════════════════════════════════════════════════
    this._kutukTempVec1.set(this.aktifNokta.x, this.aktifNokta.y, this.aktifNokta.z);

    const takimYonu = this.aktifNokta.takimYonu;
    this._kutukTempVec2.set(
      takimYonu?.x ?? 0,
      takimYonu?.y ?? 0,
      takimYonu?.z ?? 1
    );
    if (this._kutukTempVec2.lengthSq() < 0.0001) { this._kutukTempVec2.set(0, 0, 1); }
    else { this._kutukTempVec2.normalize(); }

    // Ters yön modu: takım yönünü tersine çevir
    if (this.dexelTersYon) {
      this._kutukTempVec2.negate();
    }

    // Visual simülasyon modu (default - güvenilir ve hızlı)
    if (this.kutukSimModu === 'visual') {
      this.kutukVisualSimulation(takimCap, takimUzunluk);
    }
    // TRIPLE-DEXEL modu - PROFESYONEL MALZEME KALDIRMA
    else if (this.kutukSimModu === 'dexel' && this.dexelEngine) {
      this.kutukDexelSubtraction(takimCap, takimUzunluk);
    }
    // CSG modu - GERÇEK MALZEME KALDIRMA
    else if (this.kutukSimModu === 'csg' && this.csgEvaluator && this.kutukBrush) {
      this.kutukCSGSubtraction(takimCap, takimUzunluk);
    }

    // Batch sayacı
    this.csgBatchSayac++;

    // Her N adımda mesh'i güncelle (performans optimizasyonu)
    if (this.kutukSimModu === 'csg' && this.csgBatchSayac >= this.CSG_BATCH_SIZE) {
      this.kutukMeshGuncelle();
      this.csgBatchSayac = 0;
    }
    // NOTE: Dexel mesh güncelleme artık throttled sistem tarafından yönetiliyor
    // (requestThrottledMeshUpdate içinde RAF-based 60fps throttling)
  }

  /**
   * Görsel Simülasyon - Swept volume olarak takım yolunu göster
   * Gerçek geometri değiştirmez, sadece görselleştirir
   */
  private kutukVisualSimulation(takimCap: number, takimUzunluk: number) {
    if (!this.kutukMesh) return;

    // Takım pozisyonu (world coordinates) - no clone, read-only access
    const takimPoz = this._kutukTempVec1;
    const takimEksen = this._kutukTempVec2;

    // Kütük bounding box içinde mi kontrol et (sadece yoksa hesapla)
    if (!this.kutukMesh.geometry.boundingBox) {
      this.kutukMesh.geometry.computeBoundingBox();
    }
    const bbox = this.kutukMesh.geometry.boundingBox;
    if (!bbox) return;

    // World koordinatlarına çevir (pre-allocated Box3)
    const worldBBox = this._kutukTempBox3.copy(bbox);
    worldBBox.applyMatrix4(this.kutukMesh.matrixWorld);

    // Takım kütük içinde mi?
    if (!worldBBox.containsPoint(takimPoz)) {
      return; // Kütük dışında, işlem yok
    }

    // Kaldırılan hacmi hesapla (yaklaşık silindir hacmi)
    const takimHacim = Math.PI * Math.pow(takimCap / 2, 2) * takimUzunluk * 0.001; // mm³ → cm³
    this.kutukKaldirilanHacim += takimHacim * 0.01; // Her adımda küçük bir miktar

    // Kütük rengini güncelle (işleme oranına göre)
    if (this.kutukMaterial && this.kutukBaslangicHacim > 0) {
      const islemOrani = Math.min(this.kutukKaldirilanHacim / this.kutukBaslangicHacim, 0.9);
      const hamR = this._kutukHamRenk.r, hamG = this._kutukHamRenk.g, hamB = this._kutukHamRenk.b;
      const r = hamR + islemOrani * (1.0 - hamR) * 0.4;
      const g = hamG + islemOrani * (1.0 - hamG) * 0.4;
      const b = hamB + islemOrani * (1.0 - hamB) * 0.4;
      this.kutukMaterial.color.setRGB(r, g, b);
    }

    // Swept volume mesh'i oluştur/güncelle (ilk adımda)
    if (this.csgBatchSayac === 0) {
      this.kutukSweptVolumeEkle(takimPoz, takimEksen, takimCap, takimUzunluk);
    }
  }

  /** Swept volume mesh'lerini tutan grup */
  private sweptVolumeGroup: THREE.Group | null = null;
  private sweptVolumeMeshCount = 0;
  private readonly MAX_SWEPT_MESHES = 500; // Performans için limit

  /**
   * Swept volume silindir ekle
   */
  private kutukSweptVolumeEkle(poz: THREE.Vector3, eksen: THREE.Vector3, cap: number, uzunluk: number) {
    // Debug: Fonksiyon çağrıldı
    if (this.sweptVolumeMeshCount < 3) {
    }

    if (!this._sahne) {
      console.warn('⚠️ Swept volume eklenemedi: sahne yok!');
      return;
    }

    // Grup oluştur (ilk seferde)
    if (!this.sweptVolumeGroup) {
      this.sweptVolumeGroup = new THREE.Group();
      this.sweptVolumeGroup.name = 'KutukSweptVolume';
      this._sahne.add(this.sweptVolumeGroup);
    }

    // Maksimum mesh sayısına ulaşıldıysa, en eskiyi sil
    if (this.sweptVolumeMeshCount >= this.MAX_SWEPT_MESHES) {
      const oldest = this.sweptVolumeGroup.children[0];
      if (oldest) {
        this.sweptVolumeGroup.remove(oldest);
        (oldest as THREE.Mesh).geometry?.dispose();
        this.sweptVolumeMeshCount--;
      }
    }

    // Silindir geometrisi (düşük çözünürlük - performans için)
    const geo = new THREE.CylinderGeometry(cap / 2, cap / 2, uzunluk, 8, 1);

    // Materyal (yarı saydam kırmızı - kesilen alan)
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff4444,
      transparent: true,
      opacity: 0.3,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(geo, mat);

    // Pozisyon ve rotasyon ayarla (pre-allocated)
    const quat = this._kutukTempQuat.setFromUnitVectors(this._kutukUpVec.set(0, 1, 0), eksen);
    mesh.position.copy(poz).addScaledVector(eksen, uzunluk / 2);
    mesh.quaternion.copy(quat);

    this.sweptVolumeGroup.add(mesh);
    this.sweptVolumeMeshCount++;
  }

  /**
   * CSG Boolean Subtraction işlemi
   * three-bvh-csg ile 100x faster subtraction
   *
   * SWEPT VOLUME: Önceki pozisyondan şimdiki pozisyona silindir oluşturur
   * Bu sayede çizgi boyunca kesintisiz malzeme kaldırma sağlanır
   *
   * IMPORTANT: three-bvh-csg kuralları:
   * 1. Brush geometry'si oluşturulduktan sonra DEĞİŞTİRİLMEMELİ
   * 2. updateMatrixWorld() evaluate'den önce çağrılmalı
   * 3. Geometry two-manifold (water tight) olmalı
   */
  private kutukCSGSubtraction(takimCap: number, takimUzunluk: number) {
    if (!this.csgEvaluator || !this.kutukBrush || !this.kutukMesh) return;

    try {
      // Kütük brush'ın geçerli olduğunu doğrula
      if (!this.kutukBrush?.geometry?.attributes?.position?.array) {
        console.error('❌ Kütük Brush geçersiz - position attribute yok');
        return;
      }

      // Şimdiki takım pozisyonu ve yönü (copy to sim buffers, avoid clone alloc)
      const simdikiPoz = this._simTmpV1.copy(this._kutukTempVec1);
      const takimYonu = this._simTmpV2.copy(this._kutukTempVec2);

      // ─── SWEPT VOLUME: Önceki pozisyondan şimdikine silindir ───
      let sweptGeo: THREE.BufferGeometry;

      if (this._kutukOncekiPoz) {
        // Önceki ve şimdiki pozisyon arasındaki mesafe
        const mesafe = simdikiPoz.distanceTo(this._kutukOncekiPoz);

        // Eğer mesafe çok küçükse (aynı noktada), normal silindir kullan
        if (mesafe < 0.1) {
          sweptGeo = this.kutukTakimSilindiriOlustur(simdikiPoz, takimYonu, takimCap, takimUzunluk);
        } else {
          // Swept volume: takım silindirinin iki tip pozisyonu arasındaki GERÇEK süpürme katısı
          // (eski hatalı model: hareket ekseni etrafında tüp — kesim takımın üstünde görünüyordu)
          sweptGeo = this.kutukSweptPrizmaOlustur(this._kutukOncekiPoz, simdikiPoz, takimYonu, takimCap, takimUzunluk);
        }
      } else {
        // İlk pozisyon - normal silindir
        sweptGeo = this.kutukTakimSilindiriOlustur(simdikiPoz, takimYonu, takimCap, takimUzunluk);
      }

      // Önceki pozisyonu güncelle
      if (!this._kutukOncekiPoz) {
        this._kutukOncekiPoz = new THREE.Vector3();
        this._kutukOncekiYon = new THREE.Vector3();
      }
      this._kutukOncekiPoz.copy(simdikiPoz);
      this._kutukOncekiYon!.copy(takimYonu);

      // ─── Takım Brush oluştur ───
      const takimBrush = new Brush(sweptGeo);

      // ─── updateMatrixWorld çağır (ZORUNLU!) ───
      this.kutukBrush.updateMatrixWorld(true);
      takimBrush.updateMatrixWorld(true);

      // ─── CSG Subtraction ───
      const resultBrush = this.csgEvaluator.evaluate(this.kutukBrush, takimBrush, SUBTRACTION);

      // Sonuç geçerli mi kontrol et
      const resultVertCount = resultBrush.geometry.attributes.position?.count || 0;
      const origVertCount = this.kutukBrush.geometry.attributes.position?.count || 0;

      if (resultVertCount > 50 && resultVertCount >= origVertCount * 0.05) {
        // Eski brush'ı temizle
        this.kutukBrush.geometry.dispose();

        // Sonuç geometrisinden YENİ Brush oluştur (değiştirmeden!)
        const cleanGeo = resultBrush.geometry.clone();

        // UV varsa sil (klondan önce değil, klondan sonra - orijinali bozma)
        if (cleanGeo.attributes.uv) {
          cleanGeo.deleteAttribute('uv');
        }
        cleanGeo.computeVertexNormals();
        cleanGeo.computeBoundingBox();
        cleanGeo.computeBoundingSphere();

        // Yeni Brush oluştur
        this.kutukBrush = new Brush(cleanGeo);

        // Result brush'ı temizle
        resultBrush.geometry.dispose();

        // Kaldırılan hacmi hesapla (yaklaşık)
        const takimHacim = Math.PI * Math.pow(takimCap / 2, 2) * takimUzunluk * 0.001; // mm³ → cm³
        this.kutukKaldirilanHacim += takimHacim;
      } else {
        // Sonuç çok küçük, CSG başarısız olmuş olabilir - atla
        if (this.csgBatchSayac < 5) {
          console.warn('⚠️ CSG sonucu geçersiz:', resultVertCount, 'vertices (orijinal:', origVertCount, ')');
        }
        resultBrush.geometry.dispose();
      }

      // Takım brush'ı temizle
      takimBrush.geometry.dispose();
      sweptGeo.dispose();

    } catch (e) {
      // CSG hatası - geometri non-manifold olabilir
      console.error('❌ CSG Subtraction hatası:', e);
    }
  }

  /**
   * Takım silindiri geometrisi oluştur (tek pozisyon için)
   * @param poz Takım merkez pozisyonu
   * @param yon Takım yönü (normalize edilmiş)
   * @param cap Takım çapı
   * @param uzunluk Silindir uzunluğu
   */
  /** Aktif takımın geometri tipini tespit eder (ball/bull/flat) — removal şekli buna göre. */
  private kutukTakimTipiAl(cap: number): 'ball' | 'bull' | 'flat' {
    const t: any = this.aktifNokta?.takim;
    const tip = (t?.takimTipi || '').toLowerCase();
    if (tip.includes('ball') || tip.includes('nose')) return 'ball';
    const r = t?.radius || 0;
    if (r >= cap / 2 - 0.5) return 'ball';
    if (r > 0.05) return 'bull';
    return 'flat';
  }
  /** Takım kesme hacmi geometrisi — ball→kapsül (silindir+yarımküre uç), flat→silindir. */
  private kutukTakimGeoYap(cap: number, uzunluk: number): THREE.BufferGeometry {
    const tip = this.kutukTakimTipiAl(cap), r = cap / 2;
    if (tip === 'ball') return new THREE.CapsuleGeometry(r, Math.max(0.1, uzunluk - cap), 6, 14);  // toplam yükseklik = uzunluk, alt yarımküre = ball ucu
    return new THREE.CylinderGeometry(r, r, uzunluk, 14, 1);
  }

  private kutukTakimSilindiriOlustur(poz: THREE.Vector3, yon: THREE.Vector3, cap: number, uzunluk: number): THREE.BufferGeometry {
    const geo = this.kutukTakimGeoYap(cap, uzunluk);

    // UV sil
    geo.deleteAttribute('uv');

    // Normal ve bounding hesapla
    geo.computeVertexNormals();

    // Silindir Y ekseni boyunca, takım yönüne döndür (pre-allocated)
    const quat = this._kutukTempQuat.setFromUnitVectors(this._kutukUpVec.set(0, 1, 0), yon);

    // Merkez pozisyonu: takım ucundan uzunluk/2 ileride (pre-allocated, no clone)
    const merkez = this._kutukTempVec3.copy(poz).addScaledVector(yon, uzunluk / 2);

    // Transform uygula (pre-allocated)
    const mat = this._kutukTempMat4.compose(merkez, quat, this._kutukUnitScale);
    geo.applyMatrix4(mat);

    geo.computeBoundingBox();
    geo.computeBoundingSphere();

    return geo;
  }

  /**
   * Swept kesme hacmi (iki tip pozisyonu arası) — takım silindirinin gerçek süpürme katısı.
   * Yanal hareket: stadyum profil (dikdörtgen + yarım daire uçlar) takım ekseni boyunca ekstrüde edilir.
   * Dalma/çıkma (hareket ≈ takım ekseni): eksen boyunca uzatılmış silindir.
   * Rampa hareketlerinde yükseklik eksen bileşeni kadar uzatılır (hafif fazla kesim, güvenli yaklaşım).
   */
  private kutukSweptPrizmaOlustur(p0: THREE.Vector3, p1: THREE.Vector3, eksen: THREE.Vector3, cap: number, takimBoy: number): THREE.BufferGeometry {
    const R = cap / 2;
    const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
    const hEksen = dx * eksen.x + dy * eksen.y + dz * eksen.z;      // hareketin takım-ekseni bileşeni
    const yx = dx - eksen.x * hEksen, yy = dy - eksen.y * hEksen, yz = dz - eksen.z * hEksen;
    const d = Math.sqrt(yx * yx + yy * yy + yz * yz);               // yanal (eksene dik) mesafe
    const H = takimBoy + Math.abs(hEksen);

    let geo: THREE.BufferGeometry;
    const merkez = this._kutukTempVec3;

    if (d < 1e-4) {
      // Dalma/çıkma: alt tip'ten üst tip + takım boyuna kadar eksen boyunca silindir
      geo = new THREE.CylinderGeometry(R, R, H, 14, 1);
      const quat = this._kutukTempQuat.setFromUnitVectors(this._kutukUpVec.set(0, 1, 0), eksen);
      merkez.copy(p0).addScaledVector(eksen, Math.min(0, hEksen) + H / 2);
      geo.applyMatrix4(this._kutukTempMat4.compose(merkez, quat, this._kutukUnitScale));
    } else {
      // Yanal: stadyum profili (XY düzleminde p0→p1) takım ekseni boyunca ekstrüzyon
      const shape = new THREE.Shape();
      shape.moveTo(0, -R);
      shape.lineTo(d, -R);
      shape.absarc(d, 0, R, -Math.PI / 2, Math.PI / 2, false);
      shape.lineTo(0, R);
      shape.absarc(0, 0, R, Math.PI / 2, Math.PI * 1.5, false);
      geo = new THREE.ExtrudeGeometry(shape, { depth: H, bevelEnabled: false, curveSegments: 7 });
      // Lokal çerçeve: X = yanal hareket yönü, Z = takım ekseni, origin = alt tip
      const xAx = this._simTmpV3.set(yx / d, yy / d, yz / d);
      const yAx = this._simTmpV4.copy(eksen).cross(xAx);
      this._kutukTempMat4.makeBasis(xAx, yAx, eksen);
      merkez.copy(p0).addScaledVector(eksen, Math.min(0, hEksen));
      this._kutukTempMat4.setPosition(merkez);
      geo.applyMatrix4(this._kutukTempMat4);
    }

    geo.deleteAttribute('uv');
    geo.computeVertexNormals();
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    return geo;
  }

  /**
   * Mesh'i CSG sonucuyla güncelle
   * Batch işlem sonrası çağrılır
   */
  private kutukGuncellemeSayisi = 0;
  private kutukMeshGuncelle() {
    if (!this.kutukMesh || !this.kutukBrush) return;

    try {
      this.kutukGuncellemeSayisi++;

      // Mevcut geometriyi dispose et
      this.kutukMesh.geometry.dispose();

      // CSG sonuç geometrisini ata
      // Brush'ın geometrisini klonla (brush'ı bozmamak için)
      this.kutukMesh.geometry = this.kutukBrush.geometry.clone();

      // Basit kutu kullanıyoruz - zaten world koordinatlarında
      // Inverse transform UYGULAMIYORUZ (mesh origin'de)

      // Normal'ları yeniden hesapla (CSG sonrası bozulabilir)
      this.kutukMesh.geometry.computeVertexNormals();
      this.kutukMesh.geometry.computeBoundingBox();
      this.kutukMesh.geometry.computeBoundingSphere();

      // Render ayarlarını koru
      this.kutukMesh.renderOrder = 100;
      this.kutukMesh.frustumCulled = false; // Kütük her zaman render edilsin

      // Material güncelle (kaldırılan orana göre renk değişimi - opak kalacak)
      if (this.kutukMaterial && this.kutukBaslangicHacim > 0) {
        const kalanOran = 1 - (this.kutukKaldirilanHacim / this.kutukBaslangicHacim);
        const islemOrani = 1 - kalanOran;
        const hamR = this._kutukHamRenk.r, hamG = this._kutukHamRenk.g, hamB = this._kutukHamRenk.b;
        const r = hamR + islemOrani * (1.0 - hamR) * 0.4;
        const g = hamG + islemOrani * (1.0 - hamG) * 0.4;
        const b = hamB + islemOrani * (1.0 - hamB) * 0.4;
        this.kutukMaterial.color.setRGB(r, g, b);
        this.kutukMaterial.transparent = false;
        this.kutukMaterial.opacity = 1.0;
        this.kutukMaterial.needsUpdate = true;
      }

      // BVH güncelle (varsa)
      if ((this.kutukMesh.geometry as any).boundsTree) {
        (this.kutukMesh.geometry as any).boundsTree = null;
      }

    } catch (e) {
      // Geometri güncelleme hatası
    }

    this._sahne?.anaGosterim?.ciz();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ██████  TRIPLE-DEXEL STOCK REMOVAL  ██████████████████████████████████████
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Stok simülasyon sistemini başlat (Triple-Dexel veya Voxel CSG)
   * Kütük bounding box'ından grid oluşturur
   */
  private async kutukDexelBaslat(): Promise<void> {
    if (!this.kutukMesh) return;

    try {
      // Bounding box hesapla (world koordinatlarında)
      this.kutukMesh.updateMatrixWorld(true);
      const bbox = new THREE.Box3().setFromObject(this.kutukMesh);

      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      bbox.getSize(size);
      bbox.getCenter(center);

      // Padding ekle (kullanıcı ayarından)
      const padding = this.simPadding;
      const dexelBBox = {
        minX: bbox.min.x - padding,
        maxX: bbox.max.x + padding,
        minY: bbox.min.y - padding,
        maxY: bbox.max.y + padding,
        minZ: bbox.min.z - padding,
        maxZ: bbox.max.z + padding
      };

      // Resolution: cells per mm
      const maxDim = Math.max(size.x, size.y, size.z);
      const dimX = dexelBBox.maxX - dexelBBox.minX;
      const dimY = dexelBBox.maxY - dexelBBox.minY;
      const dimZ = dexelBBox.maxZ - dexelBBox.minZ;

      const is3DEngine = this.kutukSimModu === 'voxel' || this.kutukSimModu === 'marchingcubes'
        || this.kutukSimModu === 'sdf' || this.kutukSimModu === 'zbuffer';

      let resolution: number;
      if (is3DEngine) {
        // 3D: manual resolution veya auto
        if (this.simResolution3D > 0) {
          resolution = this.simResolution3D;
        } else {
          resolution = maxDim > 500 ? 2.0 : (maxDim > 200 ? 3.0 : 5.0);
        }
        while (Math.ceil(dimX * resolution) * Math.ceil(dimY * resolution) * Math.ceil(dimZ * resolution) > this.simMaxVoxels3D && resolution > 0.5) {
          resolution *= 0.85;
        }
      } else {
        // 2D: manual resolution veya auto
        if (this.simResolution2D > 0) {
          resolution = this.simResolution2D;
        } else {
          resolution = maxDim > 500 ? 0.75 : (maxDim > 200 ? 1.0 : 1.5);
        }
        while (Math.ceil(dimX * resolution) * Math.ceil(dimY * resolution) > this.simMaxCells2D && resolution > 0.25) {
          resolution *= 0.8;
        }
      }

      const resX = Math.ceil(dimX * resolution);
      const resY = Math.ceil(dimY * resolution);
      const resZ = Math.ceil(dimZ * resolution);
      const cellSize = 1 / resolution;

      // ═══════════════════════════════════════════════════════════════════════════
      // MOTOR SEÇİMİNE GÖRE İNIT
      // ═══════════════════════════════════════════════════════════════════════════

      if (this.kutukSimModu === 'voxel') {
        // ─── VOXEL CSG MOTOR ───────────────────────────────────────────────────
        await this.initVoxelEngine(dexelBBox, cellSize, resX, resY, resZ);
      } else if (this.kutukSimModu === 'marchingcubes') {
        // ─── GPU MARCHING CUBES MOTOR (voxel removal + GPU MC mesh) ────────────
        await this.initVoxelEngine(dexelBBox, cellSize, resX, resY, resZ);
        // Initialize MarchingCubesGPU on top of VoxelGPUCompute
        if (this.useGPUVoxel && this.voxelGPUCompute) {
          const device = this.voxelGPUCompute.getDevice();
          const voxelBuf = this.voxelGPUCompute.getVoxelBuffer();
          if (device && voxelBuf) {
            try {
              this.marchingCubesGPU = await createMarchingCubesGPU(
                device, voxelBuf, resX, resY, resZ, cellSize,
                [dexelBBox.minX, dexelBBox.minY, dexelBBox.minZ]
              );
            } catch (e) {
              console.warn('[MarchingCubes] GPU MC init failed, using CPU mesh:', e);
            }
          }
        }
      } else if (this.kutukSimModu === 'sdf') {
        // ─── SDF MOTOR ────────────────────────────────────────────────────────
        await this.initSDFEngine(dexelBBox, cellSize, resX, resY, resZ);
      } else if (this.kutukSimModu === 'zbuffer') {
        // ─── Z-BUFFER MOTOR ───────────────────────────────────────────────────
        await this.initZBufferEngine(dexelBBox, cellSize);
      } else if (this.kutukSimModu === 'dexel') {
        // ─── TRIPLE-DEXEL MOTOR (EXISTING) ─────────────────────────────────────
        // Önce WebGPU dene
        this.useGPUDexel = false;
        if ((navigator as any).gpu) {
          try {
            const gpuEngine = await createDexelGPUEngine({
              resX, resY, cellSize,
              bbox: dexelBBox,
              silindirYonModu: this.silindirYonModu
            });
            if (gpuEngine) {
              this.dexelGPUEngine = gpuEngine;
              this.useGPUDexel = true;
            }
          } catch (e) {
            console.warn('WebGPU init failed, using CPU:', e);
          }
        }

        // CPU fallback (her zaman oluştur - mesh için gerekli)
        this.dexelEngine = new TripleDexelEngine(dexelBBox, resolution);
        this.dexelEngine.silindirYonModu = this.silindirYonModu;

        // WebWorker dene - main thread'i bloklamadan dexel işleme
        this.useWorkerDexel = false;
        if (!this.useGPUDexel) {
          try {
            const bridge = createDexelWorkerBridge();
            if (bridge) {
              const workerOk = await bridge.init({
                resX, resY, resZ, cellSize,
                bbox: dexelBBox,
                silindirYonModu: this.silindirYonModu
              });
              if (workerOk) {
                this.dexelWorkerBridge = bridge;
                this.useWorkerDexel = true;
              }
            }
          } catch (e) {
            console.warn('WebWorker init failed, using CPU:', e);
          }
        }

        // İlk mesh oluştur
        this.dexelMeshOlustur();
      }

      // Orijinal kütüğü gizle
      this.kutukMesh.visible = false;
      this.kutukSimAktif = true;
      this.dexelOncekiPoz = null;

      // Batch processing sıfırla
      this.toolPositionBatch.length = 0;
      this.lastMeshUpdateTime = 0;
      this.meshUpdatePending = false;
      this.perfMetrics = { totalProcessTime: 0, meshUpdateTime: 0, batchCount: 0, avgBatchTime: 0 };

      // Snapshot sistemi sıfırla (ileri/geri sarma desteği için)
      this.dexelSnapshots.clear();
      this.lastSnapshotPoz = -1;
      this.lastProcessedSimPoz = -1;

      // Takım renklendirme sıfırla
      this.takimIdSayac = 0;
      this.aktifTakimId = 0;
      this.sonTakimRef = null;
      this.takimRenkHaritasi.clear();
      this.takimIsimHaritasi.clear();

    } catch (e) {
      console.error('❌ Triple-Dexel başlatılamadı:', e);
      this.kutukSimModu = 'visual';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROFESYoNEL IŞIK SİSTEMİ - CNC Simülasyon Stüdyo Aydınlatma
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Simülasyona özel profesyonel 3-nokta ışık sistemi kur
   * Key light (ana), fill light (dolgu), rim light (kenar vurgu)
   * + hemisphere ambient + environment map
   */
  private simIsikSisteminiKur(): void {
    if (!this._sahne) return;
    this.simIsikSisteminiKaldir();

    // Kütük bounding box'ını al (ışıkları konumlandırmak için)
    const bbox = this.dexelEngine?.getBBox();
    const cx = bbox ? (bbox.minX + bbox.maxX) / 2 : 0;
    const cy = bbox ? (bbox.minY + bbox.maxY) / 2 : 0;
    const cz = bbox ? (bbox.minZ + bbox.maxZ) / 2 : 0;
    const size = bbox ? Math.max(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY, bbox.maxZ - bbox.minZ) : 100;
    const dist = size * 2;

    // ═══ CATIA-LEVEL STUDIO LIGHTING (yüksek kontrast, derinlik vurgusu) ═══

    // 1. Key Light - Ana ışık (sağ üst ön, sıcak beyaz) - güçlü yönlü gölge
    const keyLight = new THREE.DirectionalLight(0xfff5e8, 3.2);
    keyLight.position.set(cx + dist * 0.7, cy - dist * 0.5, cz + dist * 1.2);
    keyLight.name = '_simKeyLight';
    this._sahne.add(keyLight);
    this._simLights.push(keyLight);

    // 2. Fill Light - Dolgu ışık (sol, soğuk mavi-beyaz, AZALTILMIŞ → yüksek kontrast)
    const fillLight = new THREE.DirectionalLight(0xd8e4f8, 0.2);
    fillLight.position.set(cx - dist * 0.8, cy + dist * 0.3, cz + dist * 0.5);
    fillLight.name = '_simFillLight';
    this._sahne.add(fillLight);
    this._simLights.push(fillLight);

    // 3. Rim Light - Kenar vurgu (arkadan, yüzey formunu belirginleştirir)
    const rimLight = new THREE.DirectionalLight(0xffd4a6, 0.6);
    rimLight.position.set(cx, cy + dist * 0.8, cz + dist * 0.3);
    rimLight.name = '_simRimLight';
    this._sahne.add(rimLight);
    this._simLights.push(rimLight);

    // 4. Top Spot - Üstten odaklı aydınlatma (cep derinliği vurgusu)
    const topSpot = new THREE.DirectionalLight(0xf0f4ff, 0.8);
    topSpot.position.set(cx, cy, cz + dist * 1.5);
    topSpot.name = '_simTopSpot';
    this._sahne.add(topSpot);
    this._simLights.push(topSpot);

    // 5. Alt dolgu ışık - alttan gelen çok yumuşak aydınlatma
    const bottomFill = new THREE.DirectionalLight(0x8090a8, 0.08);
    bottomFill.position.set(cx, cy, cz - dist * 0.5);
    bottomFill.name = '_simBottomFill';
    this._sahne.add(bottomFill);
    this._simLights.push(bottomFill);

    // 6. Hemisphere Ambient - Minimal genel dolgu (gölge belirginliği maksimum)
    const hemiLight = new THREE.HemisphereLight(0xc8d4e4, 0x404550, 0.15);
    hemiLight.name = '_simHemiLight';
    this._sahne.add(hemiLight);
    this._simLights.push(hemiLight);

    // 5. Environment Map oluştur (PMREMGenerator ile gradient env)
    this.simEnvMapOlustur();
  }

  /**
   * PMREMGenerator ile profesyonel environment map oluştur
   * Metalik yüzeylerde gerçekçi yansıma sağlar
   */
  private simEnvMapOlustur(): void {
    const renderer = this._sahne?.anaGosterim?.sunucu as THREE.WebGLRenderer;
    if (!renderer) return;

    // Basit gradient environment scene oluştur
    const envScene = new THREE.Scene();

    // CNC Atölyesi HDR Ortamı - floresan ışık bantları + metalik yansıma
    const topColor = new THREE.Color(0xe0e8f4);     // Açık gri-mavi (üst - tavan)
    const midColor = new THREE.Color(0x808890);      // Nötr gri (ortam)
    const botColor = new THREE.Color(0x404448);      // Koyu gri (alt - zemin)
    const stripColor = new THREE.Color(0xf8f0e0);   // Floresan ışık bandı (sıcak beyaz)

    // Büyük küre ile gradient + floresan bantlı environment
    const envGeo = new THREE.SphereGeometry(50, 64, 32);
    const envColors = new Float32Array(envGeo.attributes.position.count * 3);
    const envPositions = envGeo.attributes.position.array as Float32Array;
    const tmpC = new THREE.Color(); // pre-allocated - zero GC in loop
    for (let i = 0; i < envGeo.attributes.position.count; i++) {
      const px = envPositions[i * 3] / 50;
      const py = envPositions[i * 3 + 1] / 50;  // -1 to 1 normalized
      const t = py * 0.5 + 0.5; // 0 to 1
      if (t > 0.5) {
        tmpC.lerpColors(midColor, topColor, (t - 0.5) * 2);
      } else {
        tmpC.lerpColors(botColor, midColor, t * 2);
      }
      // Floresan ışık bantları (tavandaki tüp lambalar)
      if (t > 0.7) {
        const stripT = Math.abs(Math.sin(px * Math.PI * 3)) * Math.pow(Math.max(0, t - 0.7) / 0.3, 2);
        if (stripT > 0.6) {
          tmpC.lerp(stripColor, (stripT - 0.6) * 2.0);
        }
      }
      envColors[i * 3] = tmpC.r;
      envColors[i * 3 + 1] = tmpC.g;
      envColors[i * 3 + 2] = tmpC.b;
    }
    envGeo.setAttribute('color', new THREE.BufferAttribute(envColors, 3));
    const envMat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide });
    envScene.add(new THREE.Mesh(envGeo, envMat));

    // PMREMGenerator ile HDR-kalitesinde env map üret
    const pmremGen = new THREE.PMREMGenerator(renderer);
    pmremGen.compileEquirectangularShader();
    const envMapRT = pmremGen.fromScene(envScene, 0, 0.1, 100);
    this._simEnvMap = envMapRT.texture;
    pmremGen.dispose();
    envGeo.dispose();
    envMat.dispose();

    // Renderer tone mapping ayarla - CATIA-level profesyonel CNC render kalitesi
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;  // Güçlü key ışık için kompanzasyon (kontrast koruma)
  }

  /**
   * Simülasyon ışık sistemini kaldır
   */
  private simIsikSisteminiKaldir(): void {
    for (const light of this._simLights) {
      this._sahne?.remove(light);
      if ((light as any).dispose) (light as any).dispose();
    }
    this._simLights.length = 0;

    if (this._simEnvMap) {
      this._simEnvMap.dispose();
      this._simEnvMap = null;
    }
  }

  /**
   * Dexel height map'inden PROFESYONEL mesh oluştur (CATIA-level)
   */
  private dexelMeshOlustur() {
    if (!this.dexelEngine) return;

    // Önceki mesh'i temizle
    if (this.dexelMesh) {
      this._sahne?.remove(this.dexelMesh);
      this.dexelMesh.geometry?.dispose();
      (this.dexelMesh.material as THREE.Material)?.dispose();
    }
    // Önceki kenar çizgilerini temizle
    if (this.kenarCizgileriMesh) {
      this._sahne?.remove(this.kenarCizgileriMesh);
      this.kenarCizgileriMesh.geometry?.dispose();
      (this.kenarCizgileriMesh.material as THREE.Material)?.dispose();
      this.kenarCizgileriMesh = null;
    }
    // Önceki iç duvar mesh'ini temizle
    if (this.icDuvarMesh) {
      this._sahne?.remove(this.icDuvarMesh);
      this.icDuvarMesh.geometry?.dispose();
      (this.icDuvarMesh.material as THREE.Material)?.dispose();
      this.icDuvarMesh = null;
    }

    // Profesyonel ışık sistemini kur (ilk kez veya yeniden)
    this.simIsikSisteminiKur();

    // Layout cache güncelle
    this._dexelLayout = this.dexelEngine.getMeshLayout();

    // Yeni PROFESYONEL mesh oluştur (PBR + AO + flatShading)
    const stokRenkHex = parseInt(this.kutukStokRenk.replace('#', ''), 16) || 0xB8C0C8;
    const aoI = this.aoYogunluk / 100;
    this.dexelMesh = this.dexelEngine.createProfessionalStockMesh(stokRenkHex, aoI, this.aoRadius, this.dexelSmoothing);
    this.dexelMesh.name = 'DexelStockMesh';
    this.dexelMesh.renderOrder = 50;

    // Malzeme preset uygula
    this.malzemePresetUygula();

    // Environment map'i materyale uygula (MeshPhysicalMaterial)
    const mat = this.dexelMesh.material as THREE.MeshPhysicalMaterial;
    if (this._simEnvMap && mat) {
      mat.envMap = this._simEnvMap;
      mat.needsUpdate = true;
    }

    this._sahne?.add(this.dexelMesh);

    // Kenar çizgilerini oluştur
    if (this.kenarCizgileriAktif) {
      this.kenarCizgileriOlustur();
    }

    // İç duvar mesh oluştur
    if (this.icDuvarAktif) {
      this.icDuvarOlustur();
    }
  }

  /** İç duvar mesh'ini oluştur/yenile (cep kenarları) */
  private icDuvarOlustur(): void {
    if (!this.dexelEngine) return;

    // Önceki mesh'i temizle
    if (this.icDuvarMesh) {
      this._sahne?.remove(this.icDuvarMesh);
      this.icDuvarMesh.geometry?.dispose();
      (this.icDuvarMesh.material as THREE.Material)?.dispose();
      this.icDuvarMesh = null;
    }

    const heightMap = this.dexelEngine.extractHeightMap();
    const bottomMap = this.dexelEngine.extractBottomMap();
    const threshold = this.dexelEngine.cellSize * 1.5;
    const hamRenk = this._kutukHamRenk;

    // Extract main mesh top surface colors for cap color matching
    let cellColors: Float32Array | undefined;
    if (this.dexelMesh?.geometry?.attributes?.color) {
      const mainColors = this.dexelMesh.geometry.attributes.color.array as Float32Array;
      const totalCells = this.dexelEngine.resX * this.dexelEngine.resY;
      cellColors = mainColors.subarray(0, totalCells * 3);
    }

    this.icDuvarMesh = this.dexelEngine.createInternalWallMesh(
      heightMap, bottomMap,
      { r: hamRenk.r, g: hamRenk.g, b: hamRenk.b },
      threshold,
      cellColors
    );

    if (this.icDuvarMesh) {
      this.icDuvarMesh.name = 'DexelInternalWalls';
      this.icDuvarMesh.renderOrder = 50;
      // Env map paylaş
      if (this.dexelMesh) {
        const mainMat = this.dexelMesh.material as THREE.MeshPhysicalMaterial;
        const wallMat = this.icDuvarMesh.material as THREE.MeshPhysicalMaterial;
        if (mainMat?.envMap) wallMat.envMap = mainMat.envMap;
      }
      this._sahne?.add(this.icDuvarMesh);
    }
  }

  /** Malzeme presetini aktif dexel mesh'e uygula (manuel override destekli) */
  private malzemePresetUygula(): void {
    if (!this.dexelMesh) return;
    const preset = this.MALZEME_PRESETLERI[this.malzemePreset] || this.MALZEME_PRESETLERI['aluminum'];
    const mat = this.dexelMesh.material as THREE.MeshPhysicalMaterial;
    if (!mat) return;
    mat.roughness = this.manuelRoughness >= 0 ? this.manuelRoughness / 100 : preset.roughness;
    mat.metalness = this.manuelMetalness >= 0 ? this.manuelMetalness / 100 : preset.metalness;
    mat.clearcoat = this.manuelClearcoat >= 0 ? this.manuelClearcoat / 100 : preset.clearcoat;
    mat.clearcoatRoughness = preset.clearcoatRoughness;
    mat.envMapIntensity = this.manuelEnvMapIntensity >= 0 ? this.manuelEnvMapIntensity / 100 : preset.envMapIntensity;
    mat.needsUpdate = true;
  }

  /** Malzeme preset değiştiğinde çağrılır */
  tiklandiMalzemePreset(preset: string): void {
    this.malzemePreset = preset;
    // Manuel override'ları sıfırla (preset değerleri kullanılsın)
    this.manuelRoughness = -1;
    this.manuelMetalness = -1;
    this.manuelClearcoat = -1;
    this.manuelEnvMapIntensity = -1;
    const presetData = this.MALZEME_PRESETLERI[preset];
    if (presetData) {
      this.kutukStokRenk = presetData.renk;
      this._kutukHamRenk.set(presetData.renk);
    }
    this.malzemePresetUygula();
    // Mesh yeniden oluştur (AO + renk güncelleme)
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
    this.CD.detectChanges();
  }

  /**
   * Kenar çizgileri oluştur - keskin yükseklik geçişlerinde ince koyu çizgiler
   * CATIA benzeri edge line rendering
   */
  private kenarCizgileriOlustur(): void {
    if (!this.dexelEngine || !this.dexelMesh) return;

    // Önceki çizgileri temizle
    if (this.kenarCizgileriMesh) {
      this._sahne?.remove(this.kenarCizgileriMesh);
      this.kenarCizgileriMesh.geometry?.dispose();
      (this.kenarCizgileriMesh.material as THREE.Material)?.dispose();
    }

    const { cellSize } = this.dexelEngine.getResolution();
    const threshold = cellSize * (0.5 + this.kenarCizgisiEsik / 25);

    // Engine API: kenar segment'lerini direkt Float32Array olarak al
    const edgePositions = this.dexelEngine.extractEdgeSegments(threshold);
    if (edgePositions.length === 0) return;

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(edgePositions, 3));

    const mat = new THREE.LineBasicMaterial({
      color: 0x0a0a0a,
      transparent: true,
      opacity: this.kenarCizgisiOpaklik / 100,
      linewidth: 1,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    this.kenarCizgileriMesh = new THREE.LineSegments(geo, mat);
    this.kenarCizgileriMesh.name = 'DexelEdgeLines';
    this.kenarCizgileriMesh.renderOrder = 51;
    this._sahne?.add(this.kenarCizgileriMesh);
  }

  /** Kenar çizgileri toggle */
  tiklandiKenarCizgileriToggle(): void {
    this.kenarCizgileriAktif = !this.kenarCizgileriAktif;
    if (this.kenarCizgileriAktif) {
      this.kenarCizgileriOlustur();
    } else if (this.kenarCizgileriMesh) {
      this._sahne?.remove(this.kenarCizgileriMesh);
      this.kenarCizgileriMesh.geometry?.dispose();
      (this.kenarCizgileriMesh.material as THREE.Material)?.dispose();
      this.kenarCizgileriMesh = null;
    }
    this._sahne?.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  /** AO yoğunluğu değiştiğinde mesh yeniden oluştur */
  aoYogunlukDegisti(): void {
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
  }

  /** AO radius değiştiğinde mesh yeniden oluştur */
  aoRadiusDegisti(): void {
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
  }

  /** Manuel PBR parametresi değiştiğinde materyal güncelle */
  manuelPBRDegisti(): void {
    if (!this.dexelMesh) return;
    const mat = this.dexelMesh.material as THREE.MeshPhysicalMaterial;
    if (!mat) return;
    const preset = this.MALZEME_PRESETLERI[this.malzemePreset] || this.MALZEME_PRESETLERI['aluminum'];
    mat.roughness = this.manuelRoughness >= 0 ? this.manuelRoughness / 100 : preset.roughness;
    mat.metalness = this.manuelMetalness >= 0 ? this.manuelMetalness / 100 : preset.metalness;
    mat.clearcoat = this.manuelClearcoat >= 0 ? this.manuelClearcoat / 100 : preset.clearcoat;
    mat.envMapIntensity = this.manuelEnvMapIntensity >= 0 ? this.manuelEnvMapIntensity / 100 : preset.envMapIntensity;
    mat.needsUpdate = true;
    this._sahne?.anaGosterim?.ciz();
  }

  /** Kenar çizgisi opaklığı değiştiğinde güncelle */
  kenarOpaklikDegisti(): void {
    if (this.kenarCizgileriMesh) {
      (this.kenarCizgileriMesh.material as THREE.LineBasicMaterial).opacity = this.kenarCizgisiOpaklik / 100;
      (this.kenarCizgileriMesh.material as THREE.LineBasicMaterial).needsUpdate = true;
      this._sahne?.anaGosterim?.ciz();
    }
  }

  /** Kenar eşiği değiştiğinde çizgileri yeniden oluştur */
  kenarEsikDegisti(): void {
    if (this.kenarCizgileriAktif) {
      this.kenarCizgileriOlustur();
      this._sahne?.anaGosterim?.ciz();
    }
  }

  /** Tone mapping exposure değiştiğinde renderer güncelle */
  toneMappingExposureDegisti(): void {
    const renderer = this._sahne?.anaGosterim?.sunucu as THREE.WebGLRenderer;
    if (renderer) {
      renderer.toneMappingExposure = this.toneMappingExposure / 100;
      this._sahne?.anaGosterim?.ciz();
    }
  }

  /** İç duvar toggle - mesh yeniden oluştur */
  tiklandiIcDuvarToggle(): void {
    this.icDuvarAktif = !this.icDuvarAktif;
    if (this.icDuvarAktif) {
      if (this.dexelEngine) this.icDuvarOlustur();
    } else if (this.icDuvarMesh) {
      this._sahne?.remove(this.icDuvarMesh);
      this.icDuvarMesh.geometry?.dispose();
      (this.icDuvarMesh.material as THREE.Material)?.dispose();
      this.icDuvarMesh = null;
    }
    this._sahne?.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  /** Kalite preseti değiştiğinde ilgili parametreleri toplu ayarla */
  tiklandiKalitePreseti(preset: string): void {
    this.kalitePreseti = preset;
    switch (preset) {
      case 'performance':
        this.dexelSmoothing = 2;
        this.dexelSmoothSigma = 0;
        this.aoYogunluk = 50;
        this.aoRadius = 3;
        this.kenarCizgileriAktif = false;
        this.icDuvarAktif = false;
        this.meshGuncellemeAraligi = 2;
        this.snapshotAraligi = 50;
        break;
      case 'balanced':
        this.dexelSmoothing = 3;
        this.dexelSmoothSigma = 0;
        this.aoYogunluk = 65;
        this.aoRadius = 5;
        this.kenarCizgileriAktif = false;
        this.icDuvarAktif = false;
        this.meshGuncellemeAraligi = 1;
        this.snapshotAraligi = 25;
        break;
      case 'quality':
        this.dexelSmoothing = 4;
        this.dexelSmoothSigma = 0;
        this.aoYogunluk = 80;
        this.aoRadius = 7;
        this.kenarCizgileriAktif = false;
        this.icDuvarAktif = false;
        this.meshGuncellemeAraligi = 1;
        this.snapshotAraligi = 15;
        break;
    }
    // Kenar çizgileri temizle (gerekirse)
    if (!this.kenarCizgileriAktif && this.kenarCizgileriMesh) {
      this._sahne?.remove(this.kenarCizgileriMesh);
      this.kenarCizgileriMesh.geometry?.dispose();
      (this.kenarCizgileriMesh.material as THREE.Material)?.dispose();
      this.kenarCizgileriMesh = null;
    }
    // Mesh yeniden oluştur
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelMeshOlustur();
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
    this.CD.detectChanges();
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // İLERİ/GERİ SARMA (SEEKING) SİSTEMİ
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Voxel CSG pozisyon işleme (NEW!)
   * Dexel gibi snapshot yok (şimdilik), sadece ileri işleme
   */
  private _voxelBackwardPending = false;
  private _voxelBackwardTarget = -1;

  private handleVoxelProcessing(yeniPoz: number): void {
    if (!this.voxelEngine || (this.kutukSimModu !== 'voxel' && this.kutukSimModu !== 'marchingcubes') || !this.voxelEngineReady) return;

    const eskiPoz = this.lastProcessedSimPoz;

    // İlk çağrı - sadece pozisyonu kaydet, mesh zaten initVoxelEngine'de oluşturuldu
    if (eskiPoz < 0) {
      this.lastProcessedSimPoz = yeniPoz;
      if (!this.voxelMesh) {
        this.voxelMeshOlustur();
      }
      return;
    }

    // Aynı pozisyon - skip
    if (yeniPoz === eskiPoz) return;

    if (yeniPoz > eskiPoz) {
      // İleri sarma - mevcut state üzerine devam et
      this.processVoxelPositionRange(eskiPoz, yeniPoz);
      this.lastProcessedSimPoz = yeniPoz;
    } else {
      // Geri sarma - rAF ile throttle (her frame max 1 reset)
      this._voxelBackwardTarget = yeniPoz;
      if (!this._voxelBackwardPending) {
        this._voxelBackwardPending = true;
        requestAnimationFrame(() => {
          this._voxelBackwardPending = false;
          const target = this._voxelBackwardTarget;
          if (target < 0 || !this.voxelEngine) return;

          this.voxelEngine.reset();
          this.voxelEngine.initializeRectangularStock();
          this.voxelOncekiPoz = null;
          this.voxelGuncellemeSayac = 0;

          if (this.useGPUVoxel && this.voxelGPUCompute) {
            this.voxelGPUCompute.initializeSolid();
          }

          if (target > 0) {
            this.processVoxelPositionRange(0, target);
          }
          this.lastProcessedSimPoz = target;
          this.voxelMeshOlustur();
        });
      }
    }
  }

  /**
   * Throttled voxel mesh update - max 1 rebuild per animation frame
   */
  private voxelMeshUpdateThrottled(): void {
    if (this.voxelMeshUpdatePending) return;
    this.voxelMeshUpdatePending = true;
    requestAnimationFrame(() => {
      this.voxelMeshUpdatePending = false;
      this.voxelMeshOlustur();
    });
  }

  /**
   * Voxel pozisyon aralığını işle (GPU batch veya CPU octree)
   */
  private processVoxelPositionRange(startPoz: number, endPoz: number): void {
    if (!this.voxelEngine || !this.noktalar?.length) return;

    let processedCount = 0;

    // Pre-allocate tool object
    const _tool: VoxelToolGeometry = {
      type: 'flat',
      diameter: 10,
      cornerRadius: 0,
      length: 30
    };
    let _cachedTakim: any = null;
    let prevX = NaN, prevY = NaN, prevZ = NaN;
    let hadPrev = false;

    // GPU batch mode: collect swept segments into pre-allocated Float32Array
    const useGPUBatch = this.useGPUVoxel && this.voxelGPUCompute;
    // 6 floats per segment: ax,ay,az,bx,by,bz (reuse class-level buffer)
    const gpuPositions = useGPUBatch ? this.voxelGPUBatchPositions : null;
    let gpuBatchCount = 0;

    for (let p = startPoz; p <= endPoz; p++) {
      const nokta = this.noktalar[p];
      if (!nokta) continue;

      const hareket = this.hareketTuruAl(nokta);
      const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME' || hareket === 'Isleme';
      if (!isIsleme) {
        hadPrev = false;
        continue;
      }

      const takim = nokta.takim;
      if (!takim) continue;

      if (takim !== _cachedTakim) {
        // Flush GPU batch if tool changes
        if (useGPUBatch && gpuBatchCount > 0) {
          this.voxelGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
          gpuBatchCount = 0;
        }

        _tool.diameter = takim.cap || 10;
        _tool.cornerRadius = takim.radius || 0;
        _tool.length = takim.kesmeBoy || takim.boy || 50;

        const takimTipi = ((takim as any)?.takimTipi || '').toLowerCase();
        const halfDia = _tool.diameter / 2;
        if (takimTipi.includes('ball') || takimTipi.includes('nose')
            || (_tool.cornerRadius > 0 && _tool.cornerRadius >= halfDia - 0.01)) {
          _tool.type = 'ball';
        } else if (_tool.cornerRadius > 0 && _tool.cornerRadius < halfDia) {
          _tool.type = 'bull';
        } else {
          _tool.type = 'flat';
        }

        _cachedTakim = takim;
      }

      if (useGPUBatch) {
        // GPU batch: collect swept segments
        if (hadPrev) {
          const base = gpuBatchCount * 6;
          gpuPositions![base] = prevX;
          gpuPositions![base + 1] = prevY;
          gpuPositions![base + 2] = prevZ;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          // Flush when batch is full (256 max)
          if (gpuBatchCount >= 256) {
            this.voxelGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        } else {
          // Single point: A==B
          const base = gpuBatchCount * 6;
          gpuPositions![base] = nokta.x;
          gpuPositions![base + 1] = nokta.y;
          gpuPositions![base + 2] = nokta.z;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          if (gpuBatchCount >= 256) {
            this.voxelGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        }
      } else {
        // CPU octree path
        if (hadPrev) {
          this.voxelEngine.removeSweptMaterial(prevX, prevY, prevZ, nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        } else {
          this.voxelEngine.removeMaterial(nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        }
      }

      prevX = nokta.x; prevY = nokta.y; prevZ = nokta.z;
      hadPrev = true;
      processedCount++;
      this.voxelGuncellemeSayac++;
    }

    // Flush remaining GPU batch
    if (useGPUBatch && gpuBatchCount > 0) {
      this.voxelGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
    }

    // Mesh update: GPU mode uses time-throttled readback, CPU uses rAF throttle
    if (useGPUBatch && processedCount > 0) {
      this.voxelGPUDirty = true;
      this.scheduleVoxelGPUMeshUpdate();
    } else if (processedCount > 0) {
      this.voxelMeshUpdateThrottled();
    }
  }

  /**
   * Time-throttled GPU mesh update scheduler.
   * During playback, GPU dispatches happen every 60ms but readback+mesh gen
   * takes ~150-300ms. This ensures we only do readback every VOXEL_GPU_MESH_INTERVAL ms,
   * letting GPU dispatches accumulate between mesh updates.
   */
  private scheduleVoxelGPUMeshUpdate(): void {
    // Already scheduled or running - skip
    if (this.voxelGPUMeshTimerId || this.voxelGPUMeshUpdatePending) return;

    this.voxelGPUMeshTimerId = setTimeout(() => {
      this.voxelGPUMeshTimerId = null;
      if (this.voxelGPUDirty) {
        this.voxelGPUDirty = false;
        this.voxelGPUMeshUpdate();
      }
    }, this.simGPUMeshInterval);
  }

  /**
   * GPU mode: readback voxel data and generate mesh from flat grid.
   * Called by scheduleVoxelGPUMeshUpdate (time-throttled).
   * Pending flag prevents concurrent readback (mapAsync conflict).
   */
  private voxelGPUMeshUpdatePending = false;
  private async voxelGPUMeshUpdate(): Promise<void> {
    if (this.voxelGPUMeshUpdatePending) return;
    if (!this.voxelGPUCompute || !this._sahne) return;
    this.voxelGPUMeshUpdatePending = true;

    try {
      // Flush GPU queue - ensures all dispatches are complete
      await this.voxelGPUCompute.flush();

      let geometry: THREE.BufferGeometry | null = null;

      // Marching Cubes GPU mode: generate mesh entirely on GPU (zero readback for voxels)
      if (this.kutukSimModu === 'marchingcubes' && this.marchingCubesGPU) {
        geometry = await this.marchingCubesGPU.generateMesh();
      } else {
        // Standard voxel mode: readback + CPU surface extraction
        const voxelData = await this.voxelGPUCompute.readVoxelData();
        if (!voxelData) return;

        const gridInfo = this.voxelGPUCompute.getGridInfo();
        geometry = this.generateMeshFromFlatGrid(
          voxelData,
          gridInfo.resX, gridInfo.resY, gridInfo.resZ,
          gridInfo.voxelSize,
          gridInfo.origin
        );
      }

      if (!geometry) return;

      if (!this.voxelMesh) {
        const material = new THREE.MeshStandardMaterial({
          color: 0xaaaaaa,
          metalness: 0.2,
          roughness: 0.8,
          side: THREE.DoubleSide,
        });
        this.voxelMesh = new THREE.Mesh(geometry, material);
        this.voxelMesh.name = 'VoxelStockMesh';
        this._sahne.add(this.voxelMesh);
      } else {
        const oldGeo = this.voxelMesh.geometry;
        this.voxelMesh.geometry = geometry;
        oldGeo.dispose();
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this._sahne?.anaGosterim?.ciz();

    } catch (e) {
      console.error('[VoxelGPU] Mesh update failed:', e);
    } finally {
      this.voxelGPUMeshUpdatePending = false;
      // If more GPU work happened during readback, re-schedule
      if (this.voxelGPUDirty) {
        this.scheduleVoxelGPUMeshUpdate();
      }
    }
  }

  /**
   * Flat 3D grid'den yüzey mesh'i oluştur (neighbor-check face emission)
   * Sadece yüzeydeki voxeller için face çıkarır (iç faces skip edilir)
   * Pre-allocated typed arrays ile optimize edilmiş (zero dynamic push)
   */
  private generateMeshFromFlatGrid(
    voxels: Uint8Array,
    resX: number, resY: number, resZ: number,
    voxelSize: number,
    origin: [number, number, number]
  ): THREE.BufferGeometry | null {
    const resXY = resX * resY;

    // Pass 1: Count surface faces for pre-allocation
    let faceCount = 0;
    for (let z = 0; z < resZ; z++) {
      for (let y = 0; y < resY; y++) {
        for (let x = 0; x < resX; x++) {
          const idx = x + y * resX + z * resXY;
          if (voxels[idx] === 0) continue;
          if (x === resX - 1 || voxels[idx + 1] === 0) faceCount++;
          if (x === 0 || voxels[idx - 1] === 0) faceCount++;
          if (y === resY - 1 || voxels[idx + resX] === 0) faceCount++;
          if (y === 0 || voxels[idx - resX] === 0) faceCount++;
          if (z === resZ - 1 || voxels[idx + resXY] === 0) faceCount++;
          if (z === 0 || voxels[idx - resXY] === 0) faceCount++;
        }
      }
    }

    if (faceCount === 0) return null;

    // Pre-allocate: 6 vertices per face, 3 floats per vertex
    const vertexCount = faceCount * 6;
    const posArr = new Float32Array(vertexCount * 3);
    const norArr = new Float32Array(vertexCount * 3);
    let vi = 0; // vertex write index (in floats)

    for (let z = 0; z < resZ; z++) {
      const oz = origin[2] + z * voxelSize;
      const oz1 = oz + voxelSize;
      for (let y = 0; y < resY; y++) {
        const oy = origin[1] + y * voxelSize;
        const oy1 = oy + voxelSize;
        for (let x = 0; x < resX; x++) {
          const idx = x + y * resX + z * resXY;
          if (voxels[idx] === 0) continue;

          const ox = origin[0] + x * voxelSize;
          const ox1 = ox + voxelSize;

          // +X face
          if (x === resX - 1 || voxels[idx + 1] === 0) {
            posArr[vi]=ox1; posArr[vi+1]=oy; posArr[vi+2]=oz;
            posArr[vi+3]=ox1; posArr[vi+4]=oy1; posArr[vi+5]=oz;
            posArr[vi+6]=ox1; posArr[vi+7]=oy1; posArr[vi+8]=oz1;
            posArr[vi+9]=ox1; posArr[vi+10]=oy; posArr[vi+11]=oz;
            posArr[vi+12]=ox1; posArr[vi+13]=oy1; posArr[vi+14]=oz1;
            posArr[vi+15]=ox1; posArr[vi+16]=oy; posArr[vi+17]=oz1;
            norArr[vi]=1; norArr[vi+1]=0; norArr[vi+2]=0;
            norArr[vi+3]=1; norArr[vi+4]=0; norArr[vi+5]=0;
            norArr[vi+6]=1; norArr[vi+7]=0; norArr[vi+8]=0;
            norArr[vi+9]=1; norArr[vi+10]=0; norArr[vi+11]=0;
            norArr[vi+12]=1; norArr[vi+13]=0; norArr[vi+14]=0;
            norArr[vi+15]=1; norArr[vi+16]=0; norArr[vi+17]=0;
            vi += 18;
          }
          // -X face
          if (x === 0 || voxels[idx - 1] === 0) {
            posArr[vi]=ox; posArr[vi+1]=oy; posArr[vi+2]=oz;
            posArr[vi+3]=ox; posArr[vi+4]=oy1; posArr[vi+5]=oz1;
            posArr[vi+6]=ox; posArr[vi+7]=oy1; posArr[vi+8]=oz;
            posArr[vi+9]=ox; posArr[vi+10]=oy; posArr[vi+11]=oz;
            posArr[vi+12]=ox; posArr[vi+13]=oy; posArr[vi+14]=oz1;
            posArr[vi+15]=ox; posArr[vi+16]=oy1; posArr[vi+17]=oz1;
            norArr[vi]=-1; norArr[vi+1]=0; norArr[vi+2]=0;
            norArr[vi+3]=-1; norArr[vi+4]=0; norArr[vi+5]=0;
            norArr[vi+6]=-1; norArr[vi+7]=0; norArr[vi+8]=0;
            norArr[vi+9]=-1; norArr[vi+10]=0; norArr[vi+11]=0;
            norArr[vi+12]=-1; norArr[vi+13]=0; norArr[vi+14]=0;
            norArr[vi+15]=-1; norArr[vi+16]=0; norArr[vi+17]=0;
            vi += 18;
          }
          // +Y face
          if (y === resY - 1 || voxels[idx + resX] === 0) {
            posArr[vi]=ox; posArr[vi+1]=oy1; posArr[vi+2]=oz;
            posArr[vi+3]=ox1; posArr[vi+4]=oy1; posArr[vi+5]=oz;
            posArr[vi+6]=ox1; posArr[vi+7]=oy1; posArr[vi+8]=oz1;
            posArr[vi+9]=ox; posArr[vi+10]=oy1; posArr[vi+11]=oz;
            posArr[vi+12]=ox1; posArr[vi+13]=oy1; posArr[vi+14]=oz1;
            posArr[vi+15]=ox; posArr[vi+16]=oy1; posArr[vi+17]=oz1;
            norArr[vi]=0; norArr[vi+1]=1; norArr[vi+2]=0;
            norArr[vi+3]=0; norArr[vi+4]=1; norArr[vi+5]=0;
            norArr[vi+6]=0; norArr[vi+7]=1; norArr[vi+8]=0;
            norArr[vi+9]=0; norArr[vi+10]=1; norArr[vi+11]=0;
            norArr[vi+12]=0; norArr[vi+13]=1; norArr[vi+14]=0;
            norArr[vi+15]=0; norArr[vi+16]=1; norArr[vi+17]=0;
            vi += 18;
          }
          // -Y face
          if (y === 0 || voxels[idx - resX] === 0) {
            posArr[vi]=ox; posArr[vi+1]=oy; posArr[vi+2]=oz;
            posArr[vi+3]=ox1; posArr[vi+4]=oy; posArr[vi+5]=oz1;
            posArr[vi+6]=ox1; posArr[vi+7]=oy; posArr[vi+8]=oz;
            posArr[vi+9]=ox; posArr[vi+10]=oy; posArr[vi+11]=oz;
            posArr[vi+12]=ox; posArr[vi+13]=oy; posArr[vi+14]=oz1;
            posArr[vi+15]=ox1; posArr[vi+16]=oy; posArr[vi+17]=oz1;
            norArr[vi]=0; norArr[vi+1]=-1; norArr[vi+2]=0;
            norArr[vi+3]=0; norArr[vi+4]=-1; norArr[vi+5]=0;
            norArr[vi+6]=0; norArr[vi+7]=-1; norArr[vi+8]=0;
            norArr[vi+9]=0; norArr[vi+10]=-1; norArr[vi+11]=0;
            norArr[vi+12]=0; norArr[vi+13]=-1; norArr[vi+14]=0;
            norArr[vi+15]=0; norArr[vi+16]=-1; norArr[vi+17]=0;
            vi += 18;
          }
          // +Z face
          if (z === resZ - 1 || voxels[idx + resXY] === 0) {
            posArr[vi]=ox; posArr[vi+1]=oy; posArr[vi+2]=oz1;
            posArr[vi+3]=ox1; posArr[vi+4]=oy; posArr[vi+5]=oz1;
            posArr[vi+6]=ox1; posArr[vi+7]=oy1; posArr[vi+8]=oz1;
            posArr[vi+9]=ox; posArr[vi+10]=oy; posArr[vi+11]=oz1;
            posArr[vi+12]=ox1; posArr[vi+13]=oy1; posArr[vi+14]=oz1;
            posArr[vi+15]=ox; posArr[vi+16]=oy1; posArr[vi+17]=oz1;
            norArr[vi]=0; norArr[vi+1]=0; norArr[vi+2]=1;
            norArr[vi+3]=0; norArr[vi+4]=0; norArr[vi+5]=1;
            norArr[vi+6]=0; norArr[vi+7]=0; norArr[vi+8]=1;
            norArr[vi+9]=0; norArr[vi+10]=0; norArr[vi+11]=1;
            norArr[vi+12]=0; norArr[vi+13]=0; norArr[vi+14]=1;
            norArr[vi+15]=0; norArr[vi+16]=0; norArr[vi+17]=1;
            vi += 18;
          }
          // -Z face
          if (z === 0 || voxels[idx - resXY] === 0) {
            posArr[vi]=ox; posArr[vi+1]=oy; posArr[vi+2]=oz;
            posArr[vi+3]=ox1; posArr[vi+4]=oy1; posArr[vi+5]=oz;
            posArr[vi+6]=ox1; posArr[vi+7]=oy; posArr[vi+8]=oz;
            posArr[vi+9]=ox; posArr[vi+10]=oy; posArr[vi+11]=oz;
            posArr[vi+12]=ox; posArr[vi+13]=oy1; posArr[vi+14]=oz;
            posArr[vi+15]=ox1; posArr[vi+16]=oy1; posArr[vi+17]=oz;
            norArr[vi]=0; norArr[vi+1]=0; norArr[vi+2]=-1;
            norArr[vi+3]=0; norArr[vi+4]=0; norArr[vi+5]=-1;
            norArr[vi+6]=0; norArr[vi+7]=0; norArr[vi+8]=-1;
            norArr[vi+9]=0; norArr[vi+10]=0; norArr[vi+11]=-1;
            norArr[vi+12]=0; norArr[vi+13]=0; norArr[vi+14]=-1;
            norArr[vi+15]=0; norArr[vi+16]=0; norArr[vi+17]=-1;
            vi += 18;
          }
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(norArr, 3));

    return geometry;
  }

  /**
   * Simülasyon pozisyonu değiştiğinde dexel state'i güncelle (ileri/geri sarma)
   * @param yeniPoz Yeni hedef pozisyon
   */
  private handleDexelSeek(yeniPoz: number): void {
    if (!this.dexelEngine || this.kutukSimModu !== 'dexel') return;

    const eskiPoz = this.lastProcessedSimPoz;

    // İlk çağrı - henüz işlenmiş pozisyon yok
    if (eskiPoz < 0) {
      this.lastProcessedSimPoz = yeniPoz;
      return;
    }

    // Aynı pozisyon - işlem yok
    if (yeniPoz === eskiPoz) return;

    // ═══════════════════════════════════════════════════════════════════════
    // İLERİ SARMA: Eski pozisyondan yeniye kadar işle
    // ═══════════════════════════════════════════════════════════════════════
    if (yeniPoz > eskiPoz) {
      // İleri sarma - mevcut state üzerine devam et
      this.processPositionRange(eskiPoz, yeniPoz);

      // Snapshot al (her snapshotAraligi pozisyonda bir)
      if (yeniPoz - this.lastSnapshotPoz >= this.snapshotAraligi) {
        this.captureSnapshot(yeniPoz);
      }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // GERİ SARMA: En yakın snapshot'ı bul, restore et, oradan hedefe işle
    // ═══════════════════════════════════════════════════════════════════════
    else {
      // geriSarmaDoldur kapalıysa: dexel state'i dokunma, sadece pozisyonu takip et
      if (!this.geriSarmaDoldur) {
        this.lastProcessedSimPoz = yeniPoz;
        return;
      }

      // En yakın snapshot'ı bul (hedef pozisyondan küçük veya eşit)
      const snapshotPoz = this.findNearestSnapshot(yeniPoz);

      if (snapshotPoz >= 0 && this.dexelSnapshots.has(snapshotPoz)) {
        // Snapshot'ı restore et
        const snapshot = this.dexelSnapshots.get(snapshotPoz)!;
        this.dexelEngine.restoreSnapshot(snapshot);
        this.dexelOncekiPoz = null; // Önceki pozisyonu sıfırla

        // Snapshot'tan hedefe kadar işle
        if (snapshotPoz < yeniPoz) {
          this.processPositionRange(snapshotPoz, yeniPoz);
        }
      } else {
        // Snapshot yok - sıfırdan başla
        this.dexelEngine.resetToInitialState();
        this.dexelOncekiPoz = null;
        this.dexelSnapshots.clear();
        this.lastSnapshotPoz = -1;

        // Baştan hedefe kadar işle
        if (yeniPoz > 0) {
          this.processPositionRange(0, yeniPoz);
        }
      }
    }

    this.lastProcessedSimPoz = yeniPoz;

    // Mesh güncelle
    this.requestThrottledMeshUpdate();
  }

  /**
   * Belirli bir pozisyon aralığını işle (start → end)
   */
  private processPositionRange(startPoz: number, endPoz: number): void {
    if (!this.dexelEngine || !this.noktalar?.length) return;

    const step = startPoz < endPoz ? 1 : -1;

    // ════════════════════════════════════════════════════════════════════════════
    // PERFORMANCE: Pre-allocate tool object + cache tool geometry per tool ref
    // Eski: her pozisyonda tool object alloc + toLowerCase + regex → N× overhead
    // Yeni: tool değişince 1× hesapla, object reuse → ~0 overhead
    // ════════════════════════════════════════════════════════════════════════════
    const _tool: any = { type: 'flat', diameter: 10, cornerRadius: 0, length: 30 };
    const _sample = { x: 0, y: 0, z: 0, i: 0, j: 0, k: 0 };
    let _cachedTakim: any = null;

    for (let p = startPoz; step > 0 ? p <= endPoz : p >= endPoz; p += step) {
      const nokta = this.noktalar[p];
      if (!nokta) continue;

      // Sadece işleme hareketlerini işle
      const hareket = this.hareketTuruAl(nokta);
      const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME' || hareket === 'Isleme';
      if (!isIsleme) {
        // Rapid harekette dexelOncekiPoz güncelle - swept move boşluğu önle
        if (nokta.x != null && nokta.y != null && nokta.z != null) {
          if (!this.dexelOncekiPoz) this.dexelOncekiPoz = new THREE.Vector3();
          this.dexelOncekiPoz.set(nokta.x, nokta.y, nokta.z);
        }
        continue;
      }

      const takim = nokta.takim;
      if (!takim) continue;

      // Takım ID güncelle (seek sırasında da takım değişikliğini takip et)
      if (takim !== this.sonTakimRef) {
        this.sonTakimRef = takim;
        if (!takim._takimRenklendirmeId) {
          this.takimIdSayac++;
          takim._takimRenklendirmeId = this.takimIdSayac;
          const takimAdi = takim.name || takim.ad || `T${this.takimIdSayac}`;
          const tc = takim.cap || 0;
          this.takimIsimHaritasi.set(this.takimIdSayac, tc > 0 ? `${takimAdi} (Ø${tc})` : takimAdi);
          const paletIdx = (this.takimIdSayac - 1) % this.TAKIM_RENK_PALETI.length;
          this.takimRenkHaritasi.set(this.takimIdSayac, new THREE.Color(this.TAKIM_RENK_PALETI[paletIdx]));
        }
        this.aktifTakimId = takim._takimRenklendirmeId;
        this.dexelEngine.setCurrentToolId(this.aktifTakimId);
      }

      // Takım pozisyonu ve yönü (pre-allocated - loop hot path)
      const takimPoz = this._kutukTempVec1.set(nokta.x, nokta.y, nokta.z);
      const takimYonu = this._kutukTempVec2;
      if (nokta.takimYonu) {
        takimYonu.set(nokta.takimYonu.x, nokta.takimYonu.y, nokta.takimYonu.z);
        if (takimYonu.lengthSq() < 0.0001) { takimYonu.set(0, 0, 1); }
        else { takimYonu.normalize(); }
      } else { takimYonu.set(0, 0, 1); }

      // Ters yön modu: takım yönünü tersine çevir
      if (this.dexelTersYon) {
        takimYonu.negate();
      }

      // ════════════════════════════════════════════════════════════════════════════
      // PERFORMANCE: Cache tool geometry per tool reference
      // Tool type + cornerRadius + diameter only recomputed when takim changes
      // Eliminates: per-position toLowerCase, includes, regex, object alloc
      // ════════════════════════════════════════════════════════════════════════════
      if (takim !== _cachedTakim) {
        _cachedTakim = takim;
        const takimCap = takim.cap || 10;
        _tool.diameter = takimCap;
        _tool.length = takim.boy || takimCap * 3;   // kesici boyu (lc) — yoksa 3×çap

        const takimRadius = takim?.radius || 0;
        const takimTipi = (takim as any)?.takimTipi || '';

        let toolType: 'flat' | 'ball' | 'bull' = 'flat';
        let cornerRadius = takimRadius;

        if (takimTipi.toLowerCase().includes('ball') || takimTipi.toLowerCase().includes('nose')) {
          toolType = 'ball';
          cornerRadius = takimCap / 2;
        } else if (takimRadius >= takimCap / 2 - 0.5) {
          toolType = 'ball';
          cornerRadius = takimCap / 2;
        } else if (takimRadius > 0) {
          toolType = 'bull';
          cornerRadius = takimRadius;
        } else {
          const takimAdi = (takim as any)?.name || (takim as any)?.ad || '';
          const operasyonAdi = (nokta as any)?.operasyonAdi || '';
          const tumIsimler = `${takimAdi} ${operasyonAdi}`.toUpperCase();
          const rMatch = tumIsimler.match(/R\s*(\d+(?:\.\d+)?)/i);
          if (rMatch) {
            const rValue = parseFloat(rMatch[1]);
            if (rValue >= takimCap / 2 - 1) {
              toolType = 'ball';
              cornerRadius = takimCap / 2;
            } else if (rValue > 0) {
              toolType = 'bull';
              cornerRadius = rValue;
            }
          }
        }

        _tool.type = toolType;
        _tool.cornerRadius = cornerRadius;
      }

      // İşle (pre-allocated _sample for single position case)
      if (this.dexelOncekiPoz) {
        this.dexelEngine.processSweptMove(
          this.dexelOncekiPoz,
          takimPoz,
          takimYonu,
          _tool
        );
      } else {
        _sample.x = takimPoz.x;
        _sample.y = takimPoz.y;
        _sample.z = takimPoz.z;
        _sample.i = takimYonu.x;
        _sample.j = takimYonu.y;
        _sample.k = takimYonu.z;
        this.dexelEngine.processToolPosition(_sample, _tool);
      }

      // Önceki pozisyonu güncelle
      if (!this.dexelOncekiPoz) {
        this.dexelOncekiPoz = new THREE.Vector3();
      }
      this.dexelOncekiPoz.copy(takimPoz);
    }
  }

  /**
   * Mevcut dexel state'ini snapshot olarak kaydet
   */
  private captureSnapshot(poz: number): void {
    if (!this.dexelEngine) return;

    const snapshot = this.dexelEngine.getSnapshot();
    this.dexelSnapshots.set(poz, snapshot);
    this.lastSnapshotPoz = poz;

    // Max snapshot sayısını kontrol et (memory management)
    if (this.dexelSnapshots.size > this.maxSnapshotSayisi) {
      // En eski snapshot'ı sil
      const keys = Array.from(this.dexelSnapshots.keys()).sort((a, b) => a - b);
      this.dexelSnapshots.delete(keys[0]);
    }
  }

  /**
   * Hedef pozisyondan küçük veya eşit en yakın snapshot'ı bul
   */
  private findNearestSnapshot(targetPoz: number): number {
    if (this.dexelSnapshots.size === 0) return -1;

    let nearestPoz = -1;
    for (const snapshotPoz of this.dexelSnapshots.keys()) {
      if (snapshotPoz <= targetPoz && snapshotPoz > nearestPoz) {
        nearestPoz = snapshotPoz;
      }
    }

    return nearestPoz;
  }

  /**
   * Tüm snapshot'ları temizle (UI butonu için)
   */
  snapshotlariTemizle(): void {
    this.dexelSnapshots.clear();
    this.lastSnapshotPoz = -1;
  }

  /** Renklendirme modunu değiştir (depth ↔ tool) ve mesh'i yeniden renklendir */
  tiklandiRenklendirmeModuDegistir(): void {
    this.kutukRenklendirmeModu = this.kutukRenklendirmeModu === 'depth' ? 'tool' : 'depth';
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
    this.CD.detectChanges();
  }

  /** Stok rengi değiştiğinde mesh'i güncelle + createStockMesh rengini değiştir */
  stokRenkDegisti(hex: string): void {
    this.kutukStokRenk = hex;
    this._kutukHamRenk.set(hex);

    // Mevcut mesh materyalinde initial vertex color'ları ve full update zorla
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
    this.CD.detectChanges();
  }

  /** Smoothing değiştiğinde mesh'i yeniden güncelle */
  dexelSmoothingDegisti(): void {
    if (this.dexelEngine && this.dexelMesh) {
      // Dirty count sıfır olsa bile full update tetikle
      this.dexelEngine.clearDirty();
      this.dexelMeshGuncelle();
    }
  }

  /**
   * Takım değişikliğini tespit et ve yeni ID ata
   * Her farklı takım objesine benzersiz bir ID verilir
   */
  private takimIdGuncelle(): void {
    const takim = this.aktifNokta?.takim;
    if (!takim) return;

    // Takım referansı değiştiyse yeni ID ata
    if (takim !== this.sonTakimRef) {
      this.sonTakimRef = takim;

      if (takim._takimRenklendirmeId > 0) {
        // Bu takım daha önce kayıtlı - mevcut ID'sini kullan
        this.aktifTakimId = takim._takimRenklendirmeId;
      } else {
        // Yeni takım - ID ata
        this.takimIdSayac++;
        this.aktifTakimId = this.takimIdSayac;
        takim._takimRenklendirmeId = this.aktifTakimId;

        const takimAdi = takim.name || takim.ad || `T${this.aktifTakimId}`;
        const takimCap = takim.cap || 0;
        this.takimIsimHaritasi.set(this.aktifTakimId, takimCap > 0 ? `${takimAdi} (Ø${takimCap})` : takimAdi);

        const paletIdx = (this.aktifTakimId - 1) % this.TAKIM_RENK_PALETI.length;
        this.takimRenkHaritasi.set(this.aktifTakimId, new THREE.Color(this.TAKIM_RENK_PALETI[paletIdx]));
      }

      if (this.dexelEngine) {
        this.dexelEngine.setCurrentToolId(this.aktifTakimId);
      }
    }
  }

  /**
   * Dexel subtraction işlemi - ENGINE'İN KENDİ SWEPT MOVE'UNU KULLAN
   * Engine zaten iyi interpolasyon yapıyor (cellSize * 0.5 step)
   */
  private kutukDexelSubtraction(takimCap: number, takimUzunluk: number) {
    if (!this.dexelEngine) return;

    // PERF: Önceki mesh güncelleme bitmemişse dexel işlemeyi atla
    // Rendering pipeline'ın tıkanmasını önler, stuttering azaltır
    if (this.meshUpdatePending) return;

    // Takım değişikliğini tespit et ve engine'e bildir
    this.takimIdGuncelle();

    const t0 = performance.now();

    // Şimdiki pozisyon ve takım yönü (copy to sim buffers, avoid clone alloc)
    const simdikiPoz = this._simTmpV1.copy(this._kutukTempVec1);
    const takimYonu = this._simTmpV2.copy(this._kutukTempVec2);

    // ═══════════════════════════════════════════════════════════════════════
    // TOOL GEOMETRY - Küresel/Bull/Flat takım algılama
    // FikoTakimFrezeleme yapısı: cap, radius (köşe yarıçapı), takimTipi
    // ═══════════════════════════════════════════════════════════════════════
    const takim = this.aktifNokta?.takim;

    // Doğrudan radius property'sini al (FikoTakimFrezeleme.radius)
    const takimRadius = takim?.radius || 0;
    const takimTipi = (takim as any)?.takimTipi || '';

    let toolType: 'flat' | 'ball' | 'bull' = 'flat';
    let cornerRadius = takimRadius;

    // 1. takimTipi 'BallNose' ise → BALL
    if (takimTipi.toLowerCase().includes('ball') || takimTipi.toLowerCase().includes('nose')) {
      toolType = 'ball';
      cornerRadius = takimCap / 2;
    }
    // 2. radius === cap/2 (veya yakın) → BALL
    else if (takimRadius >= takimCap / 2 - 0.5) {
      toolType = 'ball';
      cornerRadius = takimCap / 2;
    }
    // 3. radius > 0 → BULL
    else if (takimRadius > 0) {
      toolType = 'bull';
      cornerRadius = takimRadius;
    }
    // 4. Fallback: isimlerden regex ile bul
    else {
      const takimAdi = (takim as any)?.name || (takim as any)?.ad || '';
      const operasyonAdi = (this.aktifNokta as any)?.operasyonAdi || '';
      const tumIsimler = `${takimAdi} ${operasyonAdi}`.toUpperCase();
      const rMatch = tumIsimler.match(/R\s*(\d+(?:\.\d+)?)/i);
      if (rMatch) {
        const rValue = parseFloat(rMatch[1]);
        if (rValue >= takimCap / 2 - 1) {
          toolType = 'ball';
          cornerRadius = takimCap / 2;
        } else if (rValue > 0) {
          toolType = 'bull';
          cornerRadius = rValue;
        }
      }
    }

    const tool: DexelToolGeometry = {
      type: toolType,
      diameter: takimCap,
      cornerRadius: cornerRadius,
      length: takimUzunluk
    };

    // ═══════════════════════════════════════════════════════════════════════
    // PROCESSING: Worker → GPU → CPU fallback sırasıyla dene
    // Worker aktifse async olarak background thread'de işler (main thread serbest)
    // ═══════════════════════════════════════════════════════════════════════
    if (this.useWorkerDexel && this.dexelWorkerBridge && !this.workerPendingUpdate) {
      // ─── WORKER PATH: Main thread'i bloklamadan async işle ───
      this.workerPendingUpdate = true;
      const workerPromise = this.dexelOncekiPoz
        ? this.dexelWorkerBridge.processSweptMove(
            this.dexelOncekiPoz.x, this.dexelOncekiPoz.y, this.dexelOncekiPoz.z,
            simdikiPoz.x, simdikiPoz.y, simdikiPoz.z,
            takimYonu.x, takimYonu.y, takimYonu.z,
            tool
          )
        : this.dexelWorkerBridge.processBatch([{
            x: simdikiPoz.x, y: simdikiPoz.y, z: simdikiPoz.z,
            i: takimYonu.x, j: takimYonu.y, k: takimYonu.z
          }], tool);

      workerPromise.then((result: ProcessResult) => {
        this.workerPendingUpdate = false;
        if (result.dirtyHeights && result.dirtyHeights.indices.length > 0) {
          this.applyWorkerDirtyHeights(result.dirtyHeights);
        }
      }).catch(() => {
        this.workerPendingUpdate = false;
      });

      // CPU engine'i de snapshot için periyodik güncelle (her 10 pozisyonda)
      if (this.dexelGuncellemeSayac % 10 === 0) {
        if (this.dexelOncekiPoz) {
          this.dexelEngine.processSweptMove(this.dexelOncekiPoz, simdikiPoz, takimYonu, tool);
        } else {
          this.dexelEngine.processToolPosition({
            x: simdikiPoz.x, y: simdikiPoz.y, z: simdikiPoz.z,
            i: takimYonu.x, j: takimYonu.y, k: takimYonu.z
          }, tool);
        }
      }
      this.dexelGuncellemeSayac++;
    } else {
      // ─── CPU PATH: Senkron işle ───
      if (this.dexelOncekiPoz) {
        this.dexelEngine.processSweptMove(
          this.dexelOncekiPoz,
          simdikiPoz,
          takimYonu,
          tool
        );
      } else {
        this.dexelEngine.processToolPosition({
          x: simdikiPoz.x,
          y: simdikiPoz.y,
          z: simdikiPoz.z,
          i: takimYonu.x,
          j: takimYonu.y,
          k: takimYonu.z
        }, tool);
      }

      // Throttled mesh güncelleme talep et
      this.requestThrottledMeshUpdate();
    }

    // Önceki pozisyonu güncelle
    if (!this.dexelOncekiPoz) {
      this.dexelOncekiPoz = new THREE.Vector3();
    }
    this.dexelOncekiPoz.copy(simdikiPoz);

    this.perfMetrics.totalProcessTime += performance.now() - t0;
  }

  /**
   * Batch'teki tüm tool pozisyonlarını işle - FRAME BUDGET AWARE
   * WebGPU varsa GPU'da, yoksa CPU'da çalışır
   */
  private async flushToolPositionBatch(tool: DexelToolGeometry): Promise<void> {
    if (this.toolPositionBatch.length === 0) return;
    if (!this.dexelEngine && !this.dexelGPUEngine) return;

    const t0 = performance.now();
    const batchSize = this.toolPositionBatch.length;

    // WebGPU varsa GPU'da işle
    if (this.useGPUDexel && this.dexelGPUEngine) {
      const gpuSamples: GPUToolSample[] = this.toolPositionBatch.map(s => ({
        x: s.x, y: s.y, z: s.z,
        toolRadius: tool.diameter / 2,
        toolType: tool.type === 'ball' ? 1 : (tool.type === 'bull' ? 2 : 0),
        cornerRadius: tool.cornerRadius,
        toolAxisI: s.i,
        toolAxisJ: s.j,
        toolAxisK: s.k,
        toolLength: tool.length || tool.diameter * 3   // CPU engine (TripleDexelEngine) fallback'i ile aynı
      }));
      await this.dexelGPUEngine.processBatch(gpuSamples);

      // GPU sonuçlarını CPU engine'e de uygula (mesh için)
      if (this.dexelEngine) {
        this.dexelEngine.processToolPositionBatch(this.toolPositionBatch, tool);
      }
    } else if (this.dexelEngine) {
      // CPU fallback
      this.dexelEngine.processToolPositionBatch(this.toolPositionBatch, tool);
    }

    // Batch temizle
    this.toolPositionBatch.length = 0;

    // Metrikleri güncelle
    const elapsed = performance.now() - t0;
    this.perfMetrics.batchCount++;
    this.perfMetrics.avgBatchTime =
      (this.perfMetrics.avgBatchTime * (this.perfMetrics.batchCount - 1) + elapsed) /
      this.perfMetrics.batchCount;

    // Throttled mesh güncelleme talep et
    this.requestThrottledMeshUpdate();
  }

  /**
   * RAF-throttled mesh güncelleme - 60fps'te max 1 güncelleme
   */
  private requestThrottledMeshUpdate(): void {
    if (this.meshUpdatePending) return;

    const now = performance.now();
    const timeSinceLastUpdate = now - this.lastMeshUpdateTime;

    if (timeSinceLastUpdate >= this.MIN_MESH_UPDATE_INTERVAL) {
      // Hemen güncelle
      this.meshUpdatePending = true;
      this.meshUpdateRafId = requestAnimationFrame(() => {
        this.performMeshUpdate();
      });
    } else {
      // Bir sonraki frame'de güncelle
      this.meshUpdatePending = true;
      const delay = this.MIN_MESH_UPDATE_INTERVAL - timeSinceLastUpdate;
      setTimeout(() => {
        this.meshUpdateRafId = requestAnimationFrame(() => {
          this.performMeshUpdate();
        });
      }, delay);
    }
  }

  /**
   * Asıl mesh güncelleme işlemi
   */
  private performMeshUpdate(): void {
    if (!this.dexelEngine || !this.dexelMesh) {
      this.meshUpdatePending = false;
      return;
    }

    const t0 = performance.now();
    this.dexelMeshGuncelle();
    this.perfMetrics.meshUpdateTime += performance.now() - t0;

    this.lastMeshUpdateTime = performance.now();
    this.meshUpdatePending = false;
  }

  /**
   * Dexel mesh'i güncelle (height map'ten)
   * Vertex layout (createStockMesh'ten):
   * - Top surface: 0 to resX*resY-1
   * - Bottom surface: resX*resY to 2*resX*resY-1
   * - Front side: 2*resX*resY, alternating top/bottom for resX points
   * - Back side: 2*resX*resY + 2*resX
   * - Left side: 2*resX*resY + 4*resX
   * - Right side: 2*resX*resY + 4*resX + 2*resY
   */
  private dexelMeshGuncelle() {
    if (!this.dexelEngine || !this.dexelMesh) return;

    try {
      const { resX, resY } = this.dexelEngine.getResolution();
      const positions = this.dexelMesh.geometry.attributes.position.array as Float32Array;
      const dirtyCount = this.dexelEngine.getDirtyCount();
      const totalCells = resX * resY;

      // Layout offset'leri (engine ile paylaşılan sözleşme)
      const L = this._dexelLayout;
      const topStart = L ? L.topStart : 0;
      const bottomStart = L ? L.bottomStart : totalCells;
      const frontStart = L ? L.frontStart : totalCells * 2;
      const backStart = L ? L.backStart : frontStart + resX * 2;
      const leftStart = L ? L.leftStart : backStart + resX * 2;
      const rightStart = L ? L.rightStart : leftStart + resY * 2;

      // Incremental vs Full: dirty < %25 → incremental
      const useIncremental = dirtyCount > 0 && dirtyCount < totalCells * 0.25;
      let heightMap: Float32Array | null = null;

      if (useIncremental) {
        // ─── INCREMENTAL UPDATE: Sadece dirty cells ───
        const { indices, heights, bottoms, count } = this.dexelEngine.extractDirtyHeightUpdates();
        const dirtyBounds = this.dexelEngine.getDirtyBounds();

        // 1. Top + Bottom surface
        for (let i = 0; i < count; i++) {
          const idx = indices[i];
          positions[(topStart + idx) * 3 + 2] = heights[i];
          positions[(bottomStart + idx) * 3 + 2] = bottoms[i];
        }

        // 2. Side vertices - sınır kontrolü ile (Map allocation yok)
        // Front (iy=0)
        if (dirtyBounds.minY === 0) {
          for (let i = 0; i < count; i++) {
            const idx = indices[i];
            const ix = idx % resX;
            const iy = (idx / resX) | 0;
            if (iy === 0) {
              positions[(frontStart + ix * 2) * 3 + 2] = heights[i];
              positions[(frontStart + ix * 2 + 1) * 3 + 2] = bottoms[i];
            }
          }
        }
        // Back (iy=resY-1)
        if (dirtyBounds.maxY === resY - 1) {
          for (let i = 0; i < count; i++) {
            const idx = indices[i];
            const iy = (idx / resX) | 0;
            if (iy === resY - 1) {
              const ix = idx % resX;
              positions[(backStart + ix * 2) * 3 + 2] = heights[i];
              positions[(backStart + ix * 2 + 1) * 3 + 2] = bottoms[i];
            }
          }
        }
        // Left (ix=0)
        if (dirtyBounds.minX === 0) {
          for (let i = 0; i < count; i++) {
            const idx = indices[i];
            if (idx % resX === 0) {
              const iy = (idx / resX) | 0;
              positions[(leftStart + iy * 2) * 3 + 2] = heights[i];
              positions[(leftStart + iy * 2 + 1) * 3 + 2] = bottoms[i];
            }
          }
        }
        // Right (ix=resX-1)
        if (dirtyBounds.maxX === resX - 1) {
          for (let i = 0; i < count; i++) {
            const idx = indices[i];
            if (idx % resX === resX - 1) {
              const iy = (idx / resX) | 0;
              positions[(rightStart + iy * 2) * 3 + 2] = heights[i];
              positions[(rightStart + iy * 2 + 1) * 3 + 2] = bottoms[i];
            }
          }
        }

        // 2.5 Incremental normal update (central differences from position array)
        {
          const normalsAttr = this.dexelMesh.geometry.attributes.normal?.array as Float32Array;
          if (normalsAttr) {
            const cs = this.dexelEngine.cellSize;
            const inv2cs = 1.0 / (2.0 * cs);
            for (let i = 0; i < count; i++) {
              const idx = indices[i];
              const ix = idx % resX;
              const iy = (idx / resX) | 0;
              const hL = ix > 0 ? positions[(topStart + idx - 1) * 3 + 2] : positions[(topStart + idx) * 3 + 2];
              const hR = ix < resX - 1 ? positions[(topStart + idx + 1) * 3 + 2] : positions[(topStart + idx) * 3 + 2];
              const hD = iy > 0 ? positions[(topStart + idx - resX) * 3 + 2] : positions[(topStart + idx) * 3 + 2];
              const hU = iy < resY - 1 ? positions[(topStart + idx + resX) * 3 + 2] : positions[(topStart + idx) * 3 + 2];
              const nx = -(hR - hL) * inv2cs;
              const ny = -(hU - hD) * inv2cs;
              const invLen = 1.0 / Math.sqrt(nx * nx + ny * ny + 1);
              normalsAttr[(topStart + idx) * 3] = nx * invLen;
              normalsAttr[(topStart + idx) * 3 + 1] = ny * invLen;
              normalsAttr[(topStart + idx) * 3 + 2] = invLen;
            }
          }
        }

        // 3. Renk güncelleme (derinlik bazlı AO yaklaşımı)
        const colors = this.dexelMesh.geometry.attributes.color?.array as Float32Array;
        if (colors) {
          const bboxMaxZ = this.dexelEngine.getBBox().maxZ;
          const bboxMinZ = this.dexelEngine.getBBox().minZ;
          const stockDepth = bboxMaxZ - bboxMinZ;
          const aoI = this.aoYogunluk / 100;

          if (this.kutukRenklendirmeModu === 'tool') {
            const toolIdMap = this.dexelEngine.extractToolIdMap();
            const hamRenk = this._kutukHamRenk;
            for (let i = 0; i < count; i++) {
              const idx = indices[i];
              const toolId = toolIdMap[idx];
              const r = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.r ?? hamRenk.r) : hamRenk.r;
              const g = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.g ?? hamRenk.g) : hamRenk.g;
              const b = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.b ?? hamRenk.b) : hamRenk.b;
              const depthRatio = stockDepth > 0 ? (bboxMaxZ - heights[i]) / stockDepth : 0;
              const aoApprox = 1.0 - depthRatio * 0.5 * aoI;
              colors[idx * 3]     = r * aoApprox;
              colors[idx * 3 + 1] = g * aoApprox;
              colors[idx * 3 + 2] = b * aoApprox;
              const bIdx = bottomStart + idx;
              colors[bIdx * 3]     = r * aoApprox * 0.8;
              colors[bIdx * 3 + 1] = g * aoApprox * 0.8;
              colors[bIdx * 3 + 2] = b * aoApprox * 0.8;
            }
          } else {
            const hamR = this._kutukHamRenk.r;
            const hamG = this._kutukHamRenk.g;
            const hamB = this._kutukHamRenk.b;
            for (let i = 0; i < count; i++) {
              const idx = indices[i];
              const depthRatio = stockDepth > 0 ? (bboxMaxZ - heights[i]) / stockDepth : 0;
              const aoApprox = 1.0 - depthRatio * 0.5 * aoI;
              colors[idx * 3]     = hamR * aoApprox;
              colors[idx * 3 + 1] = hamG * aoApprox;
              colors[idx * 3 + 2] = hamB * aoApprox;
              const bIdx = bottomStart + idx;
              colors[bIdx * 3]     = hamR * aoApprox * 0.8;
              colors[bIdx * 3 + 1] = hamG * aoApprox * 0.8;
              colors[bIdx * 3 + 2] = hamB * aoApprox * 0.8;
            }
          }
        }

        this.dexelEngine.clearDirty();

      } else {
        // ─── FULL UPDATE: Tüm mesh ───
        const db = this.dexelEngine.getDirtyBounds();
        const hasDirty = db.minX <= db.maxX;
        let rawHeightMap = this.dexelEngine.extractHeightMap();
        if (this.dexelSmoothing > 0) {
          rawHeightMap = this.dexelEngine.smoothHeightMap(rawHeightMap, this.dexelSmoothing, 0, hasDirty ? db : undefined);
        }
        heightMap = rawHeightMap;
        const bottomMap = this.dexelEngine.extractBottomMap();

        // 1. Top + Bottom surface
        if (hasDirty) {
          for (let iy = db.minY; iy <= Math.min(db.maxY, resY - 1); iy++) {
            for (let ix = db.minX; ix <= Math.min(db.maxX, resX - 1); ix++) {
              const ci = iy * resX + ix;
              positions[(topStart + ci) * 3 + 2] = heightMap[ci];
              positions[(bottomStart + ci) * 3 + 2] = bottomMap[ci];
            }
          }
        } else {
          for (let ci = 0; ci < totalCells; ci++) {
            positions[(topStart + ci) * 3 + 2] = heightMap[ci];
            positions[(bottomStart + ci) * 3 + 2] = bottomMap[ci];
          }
        }

        // 2. Side vertices
        for (let ix = 0; ix < resX; ix++) {
          positions[(frontStart + ix * 2) * 3 + 2] = heightMap[ix];
          positions[(frontStart + ix * 2 + 1) * 3 + 2] = bottomMap[ix];
          const bci = (resY - 1) * resX + ix;
          positions[(backStart + ix * 2) * 3 + 2] = heightMap[bci];
          positions[(backStart + ix * 2 + 1) * 3 + 2] = bottomMap[bci];
        }
        for (let iy = 0; iy < resY; iy++) {
          const lci = iy * resX;
          positions[(leftStart + iy * 2) * 3 + 2] = heightMap[lci];
          positions[(leftStart + iy * 2 + 1) * 3 + 2] = bottomMap[lci];
          const rci = iy * resX + (resX - 1);
          positions[(rightStart + iy * 2) * 3 + 2] = heightMap[rci];
          positions[(rightStart + iy * 2 + 1) * 3 + 2] = bottomMap[rci];
        }

        // 3. Analytical normals from heightmap gradient (smooth shading)
        const normalMapData = this.dexelEngine.computeHeightMapNormals(heightMap);
        const normalsAttr = this.dexelMesh.geometry.attributes.normal?.array as Float32Array;
        if (normalsAttr) {
          if (hasDirty) {
            const nMinY = Math.max(0, db.minY - 1);
            const nMaxY = Math.min(resY - 1, db.maxY + 1);
            const nMinX = Math.max(0, db.minX - 1);
            const nMaxX = Math.min(resX - 1, db.maxX + 1);
            for (let iy = nMinY; iy <= nMaxY; iy++) {
              for (let ix = nMinX; ix <= nMaxX; ix++) {
                const ci = iy * resX + ix;
                normalsAttr[(topStart + ci) * 3] = normalMapData[ci * 3];
                normalsAttr[(topStart + ci) * 3 + 1] = normalMapData[ci * 3 + 1];
                normalsAttr[(topStart + ci) * 3 + 2] = normalMapData[ci * 3 + 2];
              }
            }
          } else {
            for (let ci = 0; ci < totalCells; ci++) {
              normalsAttr[(topStart + ci) * 3] = normalMapData[ci * 3];
              normalsAttr[(topStart + ci) * 3 + 1] = normalMapData[ci * 3 + 1];
              normalsAttr[(topStart + ci) * 3 + 2] = normalMapData[ci * 3 + 2];
            }
          }
        }

        // 4. Renk + AO güncelleme
        let colors = this.dexelMesh.geometry.attributes.color?.array as Float32Array;
        if (!colors) {
          const totalVertices = positions.length / 3;
          colors = new Float32Array(totalVertices * 3);
          this.dexelMesh.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          (this.dexelMesh.material as THREE.MeshPhysicalMaterial).vertexColors = true;
        }

        const aoMap = this.dexelEngine.computeVertexAO(heightMap, this.aoRadius, hasDirty ? db : undefined);
        const aoI = this.aoYogunluk / 100;
        const stockTopZ = this.dexelEngine.bbox.maxZ;
        const stockMinZ = this.dexelEngine.bbox.minZ;
        const invDepth = (stockTopZ - stockMinZ) > 0 ? 1.0 / (stockTopZ - stockMinZ) : 0;

        if (this.kutukRenklendirmeModu === 'tool') {
          const toolIdMap = this.dexelEngine.extractToolIdMap();
          const hamRenk = this._kutukHamRenk;
          if (hasDirty) {
            const cMinY = Math.max(0, db.minY);
            const cMaxY = Math.min(resY - 1, db.maxY);
            const cMinX = Math.max(0, db.minX);
            const cMaxX = Math.min(resX - 1, db.maxX);
            for (let iy = cMinY; iy <= cMaxY; iy++) {
              for (let ix = cMinX; ix <= cMaxX; ix++) {
                const ci = iy * resX + ix;
                const bci = bottomStart + ci;
                const isUncut = Math.abs(heightMap[ci] - stockTopZ) < 0.01;
                const aoFactor = isUncut ? 1.0 : (1.0 - aoMap[ci] * aoI);
                const depthT = isUncut ? 0 : (stockTopZ - heightMap[ci]) * invDepth;
                const depthDarken = 1.0 - depthT * 0.08;
                const toolId = toolIdMap[ci];
                const r = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.r ?? hamRenk.r) : hamRenk.r;
                const g = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.g ?? hamRenk.g) : hamRenk.g;
                const b = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.b ?? hamRenk.b) : hamRenk.b;
                colors[ci * 3]     = r * aoFactor * depthDarken;
                colors[ci * 3 + 1] = g * aoFactor * depthDarken;
                colors[ci * 3 + 2] = b * aoFactor * depthDarken;
                const botF = aoFactor * 0.95;
                colors[bci * 3]     = r * botF;
                colors[bci * 3 + 1] = g * botF;
                colors[bci * 3 + 2] = b * botF;
              }
            }
          } else {
            for (let ci = 0; ci < totalCells; ci++) {
              const bci = bottomStart + ci;
              const isUncut = Math.abs(heightMap[ci] - stockTopZ) < 0.01;
              const aoFactor = isUncut ? 1.0 : (1.0 - aoMap[ci] * aoI);
              const depthT = isUncut ? 0 : (stockTopZ - heightMap[ci]) * invDepth;
              const depthDarken = 1.0 - depthT * 0.08;
              const toolId = toolIdMap[ci];
              const r = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.r ?? hamRenk.r) : hamRenk.r;
              const g = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.g ?? hamRenk.g) : hamRenk.g;
              const b = toolId > 0 ? (this.takimRenkHaritasi.get(toolId)?.b ?? hamRenk.b) : hamRenk.b;
              colors[ci * 3]     = r * aoFactor * depthDarken;
              colors[ci * 3 + 1] = g * aoFactor * depthDarken;
              colors[ci * 3 + 2] = b * aoFactor * depthDarken;
              const botF = aoFactor * 0.95;
              colors[bci * 3]     = r * botF;
              colors[bci * 3 + 1] = g * botF;
              colors[bci * 3 + 2] = b * botF;
            }
          }
        } else {
          const hamR = this._kutukHamRenk.r;
          const hamG = this._kutukHamRenk.g;
          const hamB = this._kutukHamRenk.b;
          if (hasDirty) {
            const cMinY = Math.max(0, db.minY);
            const cMaxY = Math.min(resY - 1, db.maxY);
            const cMinX = Math.max(0, db.minX);
            const cMaxX = Math.min(resX - 1, db.maxX);
            for (let iy = cMinY; iy <= cMaxY; iy++) {
              for (let ix = cMinX; ix <= cMaxX; ix++) {
                const ci = iy * resX + ix;
                const bci = bottomStart + ci;
                const isUncut = Math.abs(heightMap[ci] - stockTopZ) < 0.01;
                const aoFactor = isUncut ? 1.0 : (1.0 - aoMap[ci] * aoI);
                const depthT = isUncut ? 0 : (stockTopZ - heightMap[ci]) * invDepth;
                const depthDarken = 1.0 - depthT * 0.08;
                colors[ci * 3]     = hamR * aoFactor * depthDarken;
                colors[ci * 3 + 1] = hamG * aoFactor * depthDarken;
                colors[ci * 3 + 2] = hamB * aoFactor * depthDarken;
                const botF = aoFactor * 0.95;
                colors[bci * 3]     = hamR * botF;
                colors[bci * 3 + 1] = hamG * botF;
                colors[bci * 3 + 2] = hamB * botF;
              }
            }
          } else {
            for (let ci = 0; ci < totalCells; ci++) {
              const bci = bottomStart + ci;
              const isUncut = Math.abs(heightMap[ci] - stockTopZ) < 0.01;
              const aoFactor = isUncut ? 1.0 : (1.0 - aoMap[ci] * aoI);
              const depthT = isUncut ? 0 : (stockTopZ - heightMap[ci]) * invDepth;
              const depthDarken = 1.0 - depthT * 0.08;
              colors[ci * 3]     = hamR * aoFactor * depthDarken;
              colors[ci * 3 + 1] = hamG * aoFactor * depthDarken;
              colors[ci * 3 + 2] = hamB * aoFactor * depthDarken;
              const botF = aoFactor * 0.95;
              colors[bci * 3]     = hamR * botF;
              colors[bci * 3 + 1] = hamG * botF;
              colors[bci * 3 + 2] = hamB * botF;
            }
          }
        }

        // Side renkleri - sabit koyu ton
        {
          const hamR = this._kutukHamRenk.r * 0.88;
          const hamG = this._kutukHamRenk.g * 0.88;
          const hamB = this._kutukHamRenk.b * 0.88;
          const sideCount = resX * 2 * 2 + resY * 2 * 2;
          const sideOfs = frontStart;
          for (let i = 0; i < sideCount; i++) {
            colors[(sideOfs + i) * 3]     = hamR;
            colors[(sideOfs + i) * 3 + 1] = hamG;
            colors[(sideOfs + i) * 3 + 2] = hamB;
          }
        }

        this.dexelEngine.clearDirty();
      }

      this.dexelMesh.geometry.attributes.color.needsUpdate = true;
      this.dexelMesh.geometry.attributes.position.needsUpdate = true;
      if (this.dexelMesh.geometry.attributes.normal) {
        this.dexelMesh.geometry.attributes.normal.needsUpdate = true;
      }

      this.normalUpdateCounter++;

      // Full update sonrası kenar çizgileri + iç duvarlar
      if (!useIncremental) {
        if (this.normalUpdateCounter % this.NORMAL_UPDATE_INTERVAL === 0) {
          if (this.kenarCizgileriAktif) this.kenarCizgileriOlustur();
          if (this.icDuvarAktif) this.icDuvarOlustur();
        }
      }

      // BoundingBox periyodik güncelleme
      if (this.normalUpdateCounter % this.BBOX_UPDATE_INTERVAL === 0) {
        this.dexelMesh.geometry.computeBoundingBox();
        this.dexelMesh.geometry.computeBoundingSphere();
      }

    } catch (e) {
      console.error('Dexel mesh guncelleme hatasi:', e);
    }

    this._sahne?.anaGosterim?.ciz();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WORKER DIRTY HEIGHTS - Async mesh güncelleme (worker sonuçları)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Worker'dan gelen dirty heights'ı doğrudan mesh'e uygula
   * Worker kendi dirty tracking'ini yaptığı için CPU engine'e dokunmaya gerek yok
   */
  private applyWorkerDirtyHeights(dirtyHeights: { indices: number[]; heights: number[]; bottoms?: number[] }): void {
    if (!this.dexelMesh || !this.dexelEngine) return;

    const { indices, heights, bottoms } = dirtyHeights;
    if (indices.length === 0) return;

    const positions = this.dexelMesh.geometry.attributes.position.array as Float32Array;
    const { resX, resY } = this.dexelEngine.getResolution();
    const topVertices = resX * resY;
    const bottomStart = topVertices;

    // 1. Top surface + Bottom surface height güncelle
    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i];
      positions[idx * 3 + 2] = heights[i];                                // Top surface
      if (bottoms) {
        positions[(bottomStart + idx) * 3 + 2] = bottoms[i];              // Bottom surface
      }
    }

    // 2. Side vertices güncelle (kenar hücreleri)
    if (bottoms) {
      const bottomVertices = resX * resY;
      const frontStart = topVertices + bottomVertices;
      const backStart = frontStart + resX * 2;
      const leftStart = backStart + resX * 2;
      const rightStart = leftStart + resY * 2;

      for (let i = 0; i < indices.length; i++) {
        const idx = indices[i];
        const ix = idx % resX;
        const iy = Math.floor(idx / resX);

        // Front kenar (iy=0)
        if (iy === 0) {
          positions[(frontStart + ix * 2) * 3 + 2] = heights[i];          // Top edge
          positions[(frontStart + ix * 2 + 1) * 3 + 2] = bottoms[i];      // Bottom edge
        }
        // Back kenar (iy=resY-1)
        if (iy === resY - 1) {
          positions[(backStart + ix * 2) * 3 + 2] = heights[i];           // Top edge
          positions[(backStart + ix * 2 + 1) * 3 + 2] = bottoms[i];       // Bottom edge
        }
        // Left kenar (ix=0)
        if (ix === 0) {
          positions[(leftStart + iy * 2) * 3 + 2] = heights[i];           // Top edge
          positions[(leftStart + iy * 2 + 1) * 3 + 2] = bottoms[i];       // Bottom edge
        }
        // Right kenar (ix=resX-1)
        if (ix === resX - 1) {
          positions[(rightStart + iy * 2) * 3 + 2] = heights[i];          // Top edge
          positions[(rightStart + iy * 2 + 1) * 3 + 2] = bottoms[i];      // Bottom edge
        }
      }
    }

    // 3. Renkleri güncelle (top + bottom surface) - stok rengi bazlı
    const colors = this.dexelMesh.geometry.attributes.color?.array as Float32Array;
    if (colors) {
      const stockDepth = this.dexelEngine.getBBox().maxZ - this.dexelEngine.getBBox().minZ;
      const bboxMinZ = this.dexelEngine.getBBox().minZ;
      const hamR = this._kutukHamRenk.r;
      const hamG = this._kutukHamRenk.g;
      const hamB = this._kutukHamRenk.b;
      for (let i = 0; i < indices.length; i++) {
        const idx = indices[i];
        const depthT = (heights[i] - bboxMinZ) / stockDepth;
        const cutT = 1 - depthT;
        colors[idx * 3]     = hamR + cutT * (1.0 - hamR) * 0.4;
        colors[idx * 3 + 1] = hamG + cutT * (1.0 - hamG) * 0.4;
        colors[idx * 3 + 2] = hamB + cutT * (1.0 - hamB) * 0.4;
        if (bottoms) {
          const botZ = bottoms[i];
          const botDepth = (botZ - bboxMinZ) / stockDepth;
          const bIdx = bottomStart + idx;
          if (botDepth > 0.001) {
            colors[bIdx * 3]     = hamR + botDepth * (1.0 - hamR) * 0.4;
            colors[bIdx * 3 + 1] = hamG + botDepth * (1.0 - hamG) * 0.4;
            colors[bIdx * 3 + 2] = hamB + botDepth * (1.0 - hamB) * 0.4;
          } else {
            colors[bIdx * 3]     = hamR;
            colors[bIdx * 3 + 1] = hamG;
            colors[bIdx * 3 + 2] = hamB;
          }
        }
      }
      this.dexelMesh.geometry.attributes.color.needsUpdate = true;
    }

    this.dexelMesh.geometry.attributes.position.needsUpdate = true;

    // flatShading: false + sabit (0,0,1) normals → düz yüzey, normal güncelleme gereksiz

    this._sahne?.anaGosterim?.ciz();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICAL NORMALS - Height Map Finite Differences
  // computeVertexNormals() yerine O(cells) analitik normal hesaplama
  // İç döngüde sıfır allocation, sadece aritmetik işlemler
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Full update: Height map array'den tüm top surface normallerini hesapla
   * Finite differences yöntemi: dzdx, dzdy → normal = normalize(dzdx, dzdy, -1)
   */
  private computeAnalyticalNormalsFromHeightMap(heightMap: Float32Array, resX: number, resY: number): void {
    if (!this.dexelMesh || !this.dexelEngine) return;
    const normals = this.dexelMesh.geometry.attributes.normal.array as Float32Array;
    const { cellSize } = this.dexelEngine.getResolution();
    const invCellSize = 1.0 / cellSize;
    const invTwoCellSize = 0.5 * invCellSize;

    for (let iy = 0; iy < resY; iy++) {
      const rowOffset = iy * resX;
      const iyD = iy > 0 ? iy - 1 : 0;
      const iyU = iy < resY - 1 ? iy + 1 : resY - 1;
      const rowD = iyD * resX;
      const rowU = iyU * resX;
      const invDy = iy === 0 || iy === resY - 1 ? invCellSize : invTwoCellSize;

      for (let ix = 0; ix < resX; ix++) {
        const vertIdx = rowOffset + ix;
        const ixL = ix > 0 ? ix - 1 : 0;
        const ixR = ix < resX - 1 ? ix + 1 : resX - 1;
        const invDx = ix === 0 || ix === resX - 1 ? invCellSize : invTwoCellSize;

        // ── Top surface normal: n = normalize(-dz/dx, -dz/dy, 1) → Z+ yukarı ──
        const dzdx = (heightMap[rowOffset + ixR] - heightMap[rowOffset + ixL]) * invDx;
        const dzdy = (heightMap[rowU + ix] - heightMap[rowD + ix]) * invDy;
        const lenSq = dzdx * dzdx + dzdy * dzdy + 1;
        const invLen = 1.0 / Math.sqrt(lenSq);

        const base = vertIdx * 3;
        normals[base]     = -dzdx * invLen;
        normals[base + 1] = -dzdy * invLen;
        normals[base + 2] = invLen;
      }
    }
    // Bottom surface normalleri sabit (0,0,-1) - createStockMesh'te zaten ayarlanıyor
    this.dexelMesh.geometry.attributes.normal.needsUpdate = true;
  }

  /**
   * Incremental update: positions array'den height okuyarak tüm top surface normallerini güncelle
   * Height map olmadan çalışır - z bileşenini positions[vertIdx*3+2]'den okur
   */
  private computeAnalyticalNormalsFromPositions(resX: number, resY: number): void {
    if (!this.dexelMesh || !this.dexelEngine) return;
    const positions = this.dexelMesh.geometry.attributes.position.array as Float32Array;
    const normals = this.dexelMesh.geometry.attributes.normal.array as Float32Array;
    const { cellSize } = this.dexelEngine.getResolution();
    const invCellSize = 1.0 / cellSize;
    const invTwoCellSize = 0.5 * invCellSize;

    // Sadece top surface normallerini güncelle
    // Bottom surface normalleri sabit (0,0,-1) kalır
    for (let iy = 0; iy < resY; iy++) {
      const rowOffset = iy * resX;
      const iyD = iy > 0 ? iy - 1 : 0;
      const iyU = iy < resY - 1 ? iy + 1 : resY - 1;
      const rowD = iyD * resX;
      const rowU = iyU * resX;
      const invDy = iy === 0 || iy === resY - 1 ? invCellSize : invTwoCellSize;

      for (let ix = 0; ix < resX; ix++) {
        const vertIdx = rowOffset + ix;
        const ixL = ix > 0 ? ix - 1 : 0;
        const ixR = ix < resX - 1 ? ix + 1 : resX - 1;
        const invDx = ix === 0 || ix === resX - 1 ? invCellSize : invTwoCellSize;

        const zLeft = positions[(rowOffset + ixL) * 3 + 2];
        const zRight = positions[(rowOffset + ixR) * 3 + 2];
        const zDown = positions[(rowD + ix) * 3 + 2];
        const zUp = positions[(rowU + ix) * 3 + 2];

        // n = normalize(-dz/dx, -dz/dy, 1) → Z+ yukarı
        const dzdx = (zRight - zLeft) * invDx;
        const dzdy = (zUp - zDown) * invDy;
        const lenSq = dzdx * dzdx + dzdy * dzdy + 1;
        const invLen = 1.0 / Math.sqrt(lenSq);

        const base = vertIdx * 3;
        normals[base]     = -dzdx * invLen;
        normals[base + 1] = -dzdy * invLen;
        normals[base + 2] = invLen;
      }
    }
    this.dexelMesh.geometry.attributes.normal.needsUpdate = true;
  }

  /**
   * Dexel temizle
   */
  private dexelTemizle() {
    // Kalan batch'i işle (son güncelleme) - fire and forget for cleanup
    if (this.toolPositionBatch.length > 0 && this.dexelEngine) {
      const tool: DexelToolGeometry = { type: 'flat', diameter: 10, cornerRadius: 0, length: 50 };
      void this.flushToolPositionBatch(tool);
    }

    // Pending mesh update'i iptal et
    if (this.meshUpdateRafId) {
      cancelAnimationFrame(this.meshUpdateRafId);
      this.meshUpdateRafId = 0;
    }
    this.meshUpdatePending = false;

    // Son mesh güncellemesi
    if (this.dexelEngine && this.dexelMesh) {
      this.dexelMeshGuncelle();
    }

    // Kenar çizgilerini temizle
    if (this.kenarCizgileriMesh) {
      this._sahne?.remove(this.kenarCizgileriMesh);
      this.kenarCizgileriMesh.geometry?.dispose();
      (this.kenarCizgileriMesh.material as THREE.Material)?.dispose();
      this.kenarCizgileriMesh = null;
    }
    // İç duvar mesh temizle
    if (this.icDuvarMesh) {
      this._sahne?.remove(this.icDuvarMesh);
      this.icDuvarMesh.geometry?.dispose();
      (this.icDuvarMesh.material as THREE.Material)?.dispose();
      this.icDuvarMesh = null;
    }

    if (this.dexelMesh) {
      this._sahne?.remove(this.dexelMesh);
      this.dexelMesh.geometry?.dispose();
      (this.dexelMesh.material as THREE.Material)?.dispose();
      this.dexelMesh = null;
    }
    if (this.dexelEngine) {
      this.dexelEngine.dispose();
      this.dexelEngine = null;
    }

    if (this.dexelGPUEngine) {
      this.dexelGPUEngine.dispose();
      this.dexelGPUEngine = null;
    }
    this.useGPUDexel = false;

    if (this.dexelWorkerBridge) {
      this.dexelWorkerBridge.dispose();
      this.dexelWorkerBridge = null;
    }
    this.useWorkerDexel = false;
    this.workerPendingUpdate = false;

    // Orijinal kütük mesh'i tekrar görünür yap
    if (this.kutukMesh) {
      this.kutukMesh.visible = true;
    }

    this.dexelOncekiPoz = null;
    this.toolPositionBatch.length = 0;

    // Metrikleri sıfırla
    this.perfMetrics = { totalProcessTime: 0, meshUpdateTime: 0, batchCount: 0, avgBatchTime: 0 };
  }

  /** Kütüğü orijinal haline döndür */
  private kutukReset() {
    if (this.kutukMesh && this.kutukOrijinalGeo) {
      this.kutukMesh.geometry.dispose();
      this.kutukMesh.geometry = this.kutukOrijinalGeo.clone();

      // Material'ı sıfırla (opak, ham malzeme rengi)
      if (this.kutukMaterial) {
        this.kutukMaterial.transparent = false;
        this.kutukMaterial.opacity = 1.0;
        this.kutukMaterial.color.copy(this._kutukHamRenk); // Kullanıcı stok rengi
        this.kutukMaterial.needsUpdate = true;
      }
    }

    // CSG kaynaklarını temizle
    this.kutukBrush?.geometry?.dispose();
    this.kutukBrush = null;
    this.sonIslemPoz = -1;
    this.csgBatchSayac = 0;
    this.kutukKaldirilanHacim = 0;
    this.sweptVolumeBuffer = [];
    // Önceki pozisyonu sıfırla (swept volume için)
    this._kutukOncekiPoz = null;
    this._kutukOncekiYon = null;

    // Triple-Dexel kaynaklarını temizle
    this.dexelTemizle();

    // Swept volume görsellerini temizle
    this.kutukSweptVolumeTemizle();

    this._sahne?.anaGosterim?.ciz();
  }

  /**
   * Swept volume görsellerini temizle
   */
  private kutukSweptVolumeTemizle() {
    if (this.sweptVolumeGroup) {
      // Tüm child mesh'leri dispose et
      while (this.sweptVolumeGroup.children.length > 0) {
        const child = this.sweptVolumeGroup.children[0] as THREE.Mesh;
        this.sweptVolumeGroup.remove(child);
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material?.dispose();
        }
      }
      // Grubu sahneden kaldır
      this._sahne?.remove(this.sweptVolumeGroup);
      this.sweptVolumeGroup = null;
      this.sweptVolumeMeshCount = 0;
    }
  }

  /**
   * Basit kutu geometrisi oluştur (CSG fallback)
   * Kütük mesh'in bounding box'ından basit bir kutu yaratır
   * Bu kutu CSG ile %100 uyumludur
   */
  private kutukSimpleBoxMesh: THREE.Mesh | null = null;
  private kutukSimpleBoxOlustur() {
    if (!this.kutukMesh) return;

    try {
      // Bounding box hesapla (world koordinatlarında)
      this.kutukMesh.updateMatrixWorld(true);
      const bbox = new THREE.Box3().setFromObject(this.kutukMesh);

      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      bbox.getSize(size);
      bbox.getCenter(center);

      // Stok = kütük bbox'ı (padding kütüğün modelden taşmasına yol açıyordu).
      // Coplanar CSG hatalarını önlemek için mikroskobik pay yeterli.
      const padding = 0.01; // mm
      size.addScalar(padding * 2);

      // Basit BoxGeometry (CSG ile %100 uyumlu)
      // Daha fazla segment = daha pürüzsüz kesim
      const boxGeo = new THREE.BoxGeometry(size.x, size.y, size.z, 4, 4, 4);

      // UV attribute'u sil (CylinderGeometry ile eşleşmesi için)
      boxGeo.deleteAttribute('uv');

      // Normal ve bounding hesapla
      boxGeo.computeVertexNormals();
      boxGeo.computeBoundingBox();
      boxGeo.computeBoundingSphere();

      // Merkeze taşı
      boxGeo.translate(center.x, center.y, center.z);

      // CSG Brush oluştur
      this.kutukBrush = new Brush(boxGeo);
      this.kutukBrush.updateMatrixWorld(true);

      // Görsel kutu mesh'i oluştur (orijinal kütüğü gizleyeceğiz)
      const kutukMat = new THREE.MeshStandardMaterial({
        color: this._kutukHamRenk.clone(),
        roughness: 0.35,
        metalness: 0.15,
        side: THREE.DoubleSide
      });

      this.kutukSimpleBoxMesh = new THREE.Mesh(boxGeo.clone(), kutukMat);
      this.kutukSimpleBoxMesh.name = 'KutukSimpleBox';
      this.kutukSimpleBoxMesh.renderOrder = 50;

      // Sahneye ekle
      this._sahne?.add(this.kutukSimpleBoxMesh);

      // Orijinal kütüğü gizle (parçayı değil, sadece kütük mesh'i)
      this.kutukMesh.visible = false;

      // Başlangıç hacmini kaydet
      this.kutukBaslangicHacim = size.x * size.y * size.z * 0.001; // mm³ → cm³

      // CSG modunu aktif et
      this.kutukSimModu = 'csg';
      this.kutukMaterial = kutukMat;
      this.kutukSimAktif = true; // CSG simülasyonu aktif

      // kutukMesh referansını güncelle (CSG işlemleri için)
      // Orijinal mesh'i saklayalım
      this.kutukOrijinalMesh = this.kutukMesh;
      this.kutukMesh = this.kutukSimpleBoxMesh;

      // Sayaçları sıfırla
      this.sonIslemPoz = -1;
      this.csgBatchSayac = 0;
      // Önceki pozisyonu sıfırla (swept volume için)
      this._kutukOncekiPoz = null;
      this._kutukOncekiYon = null;

    } catch (e) {
      console.error('❌ Basit kutu oluşturulamadı:', e);
      this.kutukSimModu = 'visual';
    }
  }
  private kutukOrijinalMesh: THREE.Mesh | null = null;

  /** Kütük simülasyonu temizle */
  private kutukSimTemizle() {
    this.kutukSecimModuKapat();
    this.kutukReset();

    // Simülasyon ışık sistemini kaldır
    this.simIsikSisteminiKaldir();

    // Geometri cache'i temizle
    this.takimGeoCache.forEach((geo) => geo.dispose());
    this.takimGeoCache.clear();

    // Basit kutu mesh'i temizle
    if (this.kutukSimpleBoxMesh) {
      this._sahne?.remove(this.kutukSimpleBoxMesh);
      this.kutukSimpleBoxMesh.geometry?.dispose();
      (this.kutukSimpleBoxMesh.material as THREE.Material)?.dispose();
      this.kutukSimpleBoxMesh = null;
    }

    // Tüm motorları temizle (hangisi aktifse)
    this.disposeVoxelEngine();
    this.disposeSDFEngine();
    this.disposeZBufferEngine();

    // Orijinal mesh'i geri yükle
    if (this.kutukOrijinalMesh) {
      this.kutukOrijinalMesh.visible = true;
      this.kutukMesh = this.kutukOrijinalMesh;
      this.kutukOrijinalMesh = null;
    }

    // CSG Evaluator'ı koru (singleton)
    this.kutukMesh = null;
    this.kutukParent = null;
    this.kutukOrijinalGeo?.dispose();
    this.kutukOrijinalGeo = null;
    this.kutukMaterial = null;
    this.kutukSimAktif = false;
  }

  // ─── / Kütük Simülasyonu ────────────────────────────────────────────

  ngOnDestroy(): void {
    this.tiklandiDuraklat();

    this.renderListeners?.forEach((value: any, key: string) => {
      if (value) value();
    });

    this.subsList.forEach(element => {
      element.unsubscribe();
    });

    // Ses + partikul + titresim + carpisma + efekt + kutuk temizle
    this.sesDurdur();
    this.talasDurdur();
    this.titresimTemizle();
    this.carpismaTemizle();
    this.efektTemizle();
    this.hudTemizle();
    this.sonarTemizle();
    this.carpismaMarkerTemizle();
    this.markerResourcesTemizle();
    this.kutukSimTemizle();

    // 3D sahnesinden simulasyon cizgisini ve aktifTakim'i kaldir
    this.similasyonCizgisi?.parent?.remove(this.similasyonCizgisi);
    const aktifTakim = this.aktifTakimAl();
    if (aktifTakim) {
      aktifTakim.clear();
      aktifTakim.parent?.remove(aktifTakim);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VOXEL CSG MOTOR FUNCTIONS (NEW!)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Voxel CSG motorunu başlat
   */
  private async initVoxelEngine(bbox: any, voxelSize: number, resX: number, resY: number, resZ: number): Promise<void> {
    this.voxelEngineReady = false;

    try {
      // CPU Voxel Engine (octree-based)
      this.voxelEngine = createVoxelCSGEngine(bbox, voxelSize);
      this.voxelEngine.initializeRectangularStock();

      // Try GPU acceleration
      this.useGPUVoxel = false;
      if ((navigator as any).gpu) {
        try {
          const gpuCompute = await createVoxelGPUCompute(
            resX, resY, resZ, voxelSize,
            [bbox.minX, bbox.minY, bbox.minZ]
          );

          if (gpuCompute?.isInitialized()) {
            this.voxelGPUCompute = gpuCompute;
            this.voxelGPUCompute.initializeSolid();
            this.useGPUVoxel = true;
          }
        } catch (e) {
          console.warn('[VoxelSim] GPU init failed, using CPU:', e);
        }
      }

      // Try Worker threading (if no GPU)
      this.useWorkerVoxel = false;
      if (!this.useGPUVoxel) {
        try {
          const workerBridge = createVoxelWorkerBridge(navigator.hardwareConcurrency || 4);
          const success = await workerBridge.initialize(resX, resY, resZ, voxelSize, bbox);

          if (success) {
            this.voxelWorkerBridge = workerBridge;
            this.useWorkerVoxel = true;
          }
        } catch (e) {
          console.warn('[VoxelSim] Worker init failed:', e);
        }
      }

      // Create visualization mesh
      this.voxelMeshOlustur();

      // Mark as ready AFTER mesh is created
      this.voxelEngineReady = true;

    } catch (error) {
      console.error('[VoxelSim] Failed to initialize voxel engine:', error);
      this.voxelEngineReady = false;
      // Fallback to visual mode
      this.kutukSimModu = 'visual';
      this.CD.detectChanges();
    }
  }

  /**
   * Voxel mesh'i oluştur veya güncelle
   */
  private voxelMeshOlustur(): void {
    if (!this.voxelEngine || !this._sahne) {
      console.warn('[VoxelSim] Cannot create mesh: engine or scene missing');
      return;
    }

    try {
      // Generate mesh from voxel data
      const geometry = this.voxelEngine.generateMesh();

      const vertexCount = geometry.attributes.position?.count || 0;

      if (vertexCount === 0) {
        return;
      }

      if (!this.voxelMesh) {
        // Create new mesh
        const material = new THREE.MeshStandardMaterial({
          color: 0xaaaaaa,
          metalness: 0.2,
          roughness: 0.8,
          side: THREE.DoubleSide,
          wireframe: false
        });

        this.voxelMesh = new THREE.Mesh(geometry, material);
        this.voxelMesh.name = 'VoxelStockMesh';
        this.voxelMesh.castShadow = true;
        this.voxelMesh.receiveShadow = true;

        // Sahneye ekle
        this._sahne.add(this.voxelMesh);
      } else {
        // Update existing mesh
        const oldGeo = this.voxelMesh.geometry;
        this.voxelMesh.geometry = geometry;
        oldGeo.dispose();
      }

      // Compute bounding box
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      // Request render
      this._sahne?.anaGosterim?.ciz();

    } catch (error) {
      console.error('[VoxelSim] Failed to create mesh:', error);
    }
  }

  /**
   * Voxel motoru ile material removal (tool position processing)
   * Bu fonksiyon simülasyon loop'undan çağrılmalı
   */
  private async processVoxelToolPosition(x: number, y: number, z: number, tool: any): Promise<void> {
    if (!this.voxelEngine) return;

    try {
      // Tool geometry için mapping (FikoTakimFrezeleme fields)
      const radius = (tool.cap || 10) / 2;
      const cornerR = tool.radius || 0;
      const voxelTool: VoxelToolGeometry = {
        type: (cornerR >= radius - 0.01 && cornerR > 0) ? 'ball'
            : (cornerR > 0) ? 'bull' : 'flat',
        diameter: tool.cap || 10,
        cornerRadius: cornerR,
        length: tool.kesmeBoy || tool.boy || 50,
      };

      // Tool direction (from active point if available)
      const dirX = 0;
      const dirY = 0;
      const dirZ = 1; // Default Z direction

      // Process based on mode
      if (this.useGPUVoxel && this.voxelGPUCompute) {
        // GPU path: single point removal (A==B)
        this.voxelGPUCompute.removeSweptGPU(x, y, z, x, y, z, voxelTool, dirX, dirY, dirZ);
      } else if (this.useWorkerVoxel && this.voxelWorkerBridge) {
        // Worker path
        await this.voxelWorkerBridge.removeVoxels(x, y, z, voxelTool);
      } else {
        // CPU path
        this.voxelEngine.removeMaterial(x, y, z, voxelTool, dirX, dirY, dirZ);
      }

      // Update mesh periodically (throttled)
      this.voxelGuncellemeSayac++;
      if (this.voxelGuncellemeSayac % this.voxelBatchSize === 0) {
        if (this.useGPUVoxel && this.voxelGPUCompute) {
          this.voxelGPUDirty = true;
          this.scheduleVoxelGPUMeshUpdate();
        } else {
          this.voxelMeshUpdateThrottled();
        }
      }

    } catch (error) {
      console.error('[VoxelSim] Material removal failed:', error);
    }
  }

  /**
   * Voxel motor temizliği
   */
  private disposeVoxelEngine(): void {
    try {
      // Dispose MarchingCubes GPU (must be before voxelGPUCompute since it shares device)
      if (this.marchingCubesGPU) {
        this.marchingCubesGPU.dispose();
        this.marchingCubesGPU = null;
      }

      // Dispose GPU resources
      if (this.voxelGPUCompute) {
        this.voxelGPUCompute.dispose();
        this.voxelGPUCompute = null;
      }

      // Dispose worker
      if (this.voxelWorkerBridge) {
        this.voxelWorkerBridge.dispose();
        this.voxelWorkerBridge = null;
      }

      // Dispose CPU engine
      if (this.voxelEngine) {
        this.voxelEngine.dispose();
        this.voxelEngine = null;
      }

      // Remove mesh
      if (this.voxelMesh) {
        this.voxelMesh.geometry?.dispose();
        (this.voxelMesh.material as THREE.Material)?.dispose();
        this._sahne?.remove(this.voxelMesh);
        this.voxelMesh = null;
      }

      // Clear mesh update timer
      if (this.voxelGPUMeshTimerId) {
        clearTimeout(this.voxelGPUMeshTimerId);
        this.voxelGPUMeshTimerId = null;
      }
      this.voxelGPUDirty = false;

      this.useGPUVoxel = false;
      this.useWorkerVoxel = false;
      this.voxelEngineReady = false;
      this.voxelGuncellemeSayac = 0;
    } catch (error) {
      console.error('[VoxelSim] Disposal error:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SDF (SIGNED DISTANCE FIELD) MOTOR FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * SDF motorunu başlat (CPU + GPU)
   */
  private async initSDFEngine(bbox: any, voxelSize: number, resX: number, resY: number, resZ: number): Promise<void> {
    this.sdfEngineReady = false;

    try {
      // CPU SDF Engine
      this.sdfEngine = createSDFEngine(bbox, voxelSize);

      // Try GPU acceleration
      this.useGPUSDF = false;
      if ((navigator as any).gpu) {
        try {
          const gpuCompute = await createSDFGPUCompute(
            resX, resY, resZ, voxelSize,
            [bbox.minX, bbox.minY, bbox.minZ]
          );

          if (gpuCompute?.isInitialized()) {
            this.sdfGPUCompute = gpuCompute;
            this.sdfGPUCompute.initializeSolid();
            this.useGPUSDF = true;
          }
        } catch (e) {
          console.warn('[SDFSim] GPU init failed, using CPU:', e);
        }
      }

      // Create initial mesh
      this.sdfMeshOlustur();
      this.sdfEngineReady = true;

    } catch (error) {
      console.error('[SDFSim] Failed to initialize:', error);
      this.sdfEngineReady = false;
      this.kutukSimModu = 'visual';
      this.CD.detectChanges();
    }
  }

  /**
   * SDF pozisyon işleme (ileri/geri sarma)
   * Geri sarma: reset + 0'dan yeniPoz'a kadar işle (throttled)
   */
  private _sdfBackwardPending = false;
  private _sdfBackwardTarget = -1;
  private handleSDFProcessing(yeniPoz: number): void {
    if (!this.sdfEngine || !this.sdfEngineReady) return;

    const eskiPoz = this.lastProcessedSimPoz;

    if (eskiPoz < 0) {
      this.lastProcessedSimPoz = yeniPoz;
      if (!this.sdfMesh) {
        this.sdfMeshOlustur();
      }
      return;
    }

    if (yeniPoz === eskiPoz) return;

    if (yeniPoz > eskiPoz) {
      // İleri sarma - incremental
      this.processSDFPositionRange(eskiPoz, yeniPoz);
      this.lastProcessedSimPoz = yeniPoz;
    } else {
      // Geri sarma - throttle: rAF batch ile
      this._sdfBackwardTarget = yeniPoz;
      if (!this._sdfBackwardPending) {
        this._sdfBackwardPending = true;
        requestAnimationFrame(() => {
          this._sdfBackwardPending = false;
          const target = this._sdfBackwardTarget;
          if (target < 0 || !this.sdfEngine) return;

          this.sdfEngine.reset();
          if (this.useGPUSDF && this.sdfGPUCompute) {
            this.sdfGPUCompute.initializeSolid();
          }
          if (target > 0) {
            this.processSDFPositionRange(0, target);
          }
          this.lastProcessedSimPoz = target;
          this.sdfMeshOlustur();
        });
      }
    }
  }

  /**
   * SDF pozisyon aralığını işle (GPU batch veya CPU)
   */
  private processSDFPositionRange(startPoz: number, endPoz: number): void {
    if (!this.sdfEngine || !this.noktalar?.length) return;

    let processedCount = 0;

    const _tool: VoxelToolGeometry = {
      type: 'flat',
      diameter: 10,
      cornerRadius: 0,
      length: 30
    };
    let _cachedTakim: any = null;
    let prevX = NaN, prevY = NaN, prevZ = NaN;
    let hadPrev = false;

    // GPU batch mode
    const useGPUBatch = this.useGPUSDF && this.sdfGPUCompute;
    const gpuPositions = useGPUBatch ? this.sdfGPUBatchPositions : null;
    let gpuBatchCount = 0;

    for (let p = startPoz; p <= endPoz; p++) {
      const nokta = this.noktalar[p];
      if (!nokta) continue;

      const hareket = this.hareketTuruAl(nokta);
      const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME' || hareket === 'Isleme';
      if (!isIsleme) {
        hadPrev = false;
        continue;
      }

      const takim = nokta.takim;
      if (!takim) continue;

      if (takim !== _cachedTakim) {
        // Flush GPU batch if tool changes
        if (useGPUBatch && gpuBatchCount > 0) {
          this.sdfGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
          gpuBatchCount = 0;
        }

        _tool.diameter = takim.cap || 10;
        _tool.cornerRadius = takim.radius || 0;
        _tool.length = takim.kesmeBoy || takim.boy || 50;

        const takimTipi = ((takim as any)?.takimTipi || '').toLowerCase();
        const halfDia = _tool.diameter / 2;
        if (takimTipi.includes('ball') || takimTipi.includes('nose')
            || (_tool.cornerRadius > 0 && _tool.cornerRadius >= halfDia - 0.01)) {
          _tool.type = 'ball';
        } else if (_tool.cornerRadius > 0 && _tool.cornerRadius < halfDia) {
          _tool.type = 'bull';
        } else {
          _tool.type = 'flat';
        }
        _cachedTakim = takim;
      }

      if (useGPUBatch) {
        if (hadPrev) {
          const base = gpuBatchCount * 6;
          gpuPositions![base] = prevX;
          gpuPositions![base + 1] = prevY;
          gpuPositions![base + 2] = prevZ;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          if (gpuBatchCount >= 256) {
            this.sdfGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        } else {
          const base = gpuBatchCount * 6;
          gpuPositions![base] = nokta.x;
          gpuPositions![base + 1] = nokta.y;
          gpuPositions![base + 2] = nokta.z;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          if (gpuBatchCount >= 256) {
            this.sdfGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        }
      } else {
        // CPU path
        if (hadPrev) {
          this.sdfEngine.removeSweptMaterial(prevX, prevY, prevZ, nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        } else {
          this.sdfEngine.removeMaterial(nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        }
      }

      prevX = nokta.x; prevY = nokta.y; prevZ = nokta.z;
      hadPrev = true;
      processedCount++;
    }

    // Flush remaining GPU batch
    if (useGPUBatch && gpuBatchCount > 0) {
      this.sdfGPUCompute!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
    }

    // Mesh update
    if (useGPUBatch && processedCount > 0) {
      this.sdfGPUDirty = true;
      this.scheduleSDFGPUMeshUpdate();
    } else if (processedCount > 0) {
      this.sdfMeshUpdateThrottled();
    }
  }

  /**
   * Time-throttled SDF GPU mesh update scheduler
   */
  private scheduleSDFGPUMeshUpdate(): void {
    if (this.sdfGPUMeshTimerId) return;

    this.sdfGPUMeshTimerId = setTimeout(() => {
      this.sdfGPUMeshTimerId = null;
      if (this.sdfGPUDirty) {
        this.sdfGPUDirty = false;
        this.sdfGPUMeshUpdate();
      }
    }, this.simGPUMeshInterval);
  }

  /**
   * GPU SDF mesh update: readback SDF data -> CPU Marching Cubes
   */
  private sdfGPUMeshUpdatePending = false;
  private async sdfGPUMeshUpdate(): Promise<void> {
    if (this.sdfGPUMeshUpdatePending) return;
    if (!this.sdfGPUCompute || !this.sdfEngine || !this._sahne) return;
    this.sdfGPUMeshUpdatePending = true;

    try {
      await this.sdfGPUCompute.flush();
      const sdfData = await this.sdfGPUCompute.readSDFData();
      if (!sdfData) return;

      // Copy GPU data to CPU engine for mesh generation
      (this.sdfEngine as any).sdfData = sdfData;
      const geometry = this.sdfEngine.generateMesh();

      if (!geometry || (geometry.attributes.position?.count || 0) === 0) return;

      if (!this.sdfMesh) {
        const material = new THREE.MeshStandardMaterial({
          color: 0x88ccff,
          metalness: 0.3,
          roughness: 0.6,
          side: THREE.DoubleSide,
        });
        this.sdfMesh = new THREE.Mesh(geometry, material);
        this.sdfMesh.name = 'SDFStockMesh';
        this._sahne.add(this.sdfMesh);
      } else {
        const oldGeo = this.sdfMesh.geometry;
        this.sdfMesh.geometry = geometry;
        oldGeo.dispose();
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this._sahne?.anaGosterim?.ciz();

    } catch (e) {
      console.error('[SDFSim] GPU mesh update failed:', e);
    } finally {
      this.sdfGPUMeshUpdatePending = false;
      if (this.sdfGPUDirty) {
        this.scheduleSDFGPUMeshUpdate();
      }
    }
  }

  /**
   * CPU SDF mesh update (rAF throttled)
   */
  private sdfMeshUpdatePending = false;
  private sdfMeshUpdateThrottled(): void {
    if (this.sdfMeshUpdatePending) return;
    this.sdfMeshUpdatePending = true;
    requestAnimationFrame(() => {
      this.sdfMeshUpdatePending = false;
      this.sdfMeshOlustur();
    });
  }

  /**
   * SDF mesh oluştur veya güncelle
   */
  private sdfMeshOlustur(): void {
    if (!this.sdfEngine || !this._sahne) return;

    try {
      const geometry = this.sdfEngine.generateMesh();
      const vertexCount = geometry.attributes.position?.count || 0;
      if (vertexCount === 0) return;

      if (!this.sdfMesh) {
        const material = new THREE.MeshStandardMaterial({
          color: 0x88ccff,
          metalness: 0.3,
          roughness: 0.6,
          side: THREE.DoubleSide,
        });
        this.sdfMesh = new THREE.Mesh(geometry, material);
        this.sdfMesh.name = 'SDFStockMesh';
        this.sdfMesh.castShadow = true;
        this.sdfMesh.receiveShadow = true;
        this._sahne.add(this.sdfMesh);
      } else {
        const oldGeo = this.sdfMesh.geometry;
        this.sdfMesh.geometry = geometry;
        oldGeo.dispose();
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this._sahne?.anaGosterim?.ciz();

    } catch (error) {
      console.error('[SDFSim] Failed to create mesh:', error);
    }
  }

  /**
   * SDF motor temizliği
   */
  private disposeSDFEngine(): void {
    try {
      if (this.sdfGPUCompute) {
        this.sdfGPUCompute.dispose();
        this.sdfGPUCompute = null;
      }

      if (this.sdfEngine) {
        this.sdfEngine.dispose();
        this.sdfEngine = null;
      }

      if (this.sdfMesh) {
        this.sdfMesh.geometry?.dispose();
        (this.sdfMesh.material as THREE.Material)?.dispose();
        this._sahne?.remove(this.sdfMesh);
        this.sdfMesh = null;
      }

      if (this.sdfGPUMeshTimerId) {
        clearTimeout(this.sdfGPUMeshTimerId);
        this.sdfGPUMeshTimerId = null;
      }
      this.sdfGPUDirty = false;
      this.useGPUSDF = false;
      this.sdfEngineReady = false;
    } catch (error) {
      console.error('[SDFSim] Disposal error:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Z-BUFFER / DEPTH MAP MOTOR FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Z-Buffer motorunu başlat (CPU + GPU)
   */
  private async initZBufferEngine(bbox: any, voxelSize: number): Promise<void> {
    this.zbufferEngineReady = false;

    try {
      // CPU Z-Buffer Engine
      this.zbufferEngine = createZBufferEngine(bbox, voxelSize);

      // Try GPU acceleration
      this.useGPUZBuffer = false;
      if ((navigator as any).gpu) {
        try {
          const gpuEngine = await createZBufferGPUEngine(bbox, voxelSize);

          if (gpuEngine?.isInitialized()) {
            this.zbufferGPU = gpuEngine;
            this.zbufferGPU.initializeSolid();
            this.useGPUZBuffer = true;
          }
        } catch (e) {
          console.warn('[ZBufferSim] GPU init failed, using CPU:', e);
        }
      }

      // Create initial mesh
      this.zbufferMeshOlustur();
      this.zbufferEngineReady = true;

    } catch (error) {
      console.error('[ZBufferSim] Failed to initialize:', error);
      this.zbufferEngineReady = false;
      this.kutukSimModu = 'visual';
      this.CD.detectChanges();
    }
  }

  /**
   * Z-Buffer pozisyon işleme (ileri/geri sarma)
   * Geri sarma: reset + 0'dan yeniPoz'a kadar işle (throttled - her tick değil)
   */
  private _zbufferBackwardPending = false;
  private _zbufferBackwardTarget = -1;
  private handleZBufferProcessing(yeniPoz: number): void {
    if (!this.zbufferEngine || !this.zbufferEngineReady) return;

    const eskiPoz = this.lastProcessedSimPoz;

    if (eskiPoz < 0) {
      this.lastProcessedSimPoz = yeniPoz;
      if (!this.zbufferMesh) {
        this.zbufferMeshOlustur();
      }
      return;
    }

    if (yeniPoz === eskiPoz) return;

    if (yeniPoz > eskiPoz) {
      // İleri sarma - incremental
      this.processZBufferPositionRange(eskiPoz, yeniPoz);
      this.lastProcessedSimPoz = yeniPoz;
    } else {
      // Geri sarma - throttle: önceki backward henüz işleniyorsa sadece hedefi güncelle
      this._zbufferBackwardTarget = yeniPoz;
      if (!this._zbufferBackwardPending) {
        this._zbufferBackwardPending = true;
        requestAnimationFrame(() => {
          this._zbufferBackwardPending = false;
          const target = this._zbufferBackwardTarget;
          if (target < 0 || !this.zbufferEngine) return;

          this.zbufferEngine.reset();
          if (this.useGPUZBuffer && this.zbufferGPU) {
            this.zbufferGPU.initializeSolid();
          }
          if (target > 0) {
            this.processZBufferPositionRange(0, target);
          }
          this.lastProcessedSimPoz = target;
          this.zbufferMeshOlustur();
        });
      }
    }
  }

  /**
   * Z-Buffer pozisyon aralığını işle
   */
  private processZBufferPositionRange(startPoz: number, endPoz: number): void {
    if (!this.zbufferEngine || !this.noktalar?.length) return;

    let processedCount = 0;

    const _tool: VoxelToolGeometry = {
      type: 'flat',
      diameter: 10,
      cornerRadius: 0,
      length: 30
    };
    let _cachedTakim: any = null;
    let prevX = NaN, prevY = NaN, prevZ = NaN;
    let hadPrev = false;

    // GPU batch mode (same pattern as SDF/Voxel)
    const useGPUBatch = this.useGPUZBuffer && this.zbufferGPU;
    const gpuPositions = useGPUBatch ? this.zbufferGPUBatchPositions : null;
    let gpuBatchCount = 0;

    for (let p = startPoz; p <= endPoz; p++) {
      const nokta = this.noktalar[p];
      if (!nokta) continue;

      const hareket = this.hareketTuruAl(nokta);
      const isIsleme = hareket === 'İşleme' || hareket === 'ISLEME' || hareket === 'Isleme';
      if (!isIsleme) {
        hadPrev = false;
        continue;
      }

      const takim = nokta.takim;
      if (!takim) continue;

      if (takim !== _cachedTakim) {
        // Flush GPU batch if tool changes
        if (useGPUBatch && gpuBatchCount > 0) {
          this.zbufferGPU!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
          gpuBatchCount = 0;
        }

        _tool.diameter = takim.cap || 10;
        _tool.cornerRadius = takim.radius || 0;
        _tool.length = takim.kesmeBoy || takim.boy || 50;

        const takimTipi = ((takim as any)?.takimTipi || '').toLowerCase();
        const halfDia = _tool.diameter / 2;
        if (takimTipi.includes('ball') || takimTipi.includes('nose')
            || (_tool.cornerRadius > 0 && _tool.cornerRadius >= halfDia - 0.01)) {
          _tool.type = 'ball';
        } else if (_tool.cornerRadius > 0 && _tool.cornerRadius < halfDia) {
          _tool.type = 'bull';
        } else {
          _tool.type = 'flat';
        }
        _cachedTakim = takim;
      }

      if (useGPUBatch) {
        // GPU batch: collect swept segments
        if (hadPrev) {
          const base = gpuBatchCount * 6;
          gpuPositions![base] = prevX;
          gpuPositions![base + 1] = prevY;
          gpuPositions![base + 2] = prevZ;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          if (gpuBatchCount >= 256) {
            this.zbufferGPU!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        } else {
          // Single point: A==B
          const base = gpuBatchCount * 6;
          gpuPositions![base] = nokta.x;
          gpuPositions![base + 1] = nokta.y;
          gpuPositions![base + 2] = nokta.z;
          gpuPositions![base + 3] = nokta.x;
          gpuPositions![base + 4] = nokta.y;
          gpuPositions![base + 5] = nokta.z;
          gpuBatchCount++;

          if (gpuBatchCount >= 256) {
            this.zbufferGPU!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
            gpuBatchCount = 0;
          }
        }
      } else {
        // CPU path (swept or single point)
        if (hadPrev) {
          this.zbufferEngine.removeSweptMaterial(prevX, prevY, prevZ, nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        } else {
          this.zbufferEngine.removeMaterial(nokta.x, nokta.y, nokta.z, _tool, 0, 0, 1);
        }
      }

      prevX = nokta.x; prevY = nokta.y; prevZ = nokta.z;
      hadPrev = true;
      processedCount++;
    }

    // Flush remaining GPU batch
    if (useGPUBatch && gpuBatchCount > 0) {
      this.zbufferGPU!.removeBatchGPU(gpuPositions!, gpuBatchCount, _tool, 0, 0, 1);
    }

    // Mesh update
    if (useGPUBatch && processedCount > 0) {
      this.zbufferGPUDirty = true;
      this.scheduleZBufferGPUMeshUpdate();
    } else if (processedCount > 0) {
      this.zbufferDirty = true;
      this.scheduleZBufferMeshUpdate();
    }
  }

  /**
   * Time-throttled Z-Buffer CPU mesh update scheduler
   */
  private scheduleZBufferMeshUpdate(): void {
    if (this.zbufferMeshTimerId) return;

    this.zbufferMeshTimerId = setTimeout(() => {
      this.zbufferMeshTimerId = null;
      if (this.zbufferDirty) {
        this.zbufferDirty = false;
        this.zbufferMeshOlustur();
      }
    }, this.simGPUMeshInterval);
  }

  /**
   * Time-throttled Z-Buffer GPU mesh update scheduler
   */
  private scheduleZBufferGPUMeshUpdate(): void {
    if (this.zbufferGPUMeshTimerId) return;

    this.zbufferGPUMeshTimerId = setTimeout(() => {
      this.zbufferGPUMeshTimerId = null;
      if (this.zbufferGPUDirty) {
        this.zbufferGPUDirty = false;
        this.zbufferGPUMeshUpdate();
      }
    }, this.simGPUMeshInterval);
  }

  /**
   * GPU Z-Buffer mesh update: readback depth data -> CPU mesh generation
   */
  private zbufferGPUMeshUpdatePending = false;
  private async zbufferGPUMeshUpdate(): Promise<void> {
    if (this.zbufferGPUMeshUpdatePending) return;
    if (!this.zbufferGPU || !this.zbufferEngine || !this._sahne) return;
    this.zbufferGPUMeshUpdatePending = true;

    try {
      await this.zbufferGPU.flush();
      const allDepths = await this.zbufferGPU.readAllDepths();
      if (!allDepths) return;

      // Copy GPU depth data to CPU engine for mesh generation
      this.zbufferEngine.setDepthDataFromGPU(allDepths);
      const geometry = this.zbufferEngine.generateMesh();
      const vertexCount = geometry.attributes.position?.count || 0;
      if (vertexCount === 0) return;

      if (!this.zbufferMesh) {
        const material = new THREE.MeshStandardMaterial({
          color: 0xffaa88,
          metalness: 0.2,
          roughness: 0.7,
          side: THREE.DoubleSide,
        });
        this.zbufferMesh = new THREE.Mesh(geometry, material);
        this.zbufferMesh.name = 'ZBufferStockMesh';
        this.zbufferMesh.castShadow = true;
        this.zbufferMesh.receiveShadow = true;
        this._sahne.add(this.zbufferMesh);
      } else {
        const oldGeo = this.zbufferMesh.geometry;
        this.zbufferMesh.geometry = geometry;
        oldGeo.dispose();
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this._sahne?.anaGosterim?.ciz();

    } catch (e) {
      console.error('[ZBufferGPU] Mesh update failed:', e);
    } finally {
      this.zbufferGPUMeshUpdatePending = false;
      if (this.zbufferGPUDirty) {
        this.scheduleZBufferGPUMeshUpdate();
      }
    }
  }

  /**
   * Z-Buffer mesh oluştur veya güncelle
   */
  private zbufferMeshOlustur(): void {
    if (!this.zbufferEngine || !this._sahne) return;

    try {
      const geometry = this.zbufferEngine.generateMesh();
      const vertexCount = geometry.attributes.position?.count || 0;
      if (vertexCount === 0) return;

      if (!this.zbufferMesh) {
        const material = new THREE.MeshStandardMaterial({
          color: 0xffaa88,
          metalness: 0.2,
          roughness: 0.7,
          side: THREE.DoubleSide,
        });
        this.zbufferMesh = new THREE.Mesh(geometry, material);
        this.zbufferMesh.name = 'ZBufferStockMesh';
        this.zbufferMesh.castShadow = true;
        this.zbufferMesh.receiveShadow = true;
        this._sahne.add(this.zbufferMesh);
      } else {
        const oldGeo = this.zbufferMesh.geometry;
        this.zbufferMesh.geometry = geometry;
        oldGeo.dispose();
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      this._sahne?.anaGosterim?.ciz();

    } catch (error) {
      console.error('[ZBufferSim] Failed to create mesh:', error);
    }
  }

  /**
   * Z-Buffer motor temizliği
   */
  private disposeZBufferEngine(): void {
    try {
      if (this.zbufferGPU) {
        this.zbufferGPU.dispose();
        this.zbufferGPU = null;
      }

      if (this.zbufferEngine) {
        this.zbufferEngine.dispose();
        this.zbufferEngine = null;
      }

      if (this.zbufferMesh) {
        this.zbufferMesh.geometry?.dispose();
        (this.zbufferMesh.material as THREE.Material)?.dispose();
        this._sahne?.remove(this.zbufferMesh);
        this.zbufferMesh = null;
      }

      if (this.zbufferMeshTimerId) {
        clearTimeout(this.zbufferMeshTimerId);
        this.zbufferMeshTimerId = null;
      }
      if (this.zbufferGPUMeshTimerId) {
        clearTimeout(this.zbufferGPUMeshTimerId);
        this.zbufferGPUMeshTimerId = null;
      }
      this.zbufferDirty = false;
      this.zbufferGPUDirty = false;
      this.useGPUZBuffer = false;
      this.zbufferEngineReady = false;
    } catch (error) {
      console.error('[ZBufferSim] Disposal error:', error);
    }
  }

  /**
   * Voxel engine stats'ı al (debug için)
   */
  private getVoxelStats(): any {
    if (!this.voxelEngine) return null;

    const stats = this.voxelEngine.getStats();
    return {
      totalVoxels: stats.totalVoxels.toLocaleString(),
      activeVoxels: stats.activeVoxels.toLocaleString(),
      memoryMB: (stats.memoryUsage / 1024 / 1024).toFixed(2),
      lastOpMs: stats.lastOperationTime.toFixed(2),
      meshVertices: stats.meshVertices.toLocaleString(),
      meshTriangles: stats.meshTriangles.toLocaleString(),
      mode: this.useGPUVoxel ? 'GPU' : this.useWorkerVoxel ? 'Worker' : 'CPU'
    };
  }
}
